import { useState, useEffect, useRef } from "react";
import { useAuth } from "./auth/useAuth";
import AuthCallback from "./auth/AuthCallback";
import { LandingPage, PrivacyPolicy, TermsOfService } from "./pages/pages";
import { useAppVersion } from "./version";
import { BASE, ensureUser, pullData, syncIntake, syncSession, syncCheckin, syncStash, deleteStash } from "./api/sync";
import { uploadLocalCache, scanPackage } from "./api/sync";

const SK = { sessions:"ht_sessions", intake:"ht_intake", stash:"ht_stash", checkins:"ht_checkins", startDate:"ht_start", currentFlavors:"ht_flavors", prefs:"ht_prefs" };

// Capture referral code from URL on first visit and persist it
(()=>{
  const ref = new URLSearchParams(window.location.search).get('ref');
  if(ref) localStorage.setItem('ht_ref', ref.slice(0,50));
})();
function load(key, fb) { try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : fb; } catch { return fb; } }
function save(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }

const METHODS = ["Bowl","Bong","Joint","One Hitter","Dry Herb Vape","Edible","Tincture","Oil Vape","Dab"];
const METHOD_ICONS = { Bowl:"🪨", Bong:"🫧", Joint:"🌿", "One Hitter":"🚬", "Dry Herb Vape":"🌬️", Edible:"🍬", Tincture:"💧", "Oil Vape":"💨", Dab:"🔥" };
const METHOD_EFFICIENCY = { Bowl:[0.20,0.37], Bong:[0.25,0.40], Joint:[0.20,0.35], "One Hitter":[0.20,0.35], "Dry Herb Vape":[0.50,0.80], Edible:[1,1], Tincture:[0.20,0.40], "Oil Vape":[0.50,0.80], Dab:[0.70,0.90] };
const PK = {
  "Bowl":          {onset:10, peak:30,  dur:150, shape:"fast"},
  "Bong":          {onset:8,  peak:25,  dur:150, shape:"fast"},
  "Joint":         {onset:10, peak:35,  dur:180, shape:"fast"},
  "One Hitter":    {onset:8,  peak:20,  dur:120, shape:"fast"},
  "Dry Herb Vape": {onset:10, peak:30,  dur:150, shape:"fast"},
  "Oil Vape":      {onset:5,  peak:15,  dur:120, shape:"fast"},
  "Dab":           {onset:3,  peak:10,  dur:120, shape:"fast"},
  "Tincture":      {onset:30, peak:90,  dur:300, shape:"slow"},
  "Edible":        {onset:60, peak:150, dur:480, shape:"slow"},
};

function localDateTimeString(date) {
  const d = date || new Date();
  const pad = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// THC is fat-soluble: besides the acute spike, part of every dose is stored in
// fat and released slowly, so blood THC never returns to zero between sessions
// for regular users and takes weeks to clear. Modeled as a residual share of
// each dose that kicks in at the acute peak and decays with a multi-day
// half-life (terminal half-life in frequent users is reported at ~5–13 days).
const THC_RESIDUAL_FRACTION = 0.01;
// Half-life depends on how often someone uses: occasional users clear THC in
// days, daily users store more in fat and take weeks. Interpolated by the
// number of distinct use-days in the last 30.
const THC_HALF_LIFE_OCCASIONAL_DAYS = 1.5;
const THC_HALF_LIFE_DAILY_DAYS = 10;
const THC_RESIDUAL_LOOKBACK_DAYS = 60;

function thcHalfLifeDays(intakeEvents, dayStart) {
  const from = dayStart.getTime() - 30*86400000;
  const to = dayStart.getTime() + 86400000;
  const days = new Set();
  intakeEvents.forEach(e=>{
    const ts = new Date(e.ts).getTime();
    if(ts>=from && ts<to && Number(e.thcLo)>0) days.add(new Date(ts).toDateString());
  });
  const useDays = days.size;
  const frac = Math.min(1, useDays/30);
  return { halfLife: THC_HALF_LIFE_OCCASIONAL_DAYS + (THC_HALF_LIFE_DAILY_DAYS-THC_HALF_LIFE_OCCASIONAL_DAYS)*frac, useDays };
}

// Returns today's per-minute curve as {acute, residual, total} arrays plus the
// half-life used. Pass the full intake history — earlier days still contribute.
function buildBloodCurve(intakeEvents) {
  const now = new Date();
  const dayStart = new Date(now); dayStart.setHours(0,0,0,0);
  const MINUTES = 24*60;
  const acute = new Array(MINUTES).fill(0);
  const residual = new Array(MINUTES).fill(0);
  const lookbackStart = dayStart.getTime() - THC_RESIDUAL_LOOKBACK_DAYS*86400000;
  const { halfLife, useDays } = thcHalfLifeDays(intakeEvents, dayStart);
  const decayPerMin = Math.pow(0.5, 1/(halfLife*24*60));
  intakeEvents.forEach(e=>{
    const absorbed = Number(e.thcLo)||0;
    if(!absorbed) return;
    const pk = PK[e.method];
    if(!pk) return;
    const eTime = new Date(e.ts).getTime();
    if(eTime < lookbackStart || eTime > dayStart.getTime()+MINUTES*60000) return;
    const startMin = Math.floor((eTime-dayStart.getTime())/60000); // negative = earlier day

    // Residual (fat-stored) component
    const resStart = startMin + pk.peak;
    let level = absorbed*THC_RESIDUAL_FRACTION;
    let idx0 = resStart;
    if(idx0 < 0) { level *= Math.pow(decayPerMin, -idx0); idx0 = 0; }
    for(let idx=idx0; idx<MINUTES; idx++) {
      residual[idx] += level;
      level *= decayPerMin;
    }

    // Acute component
    for(let m=0; m<pk.dur; m++) {
      const idx = startMin+m;
      if(idx < 0) continue;
      if(idx >= MINUTES) break;
      let level;
      if(pk.shape==="fast") {
        level = m < pk.peak ? absorbed*(m/pk.peak) : absorbed*Math.exp(-3*(m-pk.peak)/(pk.dur-pk.peak));
      } else {
        const norm = m/pk.dur;
        level = absorbed*4*norm*(1-norm);
      }
      acute[idx] += Math.max(0, level);
    }
  });
  const total = acute.map((a,i)=>a+residual[i]);
  return { acute, residual, total, halfLife, useDays };
}

const METHOD_DEFAULT_WEIGHT = { Bowl:"0.2", Bong:"0.3", Joint:"0.5", "One Hitter":"0.1", Dab:"0.05" };
const JOINT_HITS = { "0.5":8, "1":15 };
const ADDITIVE_THC_BOOST = { Kief:20, Hash:25, Wax:30, Shatter:30, "Live Resin":30, "Live Rosin":30, Diamonds:40 };
const STASH_ADDITIVES = Object.keys(ADDITIVE_THC_BOOST);
const OIL_VAPE_MG_PER_HIT = 4; // ~4mg THC/hit from 80% cart at 200 hits/g
const DRY_VAPE_WEIGHTS = { Personal:0.1, Volcano:0.3 }; // hits per joint size
const STRAIN_TYPES = ["Hybrid","Sativa","Sativa Hybrid","Indica","Indica Hybrid"];
const STRAIN_COLORS = { Hybrid:"#7c3aed", Sativa:"#d97706", "Sativa Hybrid":"#ea580c", Indica:"#2563eb", "Indica Hybrid":"#4f46e5" };
const AGE_BANDS = ["Prefer not to say","Under 21","21-25","26-30","31-35","36-40","41-45","46-50","51-55","56-60","61-65","66-70","70+"];
const SEX_OPTIONS = ["Prefer not to say","Male","Female","Non-binary","Transgender","Intersex"];
const DATA_STORAGE_OPTIONS = ["Sync to Cloud","Local Only"];
const STASH_CATS = ["Flower","Pre-Roll","Blunt","Edible","Tincture","Concentrate","Vape Cart","Recipe"];
const STASH_ICONS = { Flower:"🌿", "Pre-Roll":"🚬", Blunt:"🤎", Edible:"🍬", Tincture:"💧", Concentrate:"🔥", "Vape Cart":"💨", Recipe:"🍳" };
const EDIBLE_TYPES = ["Gummy","Baked","Oil","Bev","Candy"];
const CONCENTRATE_TYPES = ["Wax","Shatter","Kief","Hash","Diamonds"];
const CANNAB_FIELDS = ["THC","CBD","THCv","CBGa","CBDn","CBC"];
// "Generic" products — for new users who have no label to scan. Picking a
// category in generic mode pre-fills these typical values; anything can be
// edited before saving. Categories not listed just get the name.
const GENERIC_NAME = "Generic";
const GENERIC_STASH = {
  Flower:      { flowerType:"Hybrid", potency:"20" },
  "Pre-Roll":  { flowerType:"Hybrid", potency:"25" },
  "Vape Cart": { flowerType:"Hybrid", potency:"85" },
  Edible:      { flowerType:"Hybrid", edibleType:"Gummy", thcMg:"5" },
};
const GROW_TYPES = ["Unknown","Indoor","Outdoor"];

const FEELINGS = [
  {key:"Happy",icon:"😄"},{key:"Sad",icon:"😢"},{key:"Energetic",icon:"⚡️"},{key:"Sleepy",icon:"😴"},
  {key:"Horny",icon:"🔥"},{key:"Paranoid",icon:"👀"},{key:"Confident",icon:"💪"},{key:"Lonely",icon:"🫥"},
  {key:"Social",icon:"🗣️"},{key:"Focused",icon:"🎯"},{key:"Content",icon:"🙂"},{key:"Stressed",icon:"😰"},
  {key:"Relaxed",icon:"😌"},{key:"Angry",icon:"😤"},{key:"Munchies",icon:"🍔"},{key:"Too High",icon:"🆘"},
  {key:"Anxious",icon:"😟"},{key:"Comfortably Numb",icon:"😶"},{key:"Love",icon:"❤️"},
];
const ACTIVITIES = [
  {key:"Moving",icon:"🚶"},{key:"TCB",icon:"✅"},{key:"Chilling",icon:"🛋️"},{key:"Creating",icon:"🎨"},
  {key:"Working",icon:"💼"},{key:"Unwinding",icon:"🌊"},{key:"Eating",icon:"🍽️"},{key:"Rewarding",icon:"🎁"},
  {key:"Stressing",icon:"💢"},{key:"Exercising",icon:"🏋️"},{key:"Practicing",icon:"🎵"},{key:"Gigging",icon:"🎤"},
  {key:"Sleeping",icon:"🌙"},{key:"Socializing",icon:"🥂"},{key:"Vacationing",icon:"🏖️"},{key:"Cleaning",icon:"🧹"},
  {key:"Building",icon:"🔨"},{key:"Lubricating",icon:"🍸"},{key:"Scrolling",icon:"📱"},{key:"Family",icon:"👨‍👩‍👧"},
  {key:"Watching",icon:"📺"},{key:"Waking up",icon:"☀️"},
];
const FEELING_MAP  = Object.fromEntries(FEELINGS.map(f=>[f.key,f.icon]));
const ACTIVITY_MAP = Object.fromEntries(ACTIVITIES.map(a=>[a.key,a.icon]));

// Strain search now powered by live API - 2351 strains
const STRAIN_API = `${BASE}/strains/search`;

// datetime-local inputs need local time, not UTC — toISOString() is wrong for this
function toLocalInput(ts) {
  const d = new Date(ts);
  const pad = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TERPENE_CANONICAL = {
  'a-bisabolol':'Bisabolol','alpha-bisabolol':'Bisabolol','bisabolol':'Bisabolol',
  'b-caryophyllene':'Beta-Caryophyllene','beta-caryophyllene':'Beta-Caryophyllene',
  'beta caryophyllene':'Beta-Caryophyllene','beta-cryophyllene':'Beta-Caryophyllene',
  'beta-cryophyliene':'Beta-Caryophyllene','caryophyllene':'Beta-Caryophyllene',
  'caryophyllene oxide':'Caryophyllene Oxide',
  'b-myrcene':'Myrcene','beta-myrcene':'Myrcene','myrcene':'Myrcene',
  'a-humulene':'Humulene','alpha-humulene':'Humulene','alpha humulene':'Humulene','humulene':'Humulene',
  'limonene':'Limonene','d-limonene':'Limonene',
  'linalool':'Linalool',
  'a-pinene':'Alpha-Pinene','alpha-pinene':'Alpha-Pinene','alpha pinene':'Alpha-Pinene',
  'b-pinene':'Beta-Pinene','beta-pinene':'Beta-Pinene','beta pinene':'Beta-Pinene',
  'terpineol':'Terpineol','alpha-terpineol':'Terpineol','alpha terpineol':'Terpineol',
  'terpinolene':'Terpinolene',
  'nerolidol':'Nerolidol','trans-nerolidol':'Nerolidol','cis-nerolidol':'Nerolidol',
  'ocimene':'Beta-Ocimene','b-ocimene':'Beta-Ocimene','trans-b-ocimene':'Beta-Ocimene',
  'trans-b-farnesene':'Beta-Farnesene',
  'a-phellandrene':'Alpha-Phellandrene','d-3-carene':'3-Carene','3-carene':'3-Carene',
  'a-terpinene':'Alpha-Terpinene','alpha-terpinene':'Alpha-Terpinene',
  'fenchol':'Fenchol','fenchyl alcohol':'Fenchol','fenchone':'Fenchone',
  'borneol':'Borneol','camphene':'Camphene','valencene':'Valencene',
  'geraniol':'Geraniol','guaiol':'Guaiol','eucalyptol':'Eucalyptol','sabinene':'Sabinene',
};
function normTerpName(name) {
  if(!name) return name;
  const key = name.trim().toLowerCase().replace(/\s+/g,' ');
  return TERPENE_CANONICAL[key] || name.trim().replace(/\b\w/g,c=>c.toUpperCase());
}

// ── WARM JOURNAL THEME ─────────────────────────────────────────
// Semantic color system:
//   clay  = feelings / internal states  (warm terracotta)
//   dusk  = activities / what you're doing (slate blue)
//   sage  = actions / logging / CTA     (muted forest green)
//   ink   = data / metrics / numbers    (charcoal, neutral)
//   amber = streak / achievement / time (warm gold)
//   cream = all backgrounds
// ──────────────────────────────────────────────────────────────
const t = {
  bg:          "#f5f0eb",
  surface:     "#fdf9f5",
  surfaceAlt:  "#ede8e1",
  border:      "#ddd5c8",
  borderFocus: "#c4a882",
  text:        "#1c1917",
  textSoft:    "#57534e",
  muted:       "#a8a29e",
  // Clay — feelings, internal states
  clay:        "#9a3412",
  clayMid:     "#c2410c",
  clayLight:   "#fff1ee",
  clayBorder:  "#fdba9d",
  // Dusk — activities, what you're doing
  dusk:        "#1e4d8c",
  duskMid:     "#2d6bc4",
  duskLight:   "#eaf1fc",
  duskBorder:  "#93c5fd",
  // Sage — actions, logging, CTA
  sage:        "#3d6b52",
  sageMid:     "#5a8a6e",
  sageLight:   "#eaf2ed",
  sageBorder:  "#a8d5b8",
  // Ink — data, stats, numbers
  ink:         "#292524",
  inkMid:      "#78716c",
  inkLight:    "#e7e5e4",
  // Amber — streak, achievement, time
  amber:       "#92400e",
  amberMid:    "#b45309",
  amberLight:  "#fef3c7",
  amberBorder: "#fcd34d",
  // Signals
  red:         "#b91c1c",
  redLight:    "#fee2e2",
  shadow:      "0 1px 4px rgba(28,20,12,0.07)",
  shadowMd:    "0 4px 20px rgba(28,20,12,0.11)",
  // Aliases
  purple:      "#9a3412",
  purpleLight: "#fff1ee",
  purpleMid:   "#fdba9d",
  green:       "#3d6b52",
  greenLight:  "#eaf2ed",
};

function fmtDate(iso, tz) { return new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit",timeZone:tz||undefined}); }
function daysSince(iso) { if(!iso) return 0; return Math.floor((Date.now()-new Date(iso).getTime())/86400000); }
const DECARB_METHODS = ["Bowl","Bong","Joint","One Hitter","Dry Herb Vape","Dab","Oil Vape"];
const DECARB_EFFICIENCY = 0.877; // THCA → THC conversion when heated

function effectiveTHCpct(stashItem, method) {
  if(!stashItem) return null;
  const thca = parseFloat(stashItem.thca)||0;
  const d9   = parseFloat(stashItem.d9thc)||parseFloat(stashItem.potency)||0;
  if(DECARB_METHODS.includes(method) && thca > 0) {
    return (thca * DECARB_EFFICIENCY) + d9;
  }
  return d9 || null;
}

function stashTotalTHCpct(stashItem) {
  if(!stashItem) return null;
  const potency = parseFloat(stashItem.potency) || 0;
  if(potency > 0) return parseFloat(potency.toFixed(1));
  const thca = parseFloat(stashItem.thca) || 0;
  const d9 = parseFloat(stashItem.d9thc) || 0;
  if(thca > 0 || d9 > 0) return parseFloat(((thca * DECARB_EFFICIENCY) + d9).toFixed(1));
  return null;
}
function calcTHC(weightG, potencyPct, method) {
  const raw = weightG*(potencyPct/100)*1000;
  const [lo,hi] = METHOD_EFFICIENCY[method]||[0.2,0.4];
  return { raw:parseFloat(raw.toFixed(1)), lo:parseFloat((raw*lo).toFixed(1)), hi:parseFloat((raw*hi).toFixed(1)) };
}
// Per-piece nutrition for one gummy/edible unit — prefers the scanner's
// already-derived per-piece values, falls back to per-serving (serving ≈
// piece) when that's all that's available. Null if neither is present.
function perPieceNutrition(nutrition) {
  if (!nutrition) return null;
  const cal   = nutrition.caloriesPerPiece ?? nutrition.caloriesPerServing ?? null;
  const carbs = nutrition.carbsGPerPiece   ?? nutrition.carbsGPerServing   ?? null;
  const sugar = nutrition.sugarsGPerPiece  ?? nutrition.sugarsGPerServing  ?? null;
  if (cal == null && carbs == null && sugar == null) return null;
  return { cal, carbs, sugar };
}
function stashIdentitySignature(item) {
  if (item?.clientItemId) {
    return `client::${String(item.clientItemId).trim().toLowerCase()}`;
  }
  const parts = [
    item?.cat,
    item?.name,
    item?.manufacturer,
    item?.batchId,
    item?.reupAt,
    item?.packagedDate,
    item?.weight,
    item?.totalPrice,
  ];
  return parts.map(v => String(v || "").trim().toLowerCase()).join("::");
}

function createStashClientItemId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `stash_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function stashCompletenessScore(item) {
  if (!item || typeof item !== "object") return 0;
  return Object.values(item).reduce((score, value) => {
    if (Array.isArray(value)) return score + (value.length > 0 ? 1 : 0);
    if (value && typeof value === "object") return score + (Object.keys(value).length > 0 ? 1 : 0);
    return score + (value !== undefined && value !== null && value !== "" ? 1 : 0);
  }, 0);
}

function dedupeStashItems(items) {
  if (!Array.isArray(items) || items.length === 0) return items;

  const mergedBySignature = new Map();
  let changed = false;

  for (const originalItem of items) {
    const item = originalItem?.clientItemId ? originalItem : { ...originalItem, clientItemId: createStashClientItemId() };
    if (item !== originalItem) changed = true;
    const signature = stashIdentitySignature(item);
    if (!signature || /^(:{2})+$/.test(signature)) {
      mergedBySignature.set(Symbol(), item);
      continue;
    }

    const existing = mergedBySignature.get(signature);
    if (!existing) {
      mergedBySignature.set(signature, item);
      continue;
    }

    changed = true;
    const existingScore = stashCompletenessScore(existing);
    const itemScore = stashCompletenessScore(item);
    const primary = itemScore >= existingScore ? item : existing;
    const secondary = primary === item ? existing : item;
    mergedBySignature.set(signature, {
      ...secondary,
      ...primary,
      favorite: Boolean(existing.favorite || item.favorite),
      isCurrent: Boolean(existing.isCurrent || item.isCurrent),
      finished: Boolean(existing.finished || item.finished),
      rating: Math.max(existing.rating || 0, item.rating || 0),
      reupAt: primary.reupAt || secondary.reupAt || null,
    });
  }

  const nextItems = Array.from(mergedBySignature.values());
  return changed || nextItems.length !== items.length ? nextItems : items;
}

// Merge a fresh cloud stash snapshot into the local list. Previously this was
// inlined at both call sites (auto-pull-on-login and the manual "Sync Now"
// button) as an add-only filter — anything already present locally (matched
// by id or signature) was skipped outright, so an edit made anywhere and
// pushed to the cloud via syncStash() could never be pulled back down to
// refresh an existing local copy. Local edits are already pushed to the
// cloud immediately on save, so by the time a later pull happens the cloud
// copy is the freshest one available — safe to treat as authoritative here.
function mergeStashFromCloud(prev, cloudItems) {
  if (!Array.isArray(cloudItems) || cloudItems.length === 0) return { merged: prev, added: 0, updated: 0 };

  const idIndex = new Map();
  const signatureIndex = new Map();
  prev.forEach((item, idx) => {
    if (item?.id) idIndex.set(item.id, idx);
    const sig = stashIdentitySignature(item);
    if (sig) signatureIndex.set(sig, idx);
  });

  const merged = [...prev];
  let added = 0;
  let updated = 0;

  for (const cloudItem of cloudItems) {
    let idx = cloudItem?.id && idIndex.has(cloudItem.id) ? idIndex.get(cloudItem.id) : undefined;
    if (idx === undefined) {
      const sig = stashIdentitySignature(cloudItem);
      if (sig && signatureIndex.has(sig)) idx = signatureIndex.get(sig);
    }

    if (idx === undefined) {
      merged.push(cloudItem);
      added += 1;
    } else if (JSON.stringify(merged[idx]) !== JSON.stringify(cloudItem)) {
      merged[idx] = { ...cloudItem, id: cloudItem.id || merged[idx].id };
      updated += 1;
    }
  }

  return { merged: added > 0 ? dedupeStashItems(merged) : merged, added, updated };
}
const inp = { width:"100%", background:"#fff", border:"1px solid #e5e5e3", color:"#1a1a18", padding:"9px 12px", borderRadius:8, fontSize:14, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };
const lbl = { fontSize:12, color:"#9b9b8e", display:"block", marginBottom:5, fontWeight:600, letterSpacing:"0.3px" };

function Card({ children, style={} }) { return <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:14,padding:18,boxShadow:t.shadow,...style}}>{children}</div>; }
function SectionLabel({ children }) { return <p style={{color:t.muted,fontSize:11,textTransform:"uppercase",letterSpacing:"1px",fontWeight:600,marginBottom:12,marginTop:0}}>{children}</p>; }
function Stepper({ value, onChange, min=1, step=1 }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:12}}>
      <button onClick={()=>onChange(Math.max(min,+(value-step).toFixed(2)))} style={{width:32,height:32,borderRadius:6,background:"#fff",border:"1px solid #e5e5e3",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",color:t.text}}>-</button>
      <span style={{fontSize:18,fontWeight:700,minWidth:24,textAlign:"center"}}>{value}</span>
      <button onClick={()=>onChange(+(value+step).toFixed(2))} style={{width:32,height:32,borderRadius:6,background:"#fff",border:"1px solid #e5e5e3",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",color:t.text}}>+</button>
    </div>
  );
}
function Stars({ value, onChange }) {
  return (
    <div style={{display:"flex",gap:4}}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} onClick={()=>onChange(n)} style={{background:"none",border:"none",cursor:"pointer",fontSize:24,padding:0,color:n<=value?t.amber:"#e5e5e3",lineHeight:1}}>★</button>
      ))}
    </div>
  );
}
function THCBadge({ entry }) {
  if(!entry.thcRaw) return null;
  return (
    <div style={{marginTop:5,display:"flex",gap:8,flexWrap:"wrap"}}>
      <span style={{fontSize:12,padding:"2px 8px",borderRadius:8,background:t.clayLight,color:t.clay,fontWeight:600}}>{entry.thcRaw}mg THC</span>
      <span style={{fontSize:12,padding:"2px 8px",borderRadius:8,background:t.inkLight,color:t.ink,fontWeight:600}}>~{entry.thcLo}-{entry.thcHi}mg absorbed</span>
    </div>
  );
}
function ChipPicker({ items, selected, onToggle, multi=true, color=t.purple, bgColor=t.purpleLight }) {
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
      {items.map(({key,icon})=>{
        const on = multi ? selected.includes(key) : selected===key;
        return (
          <button key={key} onClick={()=>onToggle(key)} style={{padding:"7px 12px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${on?color:t.border}`,background:on?bgColor:t.surface,color:on?color:t.textSoft,fontWeight:on?600:400,display:"flex",alignItems:"center",gap:5,transition:"all 0.12s"}}>
            <span>{icon}</span>{key}
          </button>
        );
      })}
    </div>
  );
}

function SwipeRow({ children, onDelete, onEdit, confirmMsg="Delete this entry?" }) {
  const [offset, setOffset] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const startX = useRef(null);
  const ACTION_W = 120;
  const THRESHOLD = 60;

  function handleTouchStart(e) { startX.current = e.touches[0].clientX; }
  function handleTouchMove(e) {
    if(startX.current===null) return;
    const dx = e.touches[0].clientX - startX.current;
    if(dx < 0) setOffset(Math.max(dx,-ACTION_W));
  }
  function handleTouchEnd() {
    startX.current = null;
    if(offset < -THRESHOLD) setOffset(-ACTION_W);
    else setOffset(0);
  }
  function close() { setOffset(0); setConfirmed(false); }

  return (
    <div style={{position:"relative",overflow:"hidden"}}>
      {/* Action buttons revealed on swipe */}
      <div style={{position:"absolute",right:0,top:0,bottom:0,width:ACTION_W,display:"flex"}}>
        <button onClick={()=>{ onEdit(); close(); }} style={{flex:1,background:t.dusk,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>Edit</button>
        {!confirmed
          ? <button onClick={()=>setConfirmed(true)} style={{flex:1,background:t.red,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>Delete</button>
          : <button onClick={()=>{ onDelete(); close(); }} style={{flex:1,background:"#7f1d1d",border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>Confirm?</button>
        }
      </div>
      {/* Row content */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={()=>{ if(offset!==0) close(); }}
        style={{transform:`translateX(${offset}px)`,transition:startX.current?'none':'transform 0.2s ease',background:t.surface,position:"relative",zIndex:1}}
      >
        {children}
      </div>
    </div>
  );
}
function CannabFields({ values, onChange, label="Strength (mg)" }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={lbl}>{label}</label>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
        {CANNAB_FIELDS.map(f=>(
          <div key={f}>
            <label style={{...lbl,fontSize:10,marginBottom:3}}>{f}</label>
            <input type="number" min="0" step="0.1" placeholder="0" value={values[f]||""} onChange={e=>onChange({...values,[f]:e.target.value})} style={{...inp,padding:"6px 8px",fontSize:12}}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, sub }) {
  return (
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:12,padding:16,boxShadow:t.shadow}}>
      <div style={{fontSize:22,marginBottom:6}}>{icon}</div>
      <div style={{fontSize:20,fontWeight:700,color:t.purple,lineHeight:1}}>{value}</div>
      <div style={{fontSize:12,color:t.textSoft,marginTop:3}}>{label}</div>
      {sub&&<div style={{fontSize:11,color:t.muted,marginTop:2}}>{sub}</div>}
    </div>
  );
}

function Sheet({ children }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:t.surface,borderRadius:"20px 20px 0 0",padding:24,width:"100%",maxWidth:520,boxShadow:t.shadowMd,maxHeight:"88dvh",overflowY:"auto"}}>
        {children}
      </div>
    </div>
  );
}

function emptyStrength() { return Object.fromEntries(CANNAB_FIELDS.map(f=>[f,""])); }

export default function HabitTrack() {
  const appVersion = useAppVersion();
  const SHOW_BACKUP_TOOLS = true;
  const importBackupInputRef = useRef(null);
  const [tab, setTab]                       = useState("home");
  const [sessions, setSessions]             = useState(()=>load(SK.sessions,[]));
  const [intake, setIntake]                 = useState(()=>load(SK.intake,[]));
  const [stash, setStash]                   = useState(()=>dedupeStashItems(load(SK.stash,[])));
  const [checkins, setCheckins]             = useState(()=>load(SK.checkins,[]));
  const [currentFlavors, setCurrentFlavors] = useState(()=>load(SK.currentFlavors,[]));
  const [startDate] = useState(()=>{ const s=load(SK.startDate,null); if(!s){const n=new Date().toISOString();save(SK.startDate,n);return n;} return s; });

  const [showCheckin, setShowCheckin]   = useState(false);
  const [ciFeeling, setCiFeeling]       = useState([]);
  const [ciActivity, setCiActivity]     = useState([]);
  const [ciNote, setCiNote]             = useState("");

  // Quick intake
  const [showQuickIntake, setShowQuickIntake] = useState(false);
  const [qiItem, setQiItem]                   = useState(null);
  const [qiMethod, setQiMethod]               = useState("Bowl");
  const [qiWeight, setQiWeight]               = useState("");
  const [qiQty, setQiQty]                     = useState(1);
  const [qiOilHits, setQiOilHits]             = useState(1);
  const [qiActivity, setQiActivity]           = useState([]);
  const [qiFeeling, setQiFeeling]             = useState([]);

  // Intake form
  const [iMethod, setIMethod]           = useState("Bowl");
  const [iTime, setITime] = useState(() => localDateTimeString());
  const [pendingIntakes, setPendingIntakes] = useState([]);
  const [iWeight, setIWeight]           = useState(METHOD_DEFAULT_WEIGHT["Bowl"]);
  const [iQty, setIQty]                 = useState(1);
  const [iHits, setIHits]               = useState(1);
  const [iEdibleSize, setIEdibleSize]   = useState("5");
  const [iEdibleQty, setIEdibleQty]     = useState(1);
  const [iTinctureML, setITinctureML]   = useState("");
  const [iAdditives, setIAdditives]     = useState([]);  // e.g. ["Wax","Kief"]
  const [iJointSharing, setIJointSharing] = useState(true);  // default to sharing/hits mode
  const [iJointSize, setIJointSize]     = useState("0.5");
  const [iJointHits, setIJointHits]     = useState(2);
  const [iDryVapeType, setIDryVapeType] = useState("Personal");
  const [iOilHits, setIOilHits]         = useState(3);
  const [iStashQuery, setIStashQuery]   = useState("");
  const [iStashId, setIStashId]         = useState(null);
  const [iActivity, setIActivity]       = useState([]);
  const [iFeeling, setIFeeling]         = useState([]);
  const [iNote, setINote]               = useState("");
  const [showIDrop, setShowIDrop]       = useState(false);

  // Stash form
  const [showStashForm, setShowStashForm] = useState(false);
  const [sGenericMode, setSGenericMode] = useState(false);
  const [showCoaSection, setShowCoaSection] = useState(false);
  const [sCat, setsCat]       = useState("Flower");
  const [sName, setSName]     = useState("");
  const [sMfr, setSMfr]       = useState("");
  const [sWeight, setSWeight]       = useState("");
  const [sTotalPrice, setSTotalPrice] = useState("");
  const [sRating, setSRating] = useState(0);
  const [sComment, setSComment] = useState("");
  const [syncStatus, setSyncStatus] = useState(null); // null | "syncing" | "ok" | "error"
  const [syncMsg, setSyncMsg]       = useState("");
  const [webLookupLoading, setWebLookupLoading] = useState(false);
  const [webLookupResult, setWebLookupResult] = useState(null);
  const [showWebLookup, setShowWebLookup] = useState(false);
  const [coaUrl, setCoaUrl] = useState("");
  const [coaConfirming, setCoaConfirming] = useState(false);
  const [coaFeedback, setCoaFeedback] = useState(null); // null | 'up' | 'down' | 'submitted'
  const [coaFeedbackComment, setCoaFeedbackComment] = useState("");
  const [editSId, setEditSId] = useState(null);
  // Flower specific
  const [sPot, setSPot]       = useState("");   // Total THC (auto-calc or manual)
  const [sThca, setSThca]     = useState("");   // THCA %
  const [sCbd, setSCbd]       = useState("");   // CBD %
  const [sD9, setSD9]         = useState("");   // Delta-9 THC %
  const [sGeekMode, setSGeekMode] = useState(false); // show THCA/D9/CBD breakdown vs. single Total THC field
  const [sTotalCannabinoid, setSTotalCannabinoid] = useState(""); // Total Cannabinoids %
  const [sTotalTerpene, setSTotalTerpene] = useState("");         // Total Terpenes %
  const [sFlowerType, setSFlowerType] = useState("Hybrid");
  const [sGrowType, setSGrowType] = useState("Unknown");
  const [sTerps, setSTerps]   = useState([]);
  const [sEffects, setSEffects] = useState([]);
  const [sFlavors, setSFlavors] = useState([]);
  const [kushyQuery, setKushyQuery]     = useState("");
  const [kushyResults, setKushyResults] = useState([]);
  const [kushySelected, setKushySelected] = useState(false);
  // Edible specific
  const [sEdibleType, setSEdibleType]           = useState("Gummy");
  const [sRecipeType, setSRecipeType]           = useState("");
  const [sRecipeUrl, setSRecipeUrl]             = useState("");
  const [sRecipeText, setSRecipeText]           = useState("");
  const [sRecipeDose, setSRecipeDose]           = useState("");
  const [sRecipeServings, setSRecipeServings]   = useState("");
  const [sRecipeStashId, setSRecipeStashId]     = useState(null);
  const [sEdibleQty, setSEdibleQty]             = useState("");
  const [sEdiblePriceEach, setSEdiblePriceEach] = useState("");
  const [sEdibleStrain, setSEdibleStrain]       = useState("");
  const [sEdibleStrainType, setSEdibleStrainType] = useState("");
  const [sBevOz, setSBevOz]                     = useState("");
  const [sBevMl, setSBevMl]                     = useState("");
  const [sStrength, setSStrength]               = useState(emptyStrength());
  const [sLiveResin, setSLiveResin]             = useState(false);
  const [sLiveRosin, setSLiveRosin]             = useState(false);
  const [sIsHemp, setSIsHemp]                   = useState(false);
  const [sEdibleSolvent, setSEdibleSolvent]     = useState("");
  // Concentrate specific
  const [sConType, setSConType]     = useState("Wax");
  const [sConStrain, setSConStrain] = useState("");
  const [sConPot, setSConPot]       = useState("");
  const [sConWeight, setSConWeight] = useState("");
  // Vape Cart specific
  const [sVapeHardware, setSVapeHardware] = useState("Cartridge");
  const [sVapeSize, setSVapeSize]       = useState("1g");
  const [sVapeStrain, setSVapeStrain]   = useState("");
  const [sVapePot, setSVapePot]         = useState("");
  const [sVapeThca, setSVapeThca]       = useState("");
  const [sVapeCbd, setSVapeCbd]         = useState("");
  const [sVapeSolvent, setSVapeSolvent] = useState("");
  const [sVapeTerps, setSVapeTerps]     = useState([]);
  const [sVapeEffects, setSVapeEffects] = useState([]);
  const [sVapeLiveResin, setSVapeLiveResin] = useState(false);
  const [sVapeLiveRosin, setSVapeLiveRosin] = useState(false);
  // Multi-barrel / "switch" vapes — one device, multiple independently-filled
  // chambers (commonly 2-3), each its own strain + potency, selected via a
  // physical switch/dial on the device.
  const [sVapeMultiBarrel, setSVapeMultiBarrel] = useState(false);
  const [sVapeBarrels, setSVapeBarrels] = useState([]); // [{strain, potency}]
  // Expanded stash card
  // Batch / lot ID from COA — shared across all stash categories
  const [sBatchId, setSBatchId] = useState("");
  // Dispensary — shared across all stash categories
  const [sDispensary, setSDispensary] = useState("");
  const [sRetailUrl, setSRetailUrl]   = useState("");

  // Package-photo scan — shared across all stash categories (see api/package-scan)
  const [sProducerName, setSProducerName]   = useState(""); // licensed producer, distinct from brand/sMfr
  const [sLicenseNumber, setSLicenseNumber] = useState("");
  const [sSolventType, setSSolventType]     = useState("");
  const [sVegan, setSVegan]         = useState(null);   // null = unknown, true/false = printed on label
  const [sKosher, setSKosher]       = useState(null);
  const [sGlutenFree, setSGlutenFree] = useState(null);
  const [sNonGmo, setSNonGmo]       = useState(null);
  const [sIngredients, setSIngredients]         = useState([]);
  const [sFlavorAdditives, setSFlavorAdditives] = useState([]);
  const [sInfusionDetails, setSInfusionDetails] = useState("");
  const [sNutrition, setSNutrition]     = useState(null);
  const [sExpirationDate, setSExpirationDate] = useState("");
  const [sPackagedDate, setSPackagedDate]     = useState("");
  const [sScanSource, setSScanSource]   = useState(null);
  // Camera capture (transient — never persisted, discarded after scan)
  const [scanImages, setScanImages]     = useState([]); // [{preview, base64, mediaType}]
  const MAX_SCAN_IMAGES = 3;
  const [scanLoading, setScanLoading]   = useState(false);
  const [scanError, setScanError]       = useState("");
  const [scanResult, setScanResult]     = useState(null);

// Pre-Roll specific
  const [sPrPackQty, setSPrPackQty] = useState(5);
  const [sPrJointWt, setSPrJointWt] = useState("0.5");
  const [sPrAdditives, setSPrAdditives] = useState([]);

// Partake Now intent
  const [partakeAfterSave, setPartakeAfterSave] = useState(false);

// Expanded stash card
  const [expandedStash, setExpandedStash] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [stashView, setStashView] = useState("stash");
  const [stashQuery, setStashQuery] = useState("");
  const [stashFilterCat, setStashFilterCat] = useState("");
  const [stashFilterStrain, setStashFilterStrain] = useState("");
  const [stashFilterDays, setStashFilterDays] = useState("");
  const [stashSortKey, setStashSortKey] = useState("recommended");
  const [showFeedback, setShowFeedback] = useState(false);
  const [restockItem, setRestockItem]   = useState(null);
  const [restockDisp, setRestockDisp]   = useState("");
  const [restockPrice, setRestockPrice] = useState("");
  const [restockCoa, setRestockCoa]     = useState("");
  const [fbName, setFbName] = useState("");
  const [fbEmail, setFbEmail] = useState("");
  const [fbComment, setFbComment] = useState("");
  const [fbCaptcha, setFbCaptcha] = useState("");
  const [fbSending, setFbSending] = useState(false);
  const [fbSent, setFbSent] = useState(false);
  const fbCaptchaQ = {a:3, b:7};  // 3 + 7 = 10, simple static captcha // "stash" | "library"
  const [reupConfirmId, setReupConfirmId] = useState(null);
  const [reupDate, setReupDate] = useState("");
  const [timelineEditEntry, setTimelineEditEntry] = useState(null);
  const [editTime, setEditTime] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editFeelings, setEditFeelings] = useState([]);
  const [editActivities, setEditActivities] = useState([]);
  const [prefs, setPrefs] = useState(()=>{
    const defaults = { ageRange:"Prefer not to say", sex:"Prefer not to say", dataStorage:"Sync to Cloud", timezone:Intl.DateTimeFormat().resolvedOptions().timeZone, displayName:"" };
    const stored = load(SK.prefs, defaults);
    if (!DATA_STORAGE_OPTIONS.includes(stored.dataStorage)) stored.dataStorage = "Sync to Cloud";
    return stored;
  });

  const [pulse, setPulse]   = useState(null);
  const [statDays, setStatDays] = useState(7);
  const [terpExpanded, setTerpExpanded] = useState(null);
  const [expandedDispensary, setExpandedDispensary] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(()=>{save(SK.prefs,prefs);},[prefs]);

  useEffect(()=>{save(SK.sessions,sessions);},[sessions]);
  useEffect(()=>{save(SK.intake,intake);},[intake]);
  useEffect(()=>{save(SK.stash,stash);},[stash]);
  useEffect(()=>{
    setStash(prev => dedupeStashItems(prev));
  },[stash]);
  useEffect(()=>{save(SK.checkins,checkins);},[checkins]);
  useEffect(()=>{save(SK.currentFlavors,currentFlavors);},[currentFlavors]);

  const { user, loading, sessionExpired, login, loginWithGoogle, loginWithApple, loginWithEmail, resetPassword, refreshSession, logout } = useAuth();
  const [showSignOut, setShowSignOut] = useState(false);
  const isAuthenticated = Boolean(user);
  const shouldSync = isAuthenticated && prefs.dataStorage !== "Local Only";

  async function performLogout() {
    // On iOS, focused low-font inputs can leave the WebView zoomed after route/state changes.
    const el = document.activeElement;
    if (el && typeof el.blur === "function") el.blur();
    window.scrollTo(0, 0);
    try {
      await logout();
    } catch (err) {
      // logout() (firebaseSignOut) was never awaited/caught here before —
      // any failure was a silent unhandled rejection, indistinguishable from
      // the button doing nothing at all.
      showToast(`Sign out failed: ${err?.message || 'unknown error'}`);
    }
  }

  useEffect(() => {
    if (loading || !isAuthenticated || prefs.dataStorage === "Local Only") return;
    ensureUser().catch(() => {});
    pullData().then(data => {
      if (!data) return;
      // Stash: merge by id/signature — new items get added, existing ones get
      // their fields refreshed from the cloud (see mergeStashFromCloud).
      if (data.stash.length > 0) {
        setStash(prev => mergeStashFromCloud(prev, data.stash).merged);
      }
      // Intake / sessions / checkins: merge by ts — add any entries not already local
      if (data.intake.length > 0) {
        setIntake(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const added = data.intake.filter(e => !seen.has(e.ts));
          return added.length ? [...prev, ...added].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
      if (data.sessions.length > 0) {
        setSessions(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const added = data.sessions.filter(e => !seen.has(e.ts));
          return added.length ? [...prev, ...added].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
      if (data.checkins.length > 0) {
        setCheckins(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const added = data.checkins.filter(e => !seen.has(e.ts));
          return added.length ? [...prev, ...added].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
    });
  }, [loading, isAuthenticated, prefs.dataStorage]);


  // All hooks must be declared before any conditional returns
  useEffect(()=>{ setShowCheckin(false); setShowQuickIntake(false); setShowStashForm(false); },[]);
  // Opening a new stash item lands on the label scan — the main way people
  // fill the form in. (Edits and dupes manage their own scroll/focus.)
  useEffect(()=>{
    if(!showStashForm || editSId || sGenericMode) return;
    document.getElementById("stash-scan-section")?.scrollIntoView({behavior:"smooth",block:"start"});
  },[showStashForm]);
  useEffect(()=>{
    if(kushyQuery.length<2){setKushyResults([]);return;}
    if(kushySelected) return;
    const timer=setTimeout(()=>{
      fetch(`${STRAIN_API}?q=${encodeURIComponent(kushyQuery)}&limit=10`)
        .then(r=>r.json())
        .then(data=>setKushyResults(data.strains||[]))
        .catch(()=>setKushyResults([]));
    },300);
    return ()=>clearTimeout(timer);
  },[kushyQuery,kushySelected]);

  if (window.location.pathname === "/auth/callback") return <AuthCallback />;
  if (window.location.hash.startsWith("#/sso-callback"))   return <AuthCallback />;
  if (window.location.pathname === "/privacy") return <PrivacyPolicy />;
  if (window.location.pathname === "/terms") return <TermsOfService />;
  if (loading) return <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading…</div>;
  if (sessionExpired) return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 24, background: t.bg, color: t.text, fontFamily: "inherit", textAlign: "center" }}>
      <p style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Your session expired</p>
      <p style={{ fontSize: 15, color: t.muted, margin: 0 }}>You were away for a while. Tap below to sign in again.</p>
      <button onClick={refreshSession} style={{ marginTop: 8, padding: "12px 32px", background: t.sage, border: "none", borderRadius: 12, color: "#fff", fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Sign In Again
      </button>
    </div>
  );
  if (!isAuthenticated) return <LandingPage onLoginGoogle={loginWithGoogle} onLoginApple={loginWithApple} onLoginEmail={loginWithEmail} onResetPassword={resetPassword} />;

  const days = daysSince(startDate);
  const flowers = stash.filter(s=>s.cat==="Flower");
  const selStash = stash.find(s=>s.id===iStashId)||null;
  // Compatible stash categories per intake method
  const COMPATIBLE_CATS = {
    "Bowl":          ["Flower","Concentrate"],
    "Bong":          ["Flower","Concentrate"],
    "Joint":         ["Flower","Pre-Roll","Blunt"],
    "One Hitter":    ["Flower","Concentrate"],
    "Dry Herb Vape": ["Flower"],
    "Dab":           ["Concentrate"],
    "Oil Vape":      ["Vape Cart"],
    "Edible":        ["Edible","Recipe"],
    "Tincture":      ["Tincture"],
  };
  const compatibleCats = COMPATIBLE_CATS[iMethod] || null;
  const stashMatches = stash.filter(s => {
    if(!s.isCurrent) return false;
    if(s.finished) return false;
    if(compatibleCats && !compatibleCats.includes(s.cat)) return false;
    if(iStashQuery.length>0 && !s.name.toLowerCase().includes(iStashQuery.toLowerCase())) return false;
    return true;
  });
  const currentItems = stash.filter(s=>s.isCurrent);

  function applyKushyResult(k) {
    setSName(k.name||"");
    const typeMap={indica:"Indica",sativa:"Sativa",hybrid:"Hybrid","indica-dominant hybrid":"Indica Hybrid","sativa-dominant hybrid":"Sativa Hybrid"};
    setSFlowerType(typeMap[(k.strain_type||k.type||"").toLowerCase()]||"Hybrid");
    setSPot("");setSThca("");setSCbd("");
    // Effects and flavors come as arrays from the API
    setSEffects(Array.isArray(k.effects)?k.effects:(k.effects||"").split(",").map(s=>s.trim()).filter(Boolean));
    setSFlavors(Array.isArray(k.flavors)?k.flavors:(k.flavors||k.flavor||"").split(",").map(s=>s.trim()).filter(Boolean));
    setSTerps([]);
    setKushySelected(true); setKushyResults([]);
  }

  function resetStashForm() {
    setSName("");setSMfr("");setSWeight("");setSTotalPrice("");setSRating(0);setSComment("");setWebLookupResult(null);setShowWebLookup(false);setCoaUrl("");setEditSId(null);
    setSPot("");setSThca("");setSCbd("");setSD9("");setSGeekMode(false);setSTotalCannabinoid("");setSTotalTerpene("");setSFlowerType("Hybrid");setSGrowType("Unknown");setSTerps([]);setSEffects([]);setSFlavors([]);
    setKushyQuery("");setKushyResults([]);setKushySelected(false);
    setSEdibleType("Gummy"); setSEdibleQty(""); setSEdiblePriceEach(""); setSEdibleStrain(""); setSEdibleStrainType(""); setSBevOz(""); setSBevMl(""); setSStrength(emptyStrength()); setSLiveResin(false); setSLiveRosin(false); setSEdibleSolvent("");
    setSRecipeType(""); setSRecipeUrl(""); setSRecipeText(""); setSRecipeDose(""); setSRecipeServings(""); setSRecipeStashId(null);
    setSConType("Wax");setSConStrain("");setSConPot("");setSConWeight("");
    setSVapeSize("1g");setSVapeStrain("");setSVapePot("");setSVapeThca("");setSVapeCbd("");setSVapeSolvent("");setSVapeTerps([]);setSVapeEffects([]);setSVapeLiveResin(false);setSVapeLiveRosin(false);setSVapeMultiBarrel(false);setSVapeBarrels([]);
    setSGenericMode(false);setShowCoaSection(false);setSDispensary("");setSRetailUrl("");setSBatchId("");setSIsHemp(false);setSPrPackQty(5);setSPrJointWt("0.5");setSPrAdditives([]);setPartakeAfterSave(false);
    setSProducerName("");setSLicenseNumber("");setSSolventType("");setSVegan(null);setSKosher(null);setSGlutenFree(null);setSNonGmo(null);
    setSIngredients([]);setSFlavorAdditives([]);setSInfusionDetails("");setSNutrition(null);setSExpirationDate("");setSPackagedDate("");setSScanSource(null);
    scanImages.forEach(img=>img.preview&&URL.revokeObjectURL(img.preview));
    setScanImages([]);setScanLoading(false);setScanError("");setScanResult(null);
    }

  async function lookupStrain() {
    const q = sName.trim();
    if (!q) return;
    setWebLookupLoading(true);
    setWebLookupResult(null);
    try {
      const params = new URLSearchParams({ name: q });
      if (sMfr.trim()) params.set('brand', sMfr.trim());
      const res  = await fetch(`${BASE}/strain-lookup?${params}`);
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Strain not found'); return; }
      setWebLookupResult(data);
    } catch {
      showToast('Lookup failed — try again');
    } finally {
      setWebLookupLoading(false);
    }
  }

  async function syncNow() {
    setSyncStatus("syncing");
    setSyncMsg("");
    try {
      await ensureUser();
      const data = await pullData();
      if (!data) { setSyncStatus("error"); setSyncMsg("Could not reach server — check your connection."); return; }
      let added = 0;
      let updated = 0;
      if (data.stash.length > 0) {
        setStash(prev => {
          const result = mergeStashFromCloud(prev, data.stash);
          added += result.added;
          updated += result.updated;
          return result.merged;
        });
      }
      if (data.intake.length > 0) {
        setIntake(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const newItems = data.intake.filter(e => !seen.has(e.ts));
          added += newItems.length;
          return newItems.length ? [...prev, ...newItems].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
      if (data.sessions.length > 0) {
        setSessions(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const newItems = data.sessions.filter(e => !seen.has(e.ts));
          added += newItems.length;
          return newItems.length ? [...prev, ...newItems].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
      if (data.checkins.length > 0) {
        setCheckins(prev => {
          const seen = new Set(prev.map(e => e.ts));
          const newItems = data.checkins.filter(e => !seen.has(e.ts));
          added += newItems.length;
          return newItems.length ? [...prev, ...newItems].sort((a,b)=>new Date(b.ts)-new Date(a.ts)) : prev;
        });
      }
      if (data.errors) {
        // Partial failure: still keep whatever did come through above, but
        // don't report a false "already up to date" — that's indistinguishable
        // from a real empty result otherwise (see pullData() in sync.js).
        setSyncStatus("error");
        setSyncMsg(`Sync incomplete (${added>0?`pulled ${added}, `:''}couldn't reach: ${data.errors.join(', ')})`);
        return;
      }
      setSyncStatus("ok");
      const parts = [];
      if (added > 0) parts.push(`${added} new item${added===1?"":"s"}`);
      if (updated > 0) parts.push(`${updated} item${updated===1?"":"s"} refreshed`);
      setSyncMsg(parts.length ? `Pulled ${parts.join(", ")} from cloud.` : "Already up to date.");
    } catch (err) {
      setSyncStatus("error");
      setSyncMsg(err?.message || "Sync failed — try again.");
    }
  }

  async function uploadLocalCacheNow() {
    setSyncStatus("syncing");
    setSyncMsg("");
    try {
      await ensureUser();
      const result = await uploadLocalCache({ stash, intake, sessions, checkins });
      const uploadedTotal = result.uploaded.stash + result.uploaded.intake + result.uploaded.sessions + result.uploaded.checkins;
      const detail = [
        result.uploaded.stash ? `${result.uploaded.stash} stash` : null,
        result.uploaded.intake ? `${result.uploaded.intake} intake` : null,
        result.uploaded.sessions ? `${result.uploaded.sessions} sessions` : null,
        result.uploaded.checkins ? `${result.uploaded.checkins} check-ins` : null,
      ].filter(Boolean).join(", ");

      const stashSkipped = result.skipped.stash > 0 ? ` Stash skipped (${result.skipped.stash}) because cloud stash already has data.` : "";

      setSyncStatus("ok");
      setSyncMsg(
        uploadedTotal > 0
          ? `Uploaded ${uploadedTotal} item${uploadedTotal === 1 ? "" : "s"}${detail ? ` (${detail})` : ""}.${stashSkipped}`
          : `No new local items to upload.${stashSkipped}`
      );
    } catch (err) {
      setSyncStatus("error");
      setSyncMsg(err?.message || "Local cache upload failed — try again.");
    }
  }

  function exportLocalBackupNow() {
    try {
      const payload = {
        exportedAt: new Date().toISOString(),
        appVersion,
        data: {
          sessions,
          intake,
          stash,
          checkins,
          currentFlavors,
        },
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `habitrak-backup-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast("Backup exported");
    } catch {
      showToast("Backup export failed");
    }
  }

  async function importLocalBackupNow(file) {
    if (!file) return;
    let nextSessions = [];
    let nextIntake = [];
    let nextStash = [];
    let nextCheckins = [];
    let nextFlavors = [];

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const source = parsed?.data || parsed;

      nextSessions = Array.isArray(source?.sessions) ? source.sessions : [];
      nextIntake = Array.isArray(source?.intake) ? source.intake : [];
      nextStash = Array.isArray(source?.stash) ? source.stash : [];
      nextCheckins = Array.isArray(source?.checkins) ? source.checkins : [];
      nextFlavors = Array.isArray(source?.currentFlavors) ? source.currentFlavors : [];

      const hasKnownKeys = Array.isArray(source?.sessions) || Array.isArray(source?.intake) || Array.isArray(source?.stash) || Array.isArray(source?.checkins);
      if (!hasKnownKeys) {
        throw new Error("Missing HabitTrack data keys");
      }

      setSessions(nextSessions);
      setIntake(nextIntake);
      setStash(nextStash);
      setCheckins(nextCheckins);
      setCurrentFlavors(nextFlavors);

      save(SK.sessions, nextSessions);
      save(SK.intake, nextIntake);
      save(SK.stash, nextStash);
      save(SK.checkins, nextCheckins);
      save(SK.currentFlavors, nextFlavors);

      setSyncStatus("ok");
      setSyncMsg(`Imported backup (${nextStash.length} stash, ${nextIntake.length} intake, ${nextSessions.length} sessions, ${nextCheckins.length} check-ins).`);
    } catch {
      setSyncStatus("error");
      setSyncMsg("Backup import failed — use a valid HabitTrack backup JSON file.");
      if (importBackupInputRef.current) importBackupInputRef.current.value = "";
      return;
    }

    if (shouldSync) {
      try {
        await ensureUser();
        const result = await uploadLocalCache({
          stash: nextStash,
          intake: nextIntake,
          sessions: nextSessions,
          checkins: nextCheckins,
        });
        const uploadedTotal = result.uploaded.stash + result.uploaded.intake + result.uploaded.sessions + result.uploaded.checkins;
        setSyncStatus("ok");
        setSyncMsg(`Imported backup (${nextStash.length} stash, ${nextIntake.length} intake, ${nextSessions.length} sessions, ${nextCheckins.length} check-ins) and uploaded ${uploadedTotal} new item${uploadedTotal === 1 ? "" : "s"} to cloud.`);
      } catch (err) {
        setSyncStatus("error");
        setSyncMsg(`Backup imported locally, but cloud upload failed: ${err?.message || "unknown error"}`);
      }
    }

    if (importBackupInputRef.current) importBackupInputRef.current.value = "";
  }

  async function submitCoaFeedback(rating, comment) {
    try {
      await fetch(`${BASE}/coa-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: coaUrl.trim(), rating, comment: comment || null, result: webLookupResult }),
      });
    } catch { /* fire-and-forget */ }
    setCoaFeedback('submitted');
    setCoaFeedbackComment("");
  }

  async function lookupCOA() {
    const url = coaUrl.trim();
    if (!url) return;
    setWebLookupLoading(true);
    setWebLookupResult(null);
    setCoaFeedback(null);
    setCoaFeedbackComment("");
    try {
      const res = await fetch(`${BASE}/coa-parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'COA lookup failed'); return; }
      setWebLookupResult({
        name:                 data.strain || sName.trim() || null,
        brand:                data.brand  || null,
        productName:          data.productName || null,
        lab:                  data.lab || null,
        labSampleId:          data.labSampleId || null,
        batchId:              data.batchId || null,
        flowerType:           data.flowerType || null,
        productCategory:      data.productCategory || null,
        thcaPct:              data.thca,
        thcPct:               data.totalThc,
        cbdPct:               data.cbd,
        cbgPct:               data.cbg,
        cbgaPct:              data.cbga,
        cbnPct:               data.cbn,
        cbcPct:               data.cbc,
        thcvPct:              data.thcv,
        cbdvPct:              data.cbdv,
        totalCannabinoidsPct: data.totalCannabinoids,
        totalTerpenesPct:     data.totalTerpenes,
        thcMgPerDose:         data.thcMgPerDose,
        thcMgPerUnit:         data.thcMgPerUnit,
        cbdMgPerDose:         data.cbdMgPerDose,
        cbnMgPerDose:         data.cbnMgPerDose,
        cbdvMgPerDose:        data.cbdvMgPerDose,
        thcvMgPerDose:        data.thcvMgPerDose,
        cbgaMgPerDose:        data.cbgaMgPerDose,
        totalMgPerDose:       data.totalMgPerDose,
        totalMgPerUnit:       data.totalMgPerUnit,
        terpenes:             data.terpenes || [],
        liveRosin:            data.liveRosin   || false,
        liveResin:            data.liveResin   || false,
        distillate:           data.distillate  || false,
        verified:             data.verified || false,
        cached:               data.cached || false,
        source:               data.verified ? 'COA (verified)' : 'COA',
      });
    } catch {
      showToast('COA lookup failed — check the URL');
    } finally {
      setWebLookupLoading(false);
    }
  }

  function applyWebResult(data) {
    const pc = data.productCategory;
    const isVapeCOA      = pc === 'Vape Cart' || pc === 'Vape';
    const isPreRollCOA   = pc === 'Pre-Roll';
    const isBluntCOA     = pc === 'Blunt';
    const isConcentrate  = pc === 'Concentrate';
    const isTincture     = pc === 'Tincture';
    const isEdibleCOA    = pc === 'Edible' || (!pc && (data.thcMgPerUnit || data.thcMgPerDose));

    // ── Auto-switch category ──────────────────────────────────────────────────
    if      (isVapeCOA    && sCat !== 'Vape Cart')  setsCat('Vape Cart');
    else if (isPreRollCOA && sCat !== 'Pre-Roll')   setsCat('Pre-Roll');
    else if (isBluntCOA   && sCat !== 'Blunt')      setsCat('Blunt');
    else if (isConcentrate && sCat === 'Flower')    setsCat('Concentrate');
    else if (isTincture   && sCat === 'Flower')     setsCat('Tincture');
    else if (isEdibleCOA  && sCat === 'Flower') {
      setsCat('Edible');
      const pn = (data.name || data.productName || '').toLowerCase();
      if      (/gummy|gummies/.test(pn))         setSEdibleType('Gummy');
      else if (/choco|brownie|cookie|baked/.test(pn)) setSEdibleType('Baked');
      else if (/drink|bev|soda|seltz/.test(pn))  setSEdibleType('Bev');
      else if (/mint|candy|lozenge/.test(pn))    setSEdibleType('Candy');
      else if (/oil|tincture/.test(pn))          setSEdibleType('Oil');
    }

    // ── Name ─────────────────────────────────────────────────────────────────
    // Vape/Concentrate: product name is the stash name; strain stored separately
    // Flower/Pre-Roll/Blunt: strain name is the stash name
    const stashName = (isVapeCOA || isConcentrate)
      ? (data.productName || data.name || '')
      : (data.name || data.productName || '');
    if (stashName) setSName(stashName);
    if (data.brand) setSMfr(data.brand);
    if (data.batchId) setSBatchId(data.batchId);

    // ── Vape Cart specific ───────────────────────────────────────────────────
    if (isVapeCOA) {
      if (data.name) setSVapeStrain(data.name);       // strain name separate from product name
      const pn = (data.productName || '').toLowerCase();
      if      (data.liveRosin)         { setSVapeLiveRosin(true); setSVapeSolvent('Solventless'); }
      else if (data.liveResin)         { setSVapeLiveResin(true); setSVapeSolvent('Hydrocarbon'); }
      else if (/distillat/i.test(pn))  setSVapeSolvent('Distillate');
      else if (/co2/i.test(pn))        setSVapeSolvent('CO2');
    }

    // ── Flower / Pre-Roll / Blunt specific ───────────────────────────────────
    if (data.flowerType || data.strainType) setSFlowerType(data.flowerType || data.strainType);

    // ── Cannabinoid % ────────────────────────────────────────────────────────
    if (data.thcaPct != null) { setSThca(String(data.thcaPct)); setSGeekMode(true); }
    if (data.thcPct  != null) setSPot(String(data.thcPct));
    if (data.cbdPct  != null) setSCbd(String(data.cbdPct));
    if (data.totalCannabinoidsPct != null) setSTotalCannabinoid(String(data.totalCannabinoidsPct));
    if (data.totalTerpenesPct     != null) setSTotalTerpene(String(data.totalTerpenesPct));

    // ── Minor cannabinoids / mg strength ────────────────────────────────────
    if (isEdibleCOA || isTincture) {
      const thcMg = data.thcMgPerUnit ?? data.thcMgPerDose;
      if (thcMg)              setSStrength(prev => ({ ...prev, THC:  String(thcMg) }));
      if (data.cbdMgPerDose)  setSStrength(prev => ({ ...prev, CBD:  String(data.cbdMgPerDose) }));
      const cbdnMg = data.cbnMgPerDose ?? data.cbdvMgPerDose;
      if (cbdnMg)             setSStrength(prev => ({ ...prev, CBDn: String(cbdnMg) }));
      if (data.thcvMgPerDose) setSStrength(prev => ({ ...prev, THCv: String(data.thcvMgPerDose) }));
      if (data.cbgaMgPerDose) setSStrength(prev => ({ ...prev, CBGa: String(data.cbgaMgPerDose) }));
      if (data.liveRosin) setSLiveRosin(true);
      if (data.liveResin) setSLiveResin(true);
    } else {
      if (data.cbgPct  != null) setSStrength(prev => ({ ...prev, CBGa: String(data.cbgPct) }));
      if (data.cbnPct  != null) setSStrength(prev => ({ ...prev, CBDn: String(data.cbnPct) }));
      if (data.cbcPct  != null) setSStrength(prev => ({ ...prev, CBC:  String(data.cbcPct) }));
      if (data.thcvPct != null) setSStrength(prev => ({ ...prev, THCv: String(data.thcvPct) }));
    }

    // ── Terpenes & effects ───────────────────────────────────────────────────
    if (data.terpenes?.length > 0) {
      if (isVapeCOA || sCat === 'Vape Cart') setSVapeTerps(data.terpenes);
      else setSTerps(data.terpenes);
    }
    if (data.effects?.length > 0) {
      if (isVapeCOA || sCat === 'Vape Cart') setSVapeEffects(data.effects);
      else setSEffects(data.effects);
    }
    if (data.flavors?.length > 0) setSFlavors(data.flavors);

    setWebLookupResult(null);
    setShowWebLookup(false);
    const src = data.source || '';
    showToast(src.includes('verified') ? '✓ Verified lab data applied' : src.includes('COA') ? '✓ Lab data applied from COA' : '✓ Strain data applied');
  }

  // ── Package photo scan ──────────────────────────────────────────────────────
  // Photos never touch localStorage/disk — decoded in-memory, compressed to a
  // JPEG that preserves fine-print detail, sent to /api/package-scan, then
  // dropped once the extracted JSON comes back.
  const MAX_SCAN_BASE64_CHARS = 5_500_000;
  const SCAN_COMPRESSION_STEPS = [
    { maxDim: 2200, quality: 0.92 },
    { maxDim: 2000, quality: 0.9 },
    { maxDim: 1800, quality: 0.88 },
    { maxDim: 1600, quality: 0.85 },
  ];

  function canvasToBlob(canvas, quality) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Could not process that photo'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', quality);
    });
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('Could not read that photo'));
      reader.readAsDataURL(blob);
    });
  }

  async function compressImageFile(file) {
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const loaded = new Image();
        loaded.onload = () => resolve(loaded);
        loaded.onerror = () => reject(new Error('Could not load that photo — try again'));
        loaded.src = objectUrl;
      });

      let bestAttempt = null;

      for (const step of SCAN_COMPRESSION_STEPS) {
        let { width, height } = img;
        if (Math.max(width, height) > step.maxDim) {
          const ratio = step.maxDim / Math.max(width, height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        const blob = await canvasToBlob(canvas, step.quality);
        const base64 = await blobToBase64(blob);
        bestAttempt = { base64, mediaType: 'image/jpeg', preview: URL.createObjectURL(blob) };

        if (base64.length <= MAX_SCAN_BASE64_CHARS) {
          return bestAttempt;
        }

        URL.revokeObjectURL(bestAttempt.preview);
      }

      if (bestAttempt) return bestAttempt;
      throw new Error('Could not process that photo');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  async function addPackagePhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setScanError('');
    try {
      const compressed = await compressImageFile(file);
      setScanImages(prev => [...prev, compressed].slice(0, MAX_SCAN_IMAGES));
    } catch (err) {
      setScanError(err?.message || 'Could not process that photo');
    }
  }

  function removeScanImage(idx) {
    setScanImages(prev => {
      const target = prev[idx];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  async function runPackageScan() {
    if (scanImages.length === 0) return;
    setScanLoading(true); setScanError(''); setScanResult(null);
    try {
      const images = scanImages.map(img => ({ data: img.base64, mediaType: img.mediaType }));
      const data = await scanPackage(images, sCat);
      setScanResult(data);
    } catch (err) {
      setScanError(err?.message || 'Scan failed — try again');
    } finally {
      setScanLoading(false);
    }
  }

  function clearPackageScan() {
    scanImages.forEach(img => img.preview && URL.revokeObjectURL(img.preview));
    setScanImages([]); setScanResult(null); setScanError('');
  }

  function applyPackageScanResult(data) {
    const isVapeScan       = data.productType === 'Vape Cart' || sCat === 'Vape Cart';
    const isEdibleScan     = data.productType === 'Edible' || data.productType === 'Beverage' || sCat === 'Edible';
    const isTinctureScan   = data.productType === 'Tincture' || sCat === 'Tincture';
    const isConcentrateScan = data.productType === 'Concentrate' || sCat === 'Concentrate';
    const isPreRollScan    = data.productType === 'Pre-Roll' || data.productType === 'Blunt' || sCat === 'Pre-Roll' || sCat === 'Blunt';

    if (data.productType === 'Beverage') { setsCat('Edible'); setSEdibleType('Bev'); }
    else if (data.productType && STASH_CATS.includes(data.productType)) setsCat(data.productType);

    if (data.brand) setSMfr(data.brand);
    if (data.website) setSRetailUrl(data.website);
    if (data.manufacturer && data.manufacturer !== data.brand) setSProducerName(data.manufacturer);
    if (data.licenseNumber) setSLicenseNumber(data.licenseNumber);
    if (data.batchId) setSBatchId(data.batchId);
    // Strain name — Vape Cart/Concentrate have a separate "Strain" field from the
    // product name; everywhere else (Flower/Pre-Roll/Blunt) the strain IS the name.
    if (data.strainName) {
      if (isVapeScan) setSVapeStrain(data.strainName);
      else if (isConcentrateScan) setSConStrain(data.strainName);
      else setSName(data.strainName);
    }
    if (data.flowerType) setSFlowerType(data.flowerType);
    if (data.growType) setSGrowType(data.growType);

    if (data.quantity != null) {
      if (isPreRollScan) setSPrPackQty(data.quantity);
      else if (isEdibleScan) setSEdibleQty(String(data.quantity));
    }

    // Pre-Roll/Blunt infusions (Kief/Hash/Wax/Shatter/Diamonds/Live Resin/Live
    // Rosin) come back as data.infusedAdditives, not the liveRosin/liveResin
    // booleans below — the pre-roll form has no field for those booleans at
    // all, so setting them here would be silently dropped on save.
    if (isPreRollScan) {
      if (data.infusedAdditives?.length) setSPrAdditives(data.infusedAdditives);
    } else {
      if (data.liveRosin) {
        if (isVapeScan) { setSVapeLiveRosin(true); setSVapeSolvent('Solventless'); }
        else setSLiveRosin(true);
      }
      if (data.liveResin) {
        if (isVapeScan) { setSVapeLiveResin(true); setSVapeSolvent('Hydrocarbon'); }
        else setSLiveResin(true);
      }
    }

    if (data.thcMgPerUnit != null && (isEdibleScan || isTinctureScan)) {
      setSStrength(prev => ({ ...prev, THC: String(data.thcMgPerUnit) }));
    }

    if (isEdibleScan || isTinctureScan) {
      const findMg = (matcher) => data.cannabinoids?.find(c => matcher(String(c.name || '').toLowerCase()))?.mg;
      const cbdMg = findMg(n => n === 'cbd');
      const cbcMg = findMg(n => n === 'cbc');
      const cbdnMg = findMg(n => n === 'cbn' || n === 'cbdv');
      const thcvMg = findMg(n => n === 'thcv');
      const cbgaMg = findMg(n => n === 'cbga' || n === 'cbg');
      setSStrength(prev => ({
        ...prev,
        CBD: cbdMg != null ? String(cbdMg) : prev.CBD,
        CBC: cbcMg != null ? String(cbcMg) : prev.CBC,
        CBDn: cbdnMg != null ? String(cbdnMg) : prev.CBDn,
        THCv: thcvMg != null ? String(thcvMg) : prev.THCv,
        CBGa: cbgaMg != null ? String(cbgaMg) : prev.CBGa,
      }));
    } else {
      // Flower/Pre-Roll/Blunt/Vape/Concentrate: same minor-cannabinoid slots,
      // but as printed percentages rather than mg-per-dose. This was
      // previously never populated from a package scan at all — CBG/CBN/CBC/
      // THCv printed on non-edible packaging (e.g. "CBG: 0.4%") were
      // extracted into data.cannabinoids by the model but then silently
      // dropped here, even though the form has a field for them.
      const findPct = (matcher) => data.cannabinoids?.find(c => matcher(String(c.name || '').toLowerCase()))?.pct;
      const cbcPct = findPct(n => n === 'cbc');
      const cbdnPct = findPct(n => n === 'cbn' || n === 'cbdv');
      const thcvPct = findPct(n => n === 'thcv');
      const cbgaPct = findPct(n => n === 'cbga' || n === 'cbg');
      if (cbcPct != null || cbdnPct != null || thcvPct != null || cbgaPct != null) {
        setSStrength(prev => ({
          ...prev,
          CBC: cbcPct != null ? String(cbcPct) : prev.CBC,
          CBDn: cbdnPct != null ? String(cbdnPct) : prev.CBDn,
          THCv: thcvPct != null ? String(thcvPct) : prev.THCv,
          CBGa: cbgaPct != null ? String(cbgaPct) : prev.CBGa,
        }));
      }
    }

    if (data.totalThcPct != null) setSPot(String(data.totalThcPct));
    else {
      const thc = data.cannabinoids?.find(c => /^d?9?[- ]?thc$/i.test(c.name.replace(/\s+/g, '')));
      if (thc?.pct != null) setSPot(String(thc.pct));
    }
    const thca = data.cannabinoids?.find(c => /thca/i.test(c.name));
    if (thca?.pct != null) setSThca(String(thca.pct));
    const cbd = data.cannabinoids?.find(c => /^cbd$/i.test(c.name));
    if (cbd?.pct != null) setSCbd(String(cbd.pct));
    if (data.totalCannabinoidsPct != null) setSTotalCannabinoid(String(data.totalCannabinoidsPct));
    if (data.totalTerpenesPct != null) setSTotalTerpene(String(data.totalTerpenesPct));
    if (data.terpenes?.length > 0) {
      if (isVapeScan) setSVapeTerps(data.terpenes); else setSTerps(data.terpenes);
    }

    if (data.solventType) {
      setSSolventType(data.solventType);
      if (isEdibleScan) setSEdibleSolvent(data.solventType);
      if (isVapeScan) setSVapeSolvent(data.solventType);
    }

    if (data.weightValue != null) {
      const unit = (data.weightUnit || '').toLowerCase();
      let grams = null;
      if (unit === 'g' || unit === 'gram' || unit === 'grams') grams = data.weightValue;
      else if (unit === 'oz') grams = data.weightValue * 28.3495;
      if (grams != null) setSWeight(String(Math.round(grams * 1000) / 1000));
    }

    if (data.ingredients?.length)     setSIngredients(data.ingredients);
    if (data.flavorAdditives?.length) setSFlavorAdditives(data.flavorAdditives);
    if (data.vegan != null)      setSVegan(data.vegan);
    if (data.kosher != null)     setSKosher(data.kosher);
    if (data.glutenFree != null) setSGlutenFree(data.glutenFree);
    if (data.nonGmo != null)     setSNonGmo(data.nonGmo);
    if (data.infusionDetails) setSInfusionDetails(data.infusionDetails);
    if (data.nutrition)       setSNutrition(data.nutrition);
    if (data.expirationDate)  setSExpirationDate(data.expirationDate);
    if (data.packagedDate)    setSPackagedDate(data.packagedDate);

    setSScanSource('package_photo');
    clearPackageScan();
    showToast('✓ Package data applied — review before saving');
  }

  async function confirmCOA(data) {
    if (!data.labSampleId) { applyWebResult(data); return; }
    setCoaConfirming(true);
    try {
      await fetch(`${BASE}/coa-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lab_sample_id: data.labSampleId,
          strain:       data.name    || '',
          brand:        data.brand   || '',
          flower_type:  data.flowerType || '',
        }),
      });
    } catch { /* non-fatal */ }
    setCoaConfirming(false);
    applyWebResult(data);
  }

  function saveStash() {
    if(!sName.trim()) return;
    const pricePerGram = (sTotalPrice && sWeight && parseFloat(sWeight) > 0)
      ? (parseFloat(sTotalPrice) / parseFloat(sWeight)).toFixed(2)
      : null;
    const priceLabel = sCat === "Tincture" ? "pricePerMl" : "pricePerGram";
    const base = {
      name:sName.trim(), manufacturer:sMfr.trim(), batchId:sBatchId.trim()||undefined, coaUrl:coaUrl.trim()||undefined, retailUrl:sRetailUrl.trim()||undefined, dispensary:sDispensary.trim(), isHemp:sIsHemp, weight:sWeight, totalPrice:sTotalPrice, [priceLabel]:pricePerGram, rating:sRating, comment:sComment.trim(), cat:sCat,
      // favorite/finished deliberately omitted here — they're not form fields, so on
      // an edit they must survive from the existing item, not get reset to false.
      // The new-item branch below sets them explicitly.
      // Package-photo scan fields — shared across categories
      producerName:sProducerName.trim()||undefined, licenseNumber:sLicenseNumber.trim()||undefined, solventType:sSolventType||undefined,
      vegan:sVegan, kosher:sKosher, glutenFree:sGlutenFree, nonGmo:sNonGmo,
      ingredients:sIngredients.length?sIngredients:undefined, flavorAdditives:sFlavorAdditives.length?sFlavorAdditives:undefined,
      infusionDetails:sInfusionDetails.trim()||undefined, nutrition:sNutrition||undefined,
      expirationDate:sExpirationDate||undefined, packagedDate:sPackagedDate||undefined, scanSource:sScanSource||undefined,
    };
    let extra = {};
    if(sCat==="Flower") extra={ flowerType:sFlowerType, growType:sGrowType, potency:sPot, thca:sThca, d9thc:sD9, cbd:sCbd, totalCannabinoidPct:sTotalCannabinoid, totalTerpenePct:sTotalTerpene, terpenes:sTerps, effects:sEffects, flavors:sFlavors };
    if(sCat==="Pre-Roll"||sCat==="Blunt") extra={ flowerType:sFlowerType, growType:sGrowType, potency:sPot, thca:sThca, d9thc:sD9, cbd:sCbd, totalCannabinoidPct:sTotalCannabinoid, totalTerpenePct:sTotalTerpene, terpenes:sTerps, additives:sPrAdditives, packQty:sPrPackQty, jointWt:sPrJointWt };
    if(sCat==="Edible") extra={ edibleType:sEdibleType, edibleQty:sEdibleQty, bevOz:sBevOz, bevMl:sBevMl, strength:sStrength, liveResin:sLiveResin, liveRosin:sLiveRosin, solvent:(!sLiveRosin&&sEdibleSolvent)||undefined };
    if(sCat==="Tincture") extra={ strength:sStrength };
    if(sCat==="Concentrate") extra={ conType:sConType, strain:sConStrain, potency:sConPot, weight:sConWeight };
    if(sCat==="Vape Cart") extra={ vapeHardware:sVapeHardware, vapeSize:sVapeSize, strain:sVapeStrain, potency:sPot, thca:sThca, cbd:sCbd, totalCannabinoidPct:sTotalCannabinoid, totalTerpenePct:sTotalTerpene, solvent:sVapeSolvent, terpenes:sVapeTerps, effects:sVapeEffects, liveResin:sVapeLiveResin, liveRosin:sVapeLiveRosin, strength:sStrength, multiBarrel:sVapeMultiBarrel||undefined, barrels:sVapeMultiBarrel?sVapeBarrels.filter(b=>b.strain.trim()||b.potency):undefined };
    if(sCat==="Recipe") extra={ recipeType:sRecipeType||undefined, recipeUrl:sRecipeUrl.trim()||undefined, recipeText:sRecipeText.trim()||undefined, dosePerServing:sRecipeDose.trim()||undefined, servings:sRecipeServings?parseFloat(sRecipeServings):undefined, recipeStashId:sRecipeStashId||undefined };
    if(sGenericMode && GENERIC_STASH[sCat]?.flowerType && !extra.flowerType) extra.flowerType = GENERIC_STASH[sCat].flowerType;
    const item = {...base,...extra};
    if(editSId){
      const existing = stash.find(x=>x.id===editSId);
      const updated = {...existing,...item};
      setStash(prev=>prev.map(x=>x.id===editSId?updated:x));
      if(shouldSync) syncStash(updated);
      setEditSId(null);
    }
    else {
      const newId = Date.now();
      const newEntry = {id:newId, clientItemId:createStashClientItemId(), favorite:false, finished:false, ...item};
      setStash(prev=>[...prev,newEntry]);
      if(shouldSync) syncStash(newEntry);
      // Auto-set new item as current — trigger re-up confirm with today
      const today = new Date().toISOString().slice(0,10);
      setReupDate(today);
      setReupConfirmId(newId);
    }
    resetStashForm(); setShowStashForm(false);
  }

  function applyGenericDefaults(cat) {
    setSName(GENERIC_NAME);
    const g = GENERIC_STASH[cat];
    if(!g) return;
    if(g.flowerType) setSFlowerType(g.flowerType);
    if(g.potency) setSPot(g.potency);
    if(g.edibleType) setSEdibleType(g.edibleType);
    if(g.thcMg) setSStrength({...emptyStrength(), THC:g.thcMg});
  }
  // Entry points from the home page / stash tab
  function startGenericStash() {
    switchTab("stash"); resetStashForm();
    setSGenericMode(true); setsCat("Flower"); applyGenericDefaults("Flower");
    setShowStashForm(true);
  }
  function startScanStash() {
    switchTab("stash"); resetStashForm(); setShowStashForm(true);
  }

  function openEditStash(s) {
    setEditSId(s.id); setsCat(s.cat||"Flower"); setSName(s.name); setSMfr(s.manufacturer||""); setSBatchId(s.batchId||""); setCoaUrl(s.coaUrl||""); setSRetailUrl(s.retailUrl||""); setSDispensary(s.dispensary||""); setSIsHemp(s.isHemp||false); setSWeight(s.weight||""); setSTotalPrice(s.totalPrice||"");
    setSRating(s.rating||0); setSComment(s.comment||"");
    setSProducerName(s.producerName||""); setSLicenseNumber(s.licenseNumber||""); setSSolventType(s.solventType||"");
    setSVegan(s.vegan??null); setSKosher(s.kosher??null); setSGlutenFree(s.glutenFree??null); setSNonGmo(s.nonGmo??null);
    setSIngredients(s.ingredients||[]); setSFlavorAdditives(s.flavorAdditives||[]); setSInfusionDetails(s.infusionDetails||"");
    setSNutrition(s.nutrition||null); setSExpirationDate(s.expirationDate||""); setSPackagedDate(s.packagedDate||""); setSScanSource(s.scanSource||null);
    if(s.cat==="Flower"){
      const totalTHC = stashTotalTHCpct(s);
      setSFlowerType(s.flowerType||"Hybrid");
      setSGrowType(s.growType||"Unknown");
      setSPot(s.potency||(totalTHC ? String(totalTHC) : ""));
      setSThca(s.thca||"");
      setSD9(s.d9thc||"");
      setSGeekMode(!!(s.thca||s.d9thc));
      setSCbd(s.cbd||"");
      setSTotalCannabinoid(s.totalCannabinoidPct||"");
      setSTotalTerpene(s.totalTerpenePct||"");
      setSTerps(s.terpenes||[]);
      setSEffects(s.effects||[]);
      setSFlavors(s.flavors||[]);
      setKushySelected(true);
    }
    if(s.cat==="Pre-Roll"||s.cat==="Blunt"){
      const totalTHC = stashTotalTHCpct(s);
      setSFlowerType(s.flowerType||"Hybrid");
      setSGrowType(s.growType||"Unknown");
      setSPot(s.potency||(totalTHC ? String(totalTHC) : ""));
      setSThca(s.thca||"");
      setSD9(s.d9thc||"");
      setSGeekMode(!!(s.thca||s.d9thc));
      setSCbd(s.cbd||"");
      setSTotalCannabinoid(s.totalCannabinoidPct||"");
      setSTotalTerpene(s.totalTerpenePct||"");
      setSTerps(s.terpenes||[]);
      setSEffects(s.effects||[]);
      setSFlavors(s.flavors||[]);
      setKushySelected(true);
    }
    if(s.cat==="Edible"){ setSEdibleType(s.edibleType||"Gummy"); setSEdibleQty(s.edibleQty||""); setSBevOz(s.bevOz||""); setSBevMl(s.bevMl||""); setSStrength(s.strength||emptyStrength()); setSLiveResin(s.liveResin||false); setSLiveRosin(s.liveRosin||false); setSEdibleSolvent(s.solvent||""); }
    if(s.cat==="Recipe"){ setSRecipeType(s.recipeType||""); setSRecipeUrl(s.recipeUrl||""); setSRecipeText(s.recipeText||""); setSRecipeDose(s.dosePerServing||""); setSRecipeServings(s.servings?String(s.servings):""); setSRecipeStashId(s.recipeStashId||null); }
    if(s.cat==="Tincture"){ setSStrength(s.strength||emptyStrength()); }
    if(s.cat==="Concentrate"){ setSConType(s.conType||"Wax"); setSConStrain(s.strain||""); setSConPot(s.potency||""); setSConWeight(s.weight||""); }
    if(s.cat==="Vape Cart"){ setSVapeHardware(s.vapeHardware||"Cartridge"); setSVapeSize(s.vapeSize||"1g"); setSVapeStrain(s.strain||""); setSPot(s.potency||""); setSThca(s.thca||""); setSCbd(s.cbd||""); setSTotalCannabinoid(s.totalCannabinoidPct||""); setSTotalTerpene(s.totalTerpenePct||""); setSVapeSolvent(s.solvent||""); setSVapeTerps(s.terpenes||[]); setSVapeEffects(s.effects||[]); setSVapeLiveResin(s.liveResin||false); setSVapeLiveRosin(s.liveRosin||false); setSStrength(s.strength||emptyStrength()); setSVapeMultiBarrel(s.multiBarrel||false); setSVapeBarrels(s.barrels||[]); }
    setShowStashForm(true);
  }

  function toggleFavorite(id) {
    const item = stash.find(s=>s.id===id);
    setStash(prev=>prev.map(s=>s.id===id?{...s,favorite:!s.favorite}:s));
    if(shouldSync && item) syncStash({...item,favorite:!item.favorite});
  }
  function toggleCurrentFlavor(id) {
    const item = stash.find(s=>s.id===id);
    if(!item) return;
    if(item.isCurrent) {
      // Toggling OUT — no confirm
      setStash(prev=>prev.map(s=>s.id===id?{...s,isCurrent:false}:s));
      if(shouldSync) syncStash({...item,isCurrent:false});
    } else {
      // Toggling TO current — show confirm with date
      const today = new Date().toISOString().slice(0,10);
      setReupDate(today);
      setReupConfirmId(id);
    }
  }
  
  function confirmReup() {
    const existing = stash.find(s=>s.id===reupConfirmId);
    setStash(prev=>prev.map(s=>s.id===reupConfirmId?{...s,isCurrent:true,reupAt:reupDate}:s));
    if(shouldSync && existing) syncStash({...existing,isCurrent:true,reupAt:reupDate});
    const confirmedId = reupConfirmId;
    setReupConfirmId(null);
    setReupDate("");
    showToast("✅ Re-up confirmed!");
    if(partakeAfterSave) {
      const item = stash.find(s=>s.id===confirmedId);
      if(item) {
        const m = defaultMethodForCat(item.cat);
        setQiItem({...item, isCurrent:true});
        setQiMethod(m);
        setQiWeight((item.cat==="Pre-Roll"||item.cat==="Blunt") ? item.jointWt||"0.5" : METHOD_DEFAULT_WEIGHT[m]||"");
        setQiQty(1);
        setShowQuickIntake(true);
      }
      setPartakeAfterSave(false);
    }
}

  function dupeStash(s) {
    // Pre-fill form with strain profile, clear manufacturer/weight/price/ratings
    resetStashForm();
    setsCat(s.cat||"Flower");
    setSName(s.name);
    setSMfr(""); // cursor lands here — new manufacturer
    setSWeight(""); setSTotalPrice(""); setSRating(0); setSComment("");
    if(s.cat==="Flower"){
      setSFlowerType(s.flowerType||"Hybrid");
      setSGrowType(s.growType||"Unknown");
      setSPot(s.potency||"");
      setSThca(s.thca||"");
      setSCbd(s.cbd||"");
      setSTerps(s.terpenes||[]);
      setSEffects(s.effects||[]);
      setSFlavors(s.flavors||[]);
      setKushySelected(true);
    }
    setShowStashForm(true);
    setShowLibrary(false);
    setStashView("stash");
    setTimeout(()=>{
      document.getElementById("stash-mfr-input")?.focus();
      document.querySelector("main")?.scrollTo(0,0);
    }, 100);
  }
  function toggleArr(arr,setArr,key) { setArr(prev=>prev.includes(key)?prev.filter(x=>x!==key):[...prev,key]); }
  function delEntry(setter,id) { setter(prev=>prev.filter(e=>e.id!==id)); }
  // Stash deletes additionally need to reach the cloud row (see api/stash
  // DELETE) — otherwise a deleted item's row lingers server-side and
  // pullData() silently re-adds it on the next load/device since nothing
  // ever told the server it was gone.
  function delStashEntry(id) {
    const item = stash.find(s=>s.id===id);
    setStash(prev=>prev.filter(e=>e.id!==id));
    if (shouldSync && item) deleteStash(item);
  }

  function logSession(used) { setPulse(used?"yes":"checkin"); setTimeout(()=>setPulse(null),600); const s={id:Date.now(),used,ts:new Date().toISOString()}; setSessions(prev=>[s,...prev]); if(shouldSync) syncSession(s); if(used) setTab("intake"); }
  function submitCheckin() {
    if(!ciFeeling.length && !ciActivity.length && !ciNote.trim()) return;
    const c={id:Date.now(),feelings:ciFeeling,activities:ciActivity,note:ciNote.trim(),ts:new Date().toISOString()};
    setCheckins(prev=>[c,...prev]);
    if(shouldSync) syncCheckin(c);
    setCiFeeling([]); setCiActivity([]); setCiNote("");
    setShowCheckin(false); showToast("✅ Checked in!");
  }

  function showToast(msg) { setToast(msg); setTimeout(()=>setToast(null), 2000); }

  function submitQuickIntake() {
    if(!qiItem) return;
    const isWeighed=["Bowl","Bong","Joint","One Hitter","Dab"].includes(qiMethod);
    if(isWeighed&&!qiWeight) return;
    const isEdible = qiMethod==="Edible";
    const effPct = effectiveTHCpct(qiItem, qiMethod);
    const thc=(isWeighed&&effPct)?calcTHC(parseFloat(qiWeight)*qiQty,effPct,qiMethod):null;
    let edibleThcRaw=null, edibleThcLo=null, edibleThcHi=null, edibleMgEach=null;
    if(isEdible) {
      edibleMgEach = parseFloat(qiItem.strength?.THC||0) || null;
      if(edibleMgEach) {
        edibleThcRaw = parseFloat((edibleMgEach*qiQty).toFixed(1));
        const [elo,ehi] = METHOD_EFFICIENCY["Edible"];
        edibleThcLo = parseFloat((edibleThcRaw*elo).toFixed(1));
        edibleThcHi = parseFloat((edibleThcRaw*ehi).toFixed(1));
      }
    }
    const isOilVape = qiMethod==="Oil Vape";
    const qi={id:Date.now(),method:qiMethod,...(isWeighed&&{weight:parseFloat(qiWeight),qty:qiQty}),...(isEdible&&{edibleQty:qiQty,edibleSize:edibleMgEach}),...(isOilVape&&{oilHits:qiOilHits}),stashId:qiItem.id,stashName:qiItem.name,stashCat:qiItem.cat,...(thc&&{thcRaw:thc.raw,thcLo:thc.lo,thcHi:thc.hi}),...(edibleThcRaw&&{thcRaw:edibleThcRaw,thcLo:edibleThcLo,thcHi:edibleThcHi}),...(qiFeeling.length>0&&{feelings:qiFeeling}),...(qiActivity.length>0&&{activities:qiActivity}),note:"",ts:new Date().toISOString()};
    setIntake(prev=>[qi,...prev]);
    if(shouldSync) syncIntake(qi);
    setShowQuickIntake(false); setQiWeight(""); setQiQty(1); setQiOilHits(1); setQiActivity([]); setQiFeeling([]); setQiItem(null);
    switchTab("home"); showToast("✅ Logged!");
  }

  function defaultMethodForCat(cat) {
    if(cat==="Edible") return "Edible";
    if(cat==="Tincture") return "Tincture";
    if(cat==="Vape Cart") return "Oil Vape";
    if(cat==="Concentrate") return "Dab";
    if(cat==="Pre-Roll"||cat==="Blunt") return "Joint";
    return "Bowl";
}

  function buildIntakeEntry(ts) {
    const isWeighed=["Bowl","Bong","Joint","One Hitter","Dab"].includes(iMethod);
    const isDHV=iMethod==="Dry Herb Vape", isEdible=iMethod==="Edible", isVape=iMethod==="Oil Vape", isTincture=iMethod==="Tincture";
    if(isWeighed&&(!iWeight||parseFloat(iWeight)<=0)) return null;
    const effectiveWeight = (iMethod==="Joint"&&iJointSharing)
      ? parseFloat(iWeight) * Math.min(iJointHits/(JOINT_HITS[iJointSize]||Math.round(parseFloat(iWeight)*6)),1)
      : isDHV ? DRY_VAPE_WEIGHTS[iDryVapeType]||0.1
      : parseFloat(iWeight)*iQty;
    const effPct = effectiveTHCpct(selStash, iMethod);
    let thc = (isWeighed&&effPct) ? calcTHC(effectiveWeight,effPct,iMethod) : null;
    const additiveMg = iAdditives.reduce((s,a)=>s+(ADDITIVE_THC_BOOST[a]||0),0);
    let oilThcRaw=null, oilThcLo=null, oilThcHi=null;
    if(isVape) {
      const cartTHCpct = selStash?.potency ? parseFloat(selStash.potency)/100 : 0.8;
      const cartSizeG = selStash?.vapeSize==="0.5g" ? 0.5 : 1.0;
      const mgPerHit = (cartSizeG*1000*cartTHCpct)/(cartSizeG===0.5?100:200);
      oilThcRaw = parseFloat((mgPerHit*iOilHits).toFixed(1));
      const [lo,hi] = METHOD_EFFICIENCY["Oil Vape"];
      oilThcLo = parseFloat((oilThcRaw*lo).toFixed(1));
      oilThcHi = parseFloat((oilThcRaw*hi).toFixed(1));
    }
    if(thc && additiveMg>0) {
      thc = { raw:parseFloat((thc.raw+additiveMg).toFixed(1)), lo:parseFloat((thc.lo+additiveMg*0.6).toFixed(1)), hi:parseFloat((thc.hi+additiveMg).toFixed(1)) };
    }
    const dhvThc = isDHV&&effPct ? calcTHC(DRY_VAPE_WEIGHTS[iDryVapeType]||0.1, effPct, "Dry Herb Vape") : null;
    let edibleThcRaw=null, edibleThcLo=null, edibleThcHi=null;
    if(isEdible && iEdibleSize && parseFloat(iEdibleSize)>0) {
      edibleThcRaw = parseFloat((parseFloat(iEdibleSize)*iEdibleQty).toFixed(1));
      const [elo,ehi] = METHOD_EFFICIENCY["Edible"];
      edibleThcLo = parseFloat((edibleThcRaw*elo).toFixed(1));
      edibleThcHi = parseFloat((edibleThcRaw*ehi).toFixed(1));
    }
    return {id:Date.now(),method:iMethod,
      ...(isWeighed&&{weight:effectiveWeight,qty:iMethod==="Joint"&&iJointSharing?1:iQty}),
      ...(iMethod==="Joint"&&iJointSharing&&{jointSharing:true,jointHits:iJointHits,jointSize:parseFloat(iWeight)}),
      ...(isDHV&&{vapeType:iDryVapeType,weight:DRY_VAPE_WEIGHTS[iDryVapeType]||0.1}),
      ...(isEdible&&{edibleSize:parseFloat(iEdibleSize),edibleQty:iEdibleQty}),
      ...(isEdible&&edibleThcRaw&&{thcRaw:edibleThcRaw,thcLo:edibleThcLo,thcHi:edibleThcHi}),
      ...(isTincture&&{tinctureML:parseFloat(iTinctureML)}),
      ...(isVape&&{hits:iOilHits,thcRaw:oilThcRaw,thcLo:oilThcLo,thcHi:oilThcHi}),
      ...(!isVape&&!isEdible&&thc&&{thcRaw:thc.raw,thcLo:thc.lo,thcHi:thc.hi}),
      ...(!isVape&&!isEdible&&!thc&&dhvThc&&{thcRaw:dhvThc.raw,thcLo:dhvThc.lo,thcHi:dhvThc.hi}),
      ...(iAdditives.length>0&&{additives:iAdditives}),
      ...(iFeeling.length>0&&{feelings:iFeeling}),
      ...(iActivity.length>0&&{activities:iActivity}),
      stashId:iStashId,stashName:selStash?.name,stashCat:selStash?.cat,
      note:iNote.trim(),ts:ts||new Date(iTime).toISOString()};
  }

  function resetIntakeForm() {
    setIWeight(METHOD_DEFAULT_WEIGHT[iMethod]||""); setIQty(1); setIHits(1); setIEdibleQty(1); setITinctureML(""); setINote("");
    setIStashId(null); setIStashQuery(""); setIActivity([]); setIFeeling([]); setIAdditives([]);
    setIJointSharing(true); setIJointHits(2); setIJointSize("0.5"); setIOilHits(3); setIDryVapeType("Personal");
    setITime(localDateTimeString());
  }

  function addToBatch() {
    const entry = buildIntakeEntry();
    if(!entry) return;
    setPendingIntakes(prev=>[...prev, entry]);
    setIMethod("Bowl"); resetIntakeForm();
  }

  function logIntake() {
    const ts = new Date(iTime).toISOString();
    const current = buildIntakeEntry(ts);
    if(!current) return;
    const all = [...pendingIntakes, current];
    all.forEach((li,i) => {
      const entry = i<pendingIntakes.length ? li : current;
      setIntake(prev=>[entry,...prev]);
      if(shouldSync) syncIntake(entry);
    });
    setPendingIntakes([]);
    resetIntakeForm();
    showToast(`✅ Logged ${all.length > 1 ? all.length + " entries" : ""}`);
    switchTab("home");
  }

  const formTHCpct = effectiveTHCpct(selStash, iMethod);
  const formEffectiveWeight = (iMethod==="Joint"&&iJointSharing&&iWeight)
    ? parseFloat(iWeight) * Math.min(iJointHits/(JOINT_HITS[iJointSize]||10),1)
    : iMethod==="Dry Herb Vape" ? DRY_VAPE_WEIGHTS[iDryVapeType]||0.1
    : parseFloat(iWeight||"0")*iQty;
  const formAdditiveMg = iAdditives.reduce((s,a)=>s+(ADDITIVE_THC_BOOST[a]||0),0);
  const formTHCbase = (["Bowl","Bong","Joint","One Hitter","Dab","Dry Herb Vape"].includes(iMethod)&&(iWeight||iMethod==="Dry Herb Vape")&&formTHCpct)?calcTHC(formEffectiveWeight,formTHCpct,iMethod):null;
  const formTHC = formTHCbase ? { raw:parseFloat((formTHCbase.raw+formAdditiveMg).toFixed(1)), lo:parseFloat((formTHCbase.lo+formAdditiveMg*0.6).toFixed(1)), hi:parseFloat((formTHCbase.hi+formAdditiveMg).toFixed(1)) } : null;

  // Stats
  const statCutoff=Date.now()-statDays*86400000;
  const todayStart=new Date(); todayStart.setHours(0,0,0,0);
  // Blood THC model (Stats → 1d). Uses full history, not the 1d filter, since
  // stored THC from earlier days is still in the blood today.
  const todayIntake = intake.filter(e=>new Date(e.ts)>=todayStart);
  const blood = tab==="stats"&&statDays===1 ? buildBloodCurve(intake) : null;
  const bloodCurve = blood ? blood.total : [];
  const peakTHC = blood ? Math.max(...bloodCurve) : 0;
  const nowMinute = new Date().getHours()*60+new Date().getMinutes();
  const currentTHC = bloodCurve[nowMinute]||0;
  const currentResidual = blood ? blood.residual[nowMinute]||0 : 0;
  const showBloodCard = !!blood && peakTHC>0.05;
  const fIntake=intake.filter(e=>new Date(e.ts).getTime()>=statCutoff);
  const fSessions=sessions.filter(s=>new Date(s.ts).getTime()>=statCutoff);
  const fCheckins=checkins.filter(c=>new Date(c.ts).getTime()>=statCutoff);
  const todayCheckins=checkins.filter(c=>new Date(c.ts)>=todayStart);
  const usedCount=fSessions.filter(s=>s.used).length;
  const stashUsage={};
  fIntake.forEach(e=>{if(e.stashId) stashUsage[e.stashId]=(stashUsage[e.stashId]||0)+1;});

  const TABS=[["home","Vibe"],["intake","Track"],["stash","Stash"],["stats","Stats"],["prefs","⚙️"]];

  function switchTab(id) {
    setTab(id);
    // Reset strain lookup when leaving stash
    if(id !== "stash") {
      setKushyQuery(""); setKushyResults([]); setKushySelected(false);
      setShowStashForm(false); resetStashForm();
    }
  }

  function stashSubtitle(s) {
    const totalTHC = stashTotalTHCpct(s);
    if(s.cat==="Pre-Roll"||s.cat==="Blunt") return [`${s.packQty||"?"}×${s.jointWt||"0.5"}g`, s.flowerType, totalTHC&&`${totalTHC}% THC`, (s.additives||[]).length>0&&`+${s.additives.join(", ")}`].filter(Boolean).join(" · ");
    if(s.cat==="Flower") return [s.flowerType, totalTHC&&`${totalTHC}% THC`].filter(Boolean).join(" · ");
    if(s.cat==="Edible") return [s.edibleType, s.strength?.THC&&`${s.strength.THC}mg THC`].filter(Boolean).join(" · ");
    if(s.cat==="Tincture") return s.strength?.THC?`${s.strength.THC}mg/ml THC`:"";
    if(s.cat==="Concentrate") return [s.conType, s.potency&&`${s.potency}%`].filter(Boolean).join(" · ");
    if(s.cat==="Vape Cart") return [s.vapeHardware, s.vapeSize, s.multiBarrel?`${(s.barrels||[]).length} barrels`:(s.potency&&`${s.potency}%`)].filter(Boolean).join(" · ");
    return "";
  }

  function intakeSummary(entry) {
    if (!entry) return "";
    if (entry.method === "Edible" && entry.edibleSize) {
      return `${entry.edibleQty || 1} x ${entry.edibleSize}mg`;
    }
    if (entry.method === "Tincture" && entry.tinctureML) {
      return `${entry.tinctureML}ml`;
    }
    if (entry.method === "Oil Vape") {
      const hits = entry.hits || entry.oilHits;
      return hits ? `${hits} hit${hits === 1 ? "" : "s"}` : "";
    }
    if (entry.weight) {
      return `${entry.weight}g${entry.qty > 1 ? ` x ${entry.qty}` : ""}`;
    }
    return "";
  }

  function sortStash(items) {
    return [...(items || [])].sort((a, b) => {
      const aCurrent = a?.isCurrent ? 1 : 0;
      const bCurrent = b?.isCurrent ? 1 : 0;
      if (bCurrent !== aCurrent) return bCurrent - aCurrent;

      const aFav = a?.favorite ? 1 : 0;
      const bFav = b?.favorite ? 1 : 0;
      if (bFav !== aFav) return bFav - aFav;

      const aFinished = a?.finished ? 1 : 0;
      const bFinished = b?.finished ? 1 : 0;
      if (aFinished !== bFinished) return aFinished - bFinished;

      const aReup = a?.reupAt ? new Date(a.reupAt).getTime() : 0;
      const bReup = b?.reupAt ? new Date(b.reupAt).getTime() : 0;
      if (bReup !== aReup) return bReup - aReup;

      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }

  // ── Stash search / filter / sort ───────────────────────────────
  // Lightweight local NL parsing — no data leaves the device. Pulls
  // structured filters (favorite, category, strain type, rating, time
  // window) out of a free-text query, e.g. "find my favorite sativas
  // from the last 30 days", and leaves whatever's left as a plain
  // substring match against name/brand/dispensary/notes/etc.
  const STASH_CAT_SYNONYMS = {
    Flower: ["flower","flowers","bud","buds","nug","nugs"],
    "Pre-Roll": ["pre-roll","pre-rolls","preroll","prerolls","joint","joints"],
    Blunt: ["blunt","blunts"],
    Edible: ["edible","edibles","gummy","gummies"],
    Tincture: ["tincture","tinctures","drops"],
    Concentrate: ["concentrate","concentrates","wax","dab","dabs","shatter","rosin","resin"],
    "Vape Cart": ["vape","vapes","cart","carts","cartridge","cartridges","pen","pens"],
    Recipe: ["recipe","recipes"],
  };
  function parseNaturalStashQuery(query) {
    const out = { favorite:null, current:null, cat:null, strainType:null, minRating:null, sinceDays:null, text:"" };
    let remaining = (query || "").toLowerCase();
    if (!remaining.trim()) return out;

    if (/\bfav(?:e|orite)s?\b/.test(remaining)) { out.favorite = true; remaining = remaining.replace(/\bfav(?:e|orite)s?\b/g,""); }
    if (/\bcurrent(ly)?\b/.test(remaining)) { out.current = true; remaining = remaining.replace(/\bcurrent(ly)?\b/g,""); }
    if (/\b(out|finished|empty)\b/.test(remaining)) { out.current = false; remaining = remaining.replace(/\b(out|finished|empty)\b/g,""); }

    for (const type of ["sativa","indica","hybrid"]) {
      const re = new RegExp(`\\b${type}s?\\b`);
      if (re.test(remaining)) { out.strainType = type[0].toUpperCase()+type.slice(1); remaining = remaining.replace(re,""); }
    }

    for (const [cat, words] of Object.entries(STASH_CAT_SYNONYMS)) {
      for (const w of words) {
        const re = new RegExp(`\\b${w}\\b`);
        if (re.test(remaining)) { out.cat = cat; remaining = remaining.replace(re,""); break; }
      }
      if (out.cat) break;
    }

    const ratingMatch = remaining.match(/\b([1-5])\s*\+?\s*stars?\b/);
    if (ratingMatch) { out.minRating = parseInt(ratingMatch[1],10); remaining = remaining.replace(ratingMatch[0],""); }

    const daysMatch = remaining.match(/\blast\s+(\d+)\s*days?\b/);
    if (daysMatch) { out.sinceDays = parseInt(daysMatch[1],10); remaining = remaining.replace(daysMatch[0],""); }
    else if (/\btoday\b/.test(remaining)) { out.sinceDays = 1; remaining = remaining.replace(/\btoday\b/,""); }
    else if (/\bthis week\b/.test(remaining)) { out.sinceDays = 7; remaining = remaining.replace(/\bthis week\b/,""); }
    else if (/\b(this|last)\s+month\b/.test(remaining)) { out.sinceDays = 30; remaining = remaining.replace(/\b(this|last)\s+month\b/,""); }

    remaining = remaining.replace(/\b(find|show|me|my|all|of|the|search|for|from)\b/g," ").replace(/\s+/g," ").trim();
    out.text = remaining;
    return out;
  }

  function stashMatchesFilters(s, f) {
    if (f.favorite && !s.favorite) return false;
    if (f.current === true && !s.isCurrent) return false;
    if (f.current === false && s.isCurrent) return false;
    if (f.cat && s.cat !== f.cat) return false;
    if (f.strainType && (s.flowerType || "") !== f.strainType) return false;
    if (f.minRating && (s.rating || 0) < f.minRating) return false;
    if (f.sinceDays != null) {
      const createdAt = Number(s.id) || 0;
      if (createdAt < Date.now() - f.sinceDays * 86400000) return false;
    }
    if (f.text) {
      const hay = [s.name, s.manufacturer, s.dispensary, s.comment, s.cat, s.flowerType,
        ...(s.effects||[]), ...(s.flavors||[]), ...(s.terpenes||[]).map(x=>x?.name||x)]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(f.text)) return false;
    }
    return true;
  }

  // Explicit dropdown/chip picks win over whatever the free-text query implied.
  function stashEffectiveFilters() {
    const parsed = parseNaturalStashQuery(stashQuery);
    return {
      ...parsed,
      cat: stashFilterCat || parsed.cat,
      strainType: stashFilterStrain || parsed.strainType,
      sinceDays: stashFilterDays ? parseInt(stashFilterDays,10) : parsed.sinceDays,
    };
  }

  function sortStashItems(items, key) {
    const arr = [...(items || [])];
    if (key === "name")   return arr.sort((a,b)=>(a.name||"").localeCompare(b.name||""));
    if (key === "rating") return arr.sort((a,b)=>(b.rating||0)-(a.rating||0));
    if (key === "recent") return arr.sort((a,b)=>Number(b.id||0)-Number(a.id||0));
    if (key === "price")  return arr.sort((a,b)=>(parseFloat(a.pricePerGram||a.pricePerMl)||Infinity)-(parseFloat(b.pricePerGram||b.pricePerMl)||Infinity));
    return sortStash(arr); // "recommended" — existing current/favorite/reup ordering
  }

  // Add/Edit stash item form. Rendered near the top of the tab for new
  // items, or inline next to the item being edited (see stashFormCard /
  // editItemInlineVisible below) so editing far down the list doesn't
  // strand the form off-screen above.
  function renderStashFormCard() {
    return (
              <Card style={{marginBottom:20,border:`1px solid ${t.purpleMid}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <p style={{margin:0,fontWeight:700,fontSize:15}}>{editSId?"Edit Item":"New Stash Item"}</p>
                  <button onClick={()=>{resetStashForm();setShowStashForm(false);}} title="Close" style={{background:"none",border:"none",fontSize:20,lineHeight:1,color:t.muted,cursor:"pointer",padding:0}}>×</button>
                </div>

                {/* Category pills */}
                {!editSId&&(
                  <div style={{marginBottom:16}}>
                    <label style={lbl}>Category</label>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {STASH_CATS.map(c=>(
                        <button key={c} onClick={()=>{setsCat(c); if(sGenericMode) applyGenericDefaults(c);}} style={{padding:"8px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${sCat===c?catColor(c):t.border}`,background:sCat===c?catBg(c):t.surface,color:sCat===c?catColor(c):t.muted,fontWeight:sCat===c?700:400}}>
                          {STASH_ICONS[c]} {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {sGenericMode&&(
                  <div style={{marginBottom:16,padding:"12px 14px",background:t.sageLight,borderRadius:10,border:`1px solid ${t.sage}33`}}>
                    <p style={{margin:"0 0 4px",fontWeight:700,fontSize:13,color:t.sage}}>🌿 Generic product</p>
                    <p style={{margin:"0 0 8px",fontSize:12,color:t.muted,lineHeight:1.4}}>Pick a category above — we've filled in typical values. Change anything you know.</p>
                    <button onClick={()=>{setSGenericMode(false);setSName("");}} style={{background:"none",border:"none",padding:0,color:t.purple,cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600}}>📷 Have a label? Scan it instead</button>
                  </div>
                )}

                {!sGenericMode&&<>
                {/* Package Photo Scan — primary extraction path; COA below is an optional supplement */}
                <div id="stash-scan-section" style={{marginBottom:16,padding:"12px 14px",background:t.purpleLight,borderRadius:10,border:`1px solid ${t.purple}33`,scrollMarginTop:16}}>
                  <p style={{margin:"0 0 4px",fontWeight:700,fontSize:13,color:t.purple}}>📷 Scan Package</p>
                  <p style={{margin:"0 0 10px",fontSize:11,color:t.muted,lineHeight:1.4}}>Snap the label — front, and back too if cannabinoid info is on a separate panel. Photos are never saved, only the data extracted from them.</p>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                    {scanImages.map((img,i)=>(
                      <div key={i} style={{position:"relative"}}>
                        <img src={img.preview} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:8,border:`1px solid ${t.border}`,display:"block"}}/>
                        <button onClick={()=>removeScanImage(i)} title="Remove" style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:t.red,color:"#fff",border:"none",cursor:"pointer",fontSize:12,lineHeight:1,padding:0}}>×</button>
                      </div>
                    ))}
                    {scanImages.length<MAX_SCAN_IMAGES&&(
                      <label style={{width:64,height:64,borderRadius:8,border:`1.5px dashed ${t.purple}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:t.purple,fontSize:22,flexShrink:0}}>
                        +
                        <input type="file" accept="image/*" capture="environment" onChange={addPackagePhoto} style={{display:"none"}}/>
                      </label>
                    )}
                  </div>
                  {scanError&&<p style={{margin:"0 0 10px",fontSize:11,color:t.red}}>{scanError}</p>}
                  <button
                    onClick={runPackageScan}
                    disabled={scanImages.length===0||scanLoading}
                    style={{width:"100%",padding:"9px 0",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,opacity:scanImages.length===0||scanLoading?0.5:1}}>
                    {scanLoading?"Scanning…":scanImages.length>0?`Scan ${scanImages.length} Photo${scanImages.length===1?"":"s"}`:"Add a photo to scan"}
                  </button>

                  {scanResult&&(
                    <div style={{marginTop:10,background:t.surface,border:`1px solid ${t.purple}44`,borderRadius:12,padding:"14px 16px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div>
                          <p style={{margin:0,fontWeight:700,fontSize:14,color:t.purple}}>{scanResult.strainName||scanResult.brand||"Scanned item"}</p>
                          {scanResult.brand&&<p style={{margin:"1px 0 0",fontSize:11,color:t.text}}>{scanResult.brand}{scanResult.manufacturer&&scanResult.manufacturer!==scanResult.brand?` · ${scanResult.manufacturer}`:""}</p>}
                          {scanResult.productType&&<p style={{margin:"1px 0 0",fontSize:10,color:t.muted}}>{scanResult.productType}{scanResult.flowerType?` · ${scanResult.flowerType}`:""}</p>}
                        </div>
                        <button onClick={()=>setScanResult(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
                      </div>
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8}}>
                        {scanResult.totalThcPct!=null&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THC: </span><strong>{scanResult.totalThcPct}%</strong></span>}
                        {scanResult.totalCannabinoidsPct!=null&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Total Cann: </span><strong>{scanResult.totalCannabinoidsPct}%</strong></span>}
                        {scanResult.totalTerpenesPct!=null&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Terpenes: </span><strong>{scanResult.totalTerpenesPct}%</strong></span>}
                        {scanResult.weightValue!=null&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Weight: </span><strong>{scanResult.weightValue}{scanResult.weightUnit}</strong></span>}
                        {scanResult.vegan&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Vegan</span>}
                        {scanResult.kosher&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Kosher</span>}
                        {scanResult.glutenFree&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Gluten-Free</span>}
                        {scanResult.nonGmo&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Non-GMO</span>}
                        {scanResult.solventType&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.cardBg,color:t.muted,fontWeight:600}}>{scanResult.solventType}</span>}
                      </div>
                      {scanResult.cannabinoids?.length>0&&(
                        <div style={{fontSize:11,color:t.muted,marginBottom:6}}>
                          <span>Cannabinoids: </span>{scanResult.cannabinoids.map(c=>`${c.name} ${c.pct!=null?c.pct+'%':c.mg!=null?c.mg+'mg':''}`).join(' · ')}
                        </div>
                      )}
                      {scanResult.terpenes?.length>0&&(
                        <div style={{fontSize:11,color:t.muted,marginBottom:6}}>
                          <span>Terpenes: </span>{scanResult.terpenes.map(tp=>tp.pct?`${tp.name} ${tp.pct}%`:tp.name).join(' · ')}
                        </div>
                      )}
                      {scanResult.ingredients?.length>0&&<p style={{fontSize:11,color:t.muted,margin:"0 0 6px",lineHeight:1.4}}><span style={{color:t.text}}>Ingredients: </span>{scanResult.ingredients.join(', ')}</p>}
                      {scanResult.infusionDetails&&<p style={{fontSize:11,color:t.muted,margin:"0 0 6px"}}>{scanResult.infusionDetails}</p>}
                      {(scanResult.batchId||scanResult.expirationDate)&&<p style={{fontSize:10,color:t.muted,margin:"0 0 10px"}}>{[scanResult.batchId&&`Batch: ${scanResult.batchId}`,scanResult.expirationDate&&`Exp: ${scanResult.expirationDate}`].filter(Boolean).join(' · ')}</p>}
                      <button onClick={()=>applyPackageScanResult(scanResult)} style={{width:"100%",padding:"9px 0",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                        Apply to Form
                      </button>
                    </div>
                  )}
                </div>

                {/* COA Lookup — optional supplement when a lab link exists */}
                <div style={{marginBottom:16,padding:"12px 14px",background:t.sageLight,borderRadius:10,border:`1px solid ${t.sage}33`}}>
                  <button onClick={()=>setShowCoaSection(v=>!v)} aria-expanded={showCoaSection||!!coaUrl||!!webLookupResult} style={{display:"flex",alignItems:"center",gap:6,width:"100%",background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                    <span style={{fontSize:10,color:t.sage,display:"inline-block",transform:(showCoaSection||!!coaUrl||!!webLookupResult)?"rotate(90deg)":"none",transition:"transform 0.15s"}}>▶</span>
                    <span style={{fontWeight:700,fontSize:13,color:t.sage}}>COA / Strain Lookup (optional)</span>
                  </button>
                  {(showCoaSection||!!coaUrl||!!webLookupResult)&&<div style={{marginTop:10}}>
                  <div style={{marginBottom:10}}>
                    <label style={lbl}>COA Link</label>
                    <input
                      placeholder="Paste COA or PDF link here…"
                      value={coaUrl}
                      onChange={e=>setCoaUrl(e.target.value)}
                      style={inp}
                    />
                    <p style={{margin:"4px 0 0",fontSize:10,color:t.muted,lineHeight:1.4}}>All licensed cannabis products have a QR code linking to the COA (Certificate of Analysis) lab test results. Paste the COA link here. A link to a PDF is best if available.</p>
                  </div>
                  <div style={{marginBottom:10}}>
                    <label style={lbl}>Strain Name</label>
                    <input
                      placeholder="Strain or product name…"
                      value={sName}
                      onChange={e=>{setSName(e.target.value);setKushyQuery(e.target.value);setKushySelected(false);}}
                      style={inp}
                    />
                  </div>
                  <button
                    onClick={coaUrl.trim() ? lookupCOA : lookupStrain}
                    disabled={(!coaUrl.trim()&&!sName.trim())||webLookupLoading}
                    style={{width:"100%",padding:"9px 0",background:t.sage,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,opacity:(!coaUrl.trim()&&!sName.trim())||webLookupLoading?0.5:1}}>
                    {webLookupLoading?"Looking up…":coaUrl.trim()?"Lookup COA":`Search "${sName.trim()||"strain name"}"`}
                  </button>
                      {webLookupResult&&(
                        <div style={{marginTop:10,background:t.sageLight,border:`1px solid ${t.sage}44`,borderRadius:12,padding:"14px 16px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            <div>
                              <p style={{margin:0,fontWeight:700,fontSize:14,color:t.sage}}>{webLookupResult.name||webLookupResult.productName}</p>
                              {webLookupResult.brand&&<p style={{margin:"1px 0 0",fontSize:11,color:t.text}}>{webLookupResult.brand}</p>}
                              {webLookupResult.productName&&webLookupResult.productName!==webLookupResult.name&&<p style={{margin:"1px 0 0",fontSize:10,color:t.muted}}>{webLookupResult.productName}</p>}
                              {webLookupResult.lab&&<p style={{margin:"1px 0 0",fontSize:10,color:t.muted}}>Lab: {webLookupResult.lab}</p>}
                              {webLookupResult.batchId&&<p style={{margin:"1px 0 0",fontSize:10,color:t.muted}}>Batch: {webLookupResult.batchId}{webLookupResult.verified&&<span style={{marginLeft:6,color:t.sage,fontWeight:700}}>✓ verified</span>}</p>}
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{fontSize:10,color:t.muted,background:t.surface,borderRadius:4,padding:"2px 6px"}}>{webLookupResult.source}</span>
                              <button onClick={()=>setWebLookupResult(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
                            </div>
                          </div>
                          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8}}>
                            {(webLookupResult.flowerType||webLookupResult.strainType)&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Type: </span><strong>{webLookupResult.flowerType||webLookupResult.strainType}</strong></span>}
                            {webLookupResult.thcaPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THCa: </span><strong>{webLookupResult.thcaPct}%</strong></span>}
                            {webLookupResult.thcPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THC: </span><strong>{webLookupResult.source==='Leafly'?'~':''}{webLookupResult.thcPct}%</strong></span>}
                            {webLookupResult.cbdPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBD: </span><strong>{webLookupResult.cbdPct}%</strong></span>}
                            {webLookupResult.cbgPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBG: </span><strong>{webLookupResult.cbgPct}%</strong></span>}
                            {webLookupResult.cbnPct&&!webLookupResult.cbnMgPerDose&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBN: </span><strong>{webLookupResult.cbnPct}%</strong></span>}
                            {webLookupResult.cbcPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBC: </span><strong>{webLookupResult.cbcPct}%</strong></span>}
                            {webLookupResult.cbgaPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBGa: </span><strong>{webLookupResult.cbgaPct}%</strong></span>}
                            {webLookupResult.cbdvPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBDV: </span><strong>{webLookupResult.cbdvPct}%</strong></span>}
                            {webLookupResult.totalCannabinoidsPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Total Cann: </span><strong>{webLookupResult.totalCannabinoidsPct}%</strong></span>}
                            {webLookupResult.totalTerpenesPct&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Terpenes: </span><strong>{webLookupResult.totalTerpenesPct}%</strong></span>}
                            {webLookupResult.thcMgPerUnit&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THC/unit: </span><strong>{webLookupResult.thcMgPerUnit}mg</strong></span>}
                            {webLookupResult.thcMgPerDose&&!webLookupResult.thcMgPerUnit&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THC: </span><strong>{webLookupResult.thcMgPerDose}mg/dose</strong></span>}
                            {webLookupResult.thcMgPerDose&&webLookupResult.thcMgPerUnit&&<span style={{fontSize:12}}><span style={{color:t.muted}}>({webLookupResult.thcMgPerDose}mg/dose)</span></span>}
                            {webLookupResult.cbdMgPerDose&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBD: </span><strong>{webLookupResult.cbdMgPerDose}mg</strong></span>}
                            {(webLookupResult.cbnMgPerDose||webLookupResult.cbdvMgPerDose)&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBDn: </span><strong>{webLookupResult.cbnMgPerDose??webLookupResult.cbdvMgPerDose}mg</strong></span>}
                            {webLookupResult.thcvMgPerDose&&<span style={{fontSize:12}}><span style={{color:t.muted}}>THCv: </span><strong>{webLookupResult.thcvMgPerDose}mg</strong></span>}
                            {webLookupResult.cbgaMgPerDose&&<span style={{fontSize:12}}><span style={{color:t.muted}}>CBGa: </span><strong>{webLookupResult.cbgaMgPerDose}mg</strong></span>}
                            {webLookupResult.totalMgPerUnit&&<span style={{fontSize:12}}><span style={{color:t.muted}}>Total/unit: </span><strong>{webLookupResult.totalMgPerUnit}mg</strong></span>}
                            {webLookupResult.liveRosin&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Live Rosin</span>}
                            {webLookupResult.liveResin&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.greenLight,color:t.green,fontWeight:600}}>Live Resin</span>}
                            {webLookupResult.distillate&&!webLookupResult.liveRosin&&!webLookupResult.liveResin&&<span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.cardBg,color:t.muted,fontWeight:600}}>Distillate</span>}
                          </div>
                          {webLookupResult.terpenes?.length>0&&(
                            <div style={{fontSize:11,color:t.muted,marginBottom:10}}>
                              <span>Terpenes: </span>{webLookupResult.terpenes.map(tp=>tp.pct?`${tp.name} ${tp.pct}%`:tp.name).join(' · ')}
                            </div>
                          )}
                          {webLookupResult.description&&<p style={{fontSize:11,color:t.muted,margin:"0 0 10px",lineHeight:1.4}}>{webLookupResult.description}</p>}
                          <div style={{display:"flex",gap:8}}>
                            {webLookupResult.labSampleId&&!webLookupResult.verified&&(
                              <button onClick={()=>confirmCOA(webLookupResult)} disabled={coaConfirming} style={{flex:1,padding:"9px 0",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,opacity:coaConfirming?0.6:1}}>
                                {coaConfirming?"Saving…":"Confirm & Save"}
                              </button>
                            )}
                            <button onClick={()=>applyWebResult(webLookupResult)} style={{flex:1,padding:"9px 0",background:t.sage,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                              Apply to Form
                            </button>
                          </div>

                          {/* COA feedback row */}
                          {coaFeedback==='submitted'?(
                            <p style={{margin:"10px 0 0",fontSize:11,color:t.muted,textAlign:"center"}}>Thanks for the feedback!</p>
                          ):coaFeedback==='down'?(
                            <div style={{marginTop:10}}>
                              <textarea
                                placeholder="What was wrong? (optional)"
                                value={coaFeedbackComment}
                                onChange={e=>setCoaFeedbackComment(e.target.value)}
                                rows={2}
                                style={{width:"100%",boxSizing:"border-box",padding:"7px 10px",fontSize:12,borderRadius:8,border:`1px solid ${t.border}`,fontFamily:"inherit",resize:"none",background:t.surface,color:t.text}}
                              />
                              <div style={{display:"flex",gap:8,marginTop:6}}>
                                <button onClick={()=>submitCoaFeedback('down',coaFeedbackComment)} style={{flex:1,padding:"7px 0",background:"#c0392b",border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600}}>
                                  Submit
                                </button>
                                <button onClick={()=>setCoaFeedback(null)} style={{flex:1,padding:"7px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:8,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ):(
                            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,justifyContent:"center"}}>
                              <span style={{fontSize:11,color:t.muted}}>Results accurate?</span>
                              <button onClick={()=>submitCoaFeedback('up',null)} title="Looks good" style={{background:"none",border:`1px solid ${t.border}`,borderRadius:6,padding:"3px 8px",fontSize:14,cursor:"pointer",opacity:coaFeedback==='up'?1:0.6}}>👍</button>
                              <button onClick={()=>setCoaFeedback('down')} title="Something's wrong" style={{background:"none",border:`1px solid ${t.border}`,borderRadius:6,padding:"3px 8px",fontSize:14,cursor:"pointer",opacity:coaFeedback==='down'?1:0.6}}>👎</button>
                            </div>
                          )}
                        </div>
                      )}
                  </div>}
                </div>
                </>}

                {/* Manual Entry heading */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                  <div style={{flex:1,height:1,background:t.border}}/>
                  <span style={{fontSize:11,fontWeight:700,color:t.muted,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>Manual Entry</span>
                  <div style={{flex:1,height:1,background:t.border}}/>
                </div>

                {/* Flower / Pre-Roll / Blunt - name with Kushy lookup */}
                {(sCat==="Flower"||sCat==="Pre-Roll"||sCat==="Blunt")&&!editSId&&(
                  <div style={{marginBottom:16,position:"relative"}}>
                    <label style={lbl}>Strain Name *</label>
                    <input placeholder="Type to search or enter manually..." value={sName} onChange={e=>{setSName(e.target.value);setKushyQuery(e.target.value);setKushySelected(false);}} style={inp}/>
                    {kushyResults.length>0&&(
                      <div style={{position:"absolute",top:"100%",left:0,right:0,background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,boxShadow:t.shadowMd,zIndex:50,maxHeight:200,overflowY:"auto",marginTop:4}}>
                        {kushyResults.map((k,i)=>(
                          <div key={i} onClick={()=>applyKushyResult(k)} style={{padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.background=t.purpleLight} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <div><span style={{fontSize:13,fontWeight:600}}>{k.name}</span>{(k.strain_type||k.type)&&<span style={{fontSize:11,color:t.muted,marginLeft:8}}>{k.strain_type||k.type}</span>}</div>
                            <div style={{display:"flex",gap:8,fontSize:11,color:t.muted}}>{k.thc>0&&<span>{(k.thc/10).toFixed(1)}%</span>}{k.terpenes&&<span style={{color:t.green}}>terps</span>}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {kushySelected&&<p style={{fontSize:11,color:t.green,marginTop:4}}>Pre-filled from database - edit any field below</p>}
                  </div>
                )}

                {/* Other cats - plain name */}
                {(sCat!=="Flower"&&sCat!=="Pre-Roll"&&sCat!=="Blunt"||editSId)&&(
                  <div style={{marginBottom:12}}>
                    <label style={lbl}>Name *</label>
                    <input placeholder={sCat==="Edible"?"e.g. Kiva Espresso Bites":sCat==="Tincture"?"e.g. Papa & Barkley Relief":sCat==="Concentrate"?"e.g. Live Rosin":"e.g. Stiiizy Pod"} value={sName} onChange={e=>setSName(e.target.value)} style={inp}/>
                  </div>
                )}

                  {/* Manufacturer — hidden for Recipe */}
                  {sCat!=="Recipe"&&(
                  <div style={{marginBottom:12}}>
                    <label style={lbl}>Manufacturer / Brand</label>
                    <input id="stash-mfr-input" placeholder="Brand or grower name" value={sMfr} onChange={e=>setSMfr(e.target.value)} style={inp}/>
                  </div>
                  )}

                  {/* Hemp toggle — hidden for Recipe */}
                  {sCat!=="Recipe"&&(
                  <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                    <button
                      type="button"
                      onClick={()=>setSIsHemp(p=>!p)}
                      style={{padding:"6px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700,border:`1px solid ${sIsHemp?"#15803d":t.border}`,background:sIsHemp?"#dcfce7":t.surface,color:sIsHemp?"#15803d":t.muted}}
                    >{sIsHemp?"🌾 Hemp":"🌿 Licensed Cannabis"}</button>
                    <span style={{fontSize:11,color:t.muted}}>{sIsHemp?"Hemp-derived · federally legal":"State-licensed dispensary product"}</span>
                  </div>
                  )}

                  {/* Source — always shown, label varies */}
                  <div style={{marginBottom:12}}>
                    <label style={lbl}>{sCat==="Recipe"?"Source (optional)":sIsHemp?"Retailer / Source":"Dispensary / Source"}</label>
                    <input
                      placeholder={sCat==="Recipe"?"e.g. From a friend, family recipe…":sIsHemp?"Where did you get it? (store, website…)":"Where did you get it?"}
                      value={sDispensary}
                      onChange={e=>setSDispensary(e.target.value)}
                      list="dispensary-suggestions"
                      style={inp}
                    />
                    <datalist id="dispensary-suggestions">
                      {[...new Set(stash.map(s=>s.dispensary).filter(Boolean))].map(d=><option key={d} value={d}/>)}
                    </datalist>
                  </div>

                  {/* Product link — hidden for Recipe (has its own recipe link) */}
                  {sCat!=="Recipe"&&(
                  <div style={{marginBottom:12}}>
                    <label style={lbl}>Product Link (optional)</label>
                    <input
                      placeholder="Link to product page…"
                      value={sRetailUrl}
                      onChange={e=>setSRetailUrl(e.target.value)}
                      style={inp}
                    />
                    <p style={{margin:"4px 0 0",fontSize:10,color:t.muted}}>Save the product page URL so you can find it again.</p>
                  </div>
                  )}

                {/* Universal weight + price fields — not for Edibles, Vape Cart, Pre-Rolls, Blunts, or Recipes */}
                {sCat!=="Edible"&&sCat!=="Vape Cart"&&sCat!=="Pre-Roll"&&sCat!=="Blunt"&&sCat!=="Recipe"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                    <div>
                      <label style={lbl}>{sCat==="Tincture"?"Volume (ml)":"Weight (g)"}</label>
                      <input type="number" min="0" step={sCat==="Tincture"?"0.5":"0.01"} placeholder={sCat==="Tincture"?"30":"3.5"} value={sWeight} onChange={e=>setSWeight(e.target.value)} style={inp}/>
                    </div>
                    <div><label style={lbl}>Total Price ($)</label><input type="number" min="0" step="0.01" placeholder="45.00" value={sTotalPrice} onChange={e=>setSTotalPrice(e.target.value)} style={inp}/></div>
                  </div>
                )}
                {sCat!=="Edible"&&sCat!=="Vape Cart"&&sCat!=="Pre-Roll"&&sCat!=="Blunt"&&sCat!=="Recipe"&&sWeight && sTotalPrice && parseFloat(sWeight)>0 && parseFloat(sTotalPrice)>0 && (
                  <div style={{marginBottom:12,padding:"8px 12px",background:t.greenLight,borderRadius:8,fontSize:13,color:t.green,fontWeight:600}}>
                    ${(parseFloat(sTotalPrice)/parseFloat(sWeight)).toFixed(2)}/{sCat==="Tincture"?"ml":"g"}
                  </div>
                )}

                {/* Flower specific */}
                {sCat==="Flower"&&(
                  <div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Type</label>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                        {STRAIN_TYPES.map(st=><button key={st} onClick={()=>setSFlowerType(st)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sFlowerType===st?STRAIN_COLORS[st]:t.border}`,background:sFlowerType===st?STRAIN_COLORS[st]+"22":t.surface,color:sFlowerType===st?STRAIN_COLORS[st]:t.muted,fontWeight:sFlowerType===st?700:400}}>{st}</button>)}
                      </div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Grow</label>
                      <div style={{display:"flex",gap:8}}>
                        {GROW_TYPES.map(g=>{
                          const active=sGrowType===g;
                          const col=g==="Indoor"?t.purple:g==="Outdoor"?t.green:t.muted;
                          return <button key={g} onClick={()=>setSGrowType(g)} style={{padding:"7px 16px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${active?col:t.border}`,background:active?col+"22":t.surface,color:active?col:t.muted,fontWeight:active?700:400}}>{g==="Indoor"?"🏠 Indoor":g==="Outdoor"?"🌳 Outdoor":"🤷 Unknown"}</button>;
                        })}
                      </div>
                    </div>
                    {/* Cannabinoids — easy mode first */}
                      <div style={{marginBottom:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <label style={{...lbl,marginBottom:0}}>Total THC %</label>
                          <button onClick={()=>setSGeekMode(g=>!g)} style={{background:"none",border:"none",color:t.sage,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>
                            {sGeekMode?"− Simple":"+ Geek Mode (COA)"}
                          </button>
                        </div>
                        {!sGeekMode?(
                          <input type="number" min="0" max="100" step="0.1" placeholder="e.g. 22.5" value={sPot} onChange={e=>setSPot(e.target.value)} style={inp}/>
                        ):(
                          <div>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                              <div>
                                <label style={{...lbl,fontSize:10,color:t.muted}}>THCA %</label>
                                <input type="number" min="0" max="100" step="0.1" placeholder="26.5" value={sThca} onChange={e=>{setSThca(e.target.value);setSPot(((parseFloat(e.target.value||0)*0.877)+(parseFloat(sD9||0))).toFixed(1));}} style={inp}/>
                              </div>
                              <div>
                                <label style={{...lbl,fontSize:10,color:t.muted}}>Delta-9 THC %</label>
                                <input type="number" min="0" max="100" step="0.1" placeholder="0.3" value={sD9} onChange={e=>{setSD9(e.target.value);setSPot(((parseFloat(sThca||0)*0.877)+(parseFloat(e.target.value)||0)).toFixed(1));}} style={inp}/>
                              </div>
                              <div>
                                <label style={{...lbl,fontSize:10,color:t.muted}}>CBD %</label>
                                <input type="number" min="0" max="100" step="0.1" placeholder="0.1" value={sCbd} onChange={e=>setSCbd(e.target.value)} style={inp}/>
                              </div>
                              <div>
                                <label style={{...lbl,fontSize:10,color:t.muted}}>Total Cannabinoids %</label>
                                <input type="number" min="0" max="100" step="0.1" placeholder="31.0" value={sTotalCannabinoid} onChange={e=>setSTotalCannabinoid(e.target.value)} style={inp}/>
                              </div>
                            </div>
                            {sPot&&<div style={{padding:"6px 12px",background:t.sageLight,borderRadius:8,fontSize:12,color:t.sage,fontWeight:600,marginBottom:4}}>Total THC: {sPot}% (auto-calculated)</div>}
                            <p style={{fontSize:10,color:t.muted,margin:0}}>Enter THCA + D9 from your COA. Total THC = (THCA × 0.877) + D9.</p>
                          </div>
                        )}
                      </div>
                    {/* Terpenes */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Terpenes</label>
                      {/* Total terpene % */}
                      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                        <div style={{flex:1}}>
                          <label style={{...lbl,fontSize:10,color:t.muted}}>Total Terpenes % (from label)</label>
                          <input type="number" min="0" max="100" step="0.1" placeholder="2.4" value={sTotalTerpene} onChange={e=>setSTotalTerpene(e.target.value)} style={inp}/>
                        </div>
                      </div>
                      {/* Dominant terpenes */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <label style={{...lbl,fontSize:10,color:t.muted,marginBottom:0}}>Dominant Terpenes (optional)</label>
                        <button onClick={()=>setSTerps(prev=>[...prev,{name:"",pct:""}])} style={{background:"none",border:"none",color:t.sage,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>+ Add</button>
                      </div>
                      <p style={{fontSize:10,color:t.muted,margin:"0 0 6px"}}>List the notable ones only — no need to account for every trace terpene.</p>
                      {sTerps.map((terp,i)=>(
                        <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                          <input
                            placeholder="e.g. Beta-Caryophyllene"
                            value={terp.name}
                            onChange={e=>setSTerps(prev=>prev.map((x,j)=>j===i?{...x,name:e.target.value}:x))}
                            list="terpene-suggestions"
                            style={{...inp,flex:2}}
                          />
                          <input type="number" placeholder="%" min="0" step="0.01" value={terp.pct} onChange={e=>setSTerps(prev=>prev.map((x,j)=>j===i?{...x,pct:e.target.value}:x))} style={{...inp,flex:1}}/>
                          <button onClick={()=>setSTerps(prev=>prev.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:16,flexShrink:0}}>×</button>
                        </div>
                      ))}
                      <datalist id="terpene-suggestions">
                        <option value="Beta-Caryophyllene"/>
                        <option value="Myrcene"/>
                        <option value="Limonene"/>
                        <option value="Linalool"/>
                        <option value="Alpha-Pinene"/>
                        <option value="Beta-Pinene"/>
                        <option value="Terpinolene"/>
                        <option value="Ocimene"/>
                        <option value="Humulene"/>
                        <option value="Bisabolol"/>
                        <option value="Valencene"/>
                        <option value="Geraniol"/>
                        <option value="Camphene"/>
                        <option value="Borneol"/>
                        <option value="Delta-3-Carene"/>
                        <option value="Eucalyptol"/>
                        <option value="Guaiol"/>
                        <option value="Nerolidol"/>
                        <option value="Phytol"/>
                      </datalist>
                    </div>
                  </div>
                )}

                {/* Edible specific */}
                {sCat==="Edible"&&(
                  <>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Type</label>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {EDIBLE_TYPES.map(et=><button key={et} onClick={()=>setSEdibleType(et)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sEdibleType===et?t.amber:t.border}`,background:sEdibleType===et?"#fef3c7":t.surface,color:sEdibleType===et?t.amber:t.muted,fontWeight:sEdibleType===et?700:400}}>{et}</button>)}
                      </div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Qty (pieces{sEdibleType==="Bev"?" / bottles":""})</label>
                      <input type="number" min="1" step="1" placeholder="10" value={sEdibleQty} onChange={e=>setSEdibleQty(e.target.value)} style={inp}/>
                    </div>
                    {/* Oz/ml for beverages */}
                    {sEdibleType==="Bev"&&(
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                        <div><label style={lbl}>Volume per bottle (oz)</label><input type="number" min="0" step="0.5" placeholder="12" value={sBevOz} onChange={e=>setSBevOz(e.target.value)} style={inp}/></div>
                        <div><label style={lbl}>Volume per bottle (ml)</label><input type="number" min="0" step="1" placeholder="355" value={sBevMl} onChange={e=>setSBevMl(e.target.value)} style={inp}/></div>
                      </div>
                    )}
                    {/* Live Resin / Live Rosin */}
                    <div style={{display:"flex",gap:12,marginBottom:12}}>
                      {[["Live Resin",sLiveResin,setSLiveResin],["Live Rosin",sLiveRosin,setSLiveRosin]].map(([label,val,setter])=>(
                        <button key={label} onClick={()=>setter(!val)} style={{padding:"8px 16px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${val?t.green:t.border}`,background:val?t.greenLight:t.surface,color:val?t.green:t.muted,fontWeight:val?700:400,display:"flex",alignItems:"center",gap:6}}>
                          {val?"✓":""} {label}
                        </button>
                      ))}
                    </div>
                    {/* Distillant — hidden for live rosin (solventless) */}
                    {!sLiveRosin&&(
                      <div style={{marginBottom:12}}>
                        <label style={lbl}>Distillant</label>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {["BHO","CO2","Ethanol","Other"].map(s=>(
                            <button key={s} onClick={()=>setSEdibleSolvent(sEdibleSolvent===s?"":s)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sEdibleSolvent===s?t.amber:t.border}`,background:sEdibleSolvent===s?"#fef3c7":t.surface,color:sEdibleSolvent===s?t.amber:t.muted,fontWeight:sEdibleSolvent===s?700:400}}>{s}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Total price for package */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                      <div><label style={lbl}>Total Price ($)</label><input type="number" min="0" step="0.01" placeholder="20.00" value={sTotalPrice} onChange={e=>setSTotalPrice(e.target.value)} style={inp}/></div>
                      {sEdibleQty && sTotalPrice && parseFloat(sEdibleQty)>0 && parseFloat(sTotalPrice)>0 && (
                        <div style={{display:"flex",alignItems:"flex-end",paddingBottom:8}}>
                          <span style={{fontSize:13,color:t.green,fontWeight:600}}>${(parseFloat(sTotalPrice)/parseFloat(sEdibleQty)).toFixed(2)}/piece</span>
                        </div>
                      )}
                    </div>
                    <CannabFields values={sStrength} onChange={setSStrength} label="Strength per piece (mg)"/>
                  </>
                )}

                {/* Tincture specific */}
                {sCat==="Tincture"&&(
                  <CannabFields values={sStrength} onChange={setSStrength} label="Strength per ml (mg)"/>
                )}

                {/* Pre-Roll / Blunt specific */}
                {(sCat==="Pre-Roll"||sCat==="Blunt")&&(
                  <>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Type</label>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                        {STRAIN_TYPES.map(st=><button key={st} onClick={()=>setSFlowerType(st)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sFlowerType===st?STRAIN_COLORS[st]:t.border}`,background:sFlowerType===st?STRAIN_COLORS[st]+"22":t.surface,color:sFlowerType===st?STRAIN_COLORS[st]:t.muted,fontWeight:sFlowerType===st?700:400}}>{st}</button>)}
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                      <div>
                        <label style={lbl}>{sCat==="Blunt"?"Blunts":"Joints"} in Pack</label>
                        <Stepper value={sPrPackQty} onChange={setSPrPackQty} min={1}/>
                      </div>
                      <div>
                        <label style={lbl}>Weight per {sCat==="Blunt"?"Blunt":"Joint"} (g)</label>
                        <div style={{display:"flex",gap:6}}>
                          {["0.5","0.75","1","1.5"].map(w=><button key={w} onClick={()=>setSPrJointWt(w)} style={{flex:1,padding:"8px 2px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:11,border:`1px solid ${sPrJointWt===w?t.clay:t.border}`,background:sPrJointWt===w?t.clayLight:t.surface,color:sPrJointWt===w?t.clay:t.muted,fontWeight:sPrJointWt===w?700:400}}>{w}g</button>)}
                        </div>
                      </div>
                    </div>
                    {sPrPackQty&&sPrJointWt&&<div style={{marginBottom:12,padding:"8px 12px",background:t.sageLight,borderRadius:8,fontSize:13,color:t.sage,fontWeight:600}}>{sPrPackQty} {sCat==="Blunt"?"blunts":"joints"} × {sPrJointWt}g = {(sPrPackQty*parseFloat(sPrJointWt)).toFixed(2)}g total</div>}
                    {/* Cannabinoids — easy mode first */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Total THC %</label>
                      <input type="number" min="0" max="100" step="0.1" placeholder="e.g. 22.5" value={sPot} onChange={e=>setSPot(e.target.value)} style={inp}/>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>CBD %</label>
                      <input type="number" min="0" max="100" step="0.1" placeholder="0.1" value={sCbd} onChange={e=>setSCbd(e.target.value)} style={inp}/>
                    </div>
                    {/* Terpenes */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Total Terpenes %</label>
                      <input type="number" min="0" max="30" step="0.1" placeholder="2.4" value={sTotalTerpene} onChange={e=>setSTotalTerpene(e.target.value)} style={inp}/>
                    </div>
                    {/* Additives in the joint */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Infused With (optional)</label>
                      <p style={{fontSize:11,color:t.muted,margin:"0 0 8px"}}>What's rolled into these joints?</p>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                        {STASH_ADDITIVES.map(a=>(
                          <button key={a} onClick={()=>setSPrAdditives(prev=>prev.includes(a)?prev.filter(x=>x!==a):[...prev,a])} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sPrAdditives.includes(a)?t.amber:t.border}`,background:sPrAdditives.includes(a)?"#fef3c7":t.surface,color:sPrAdditives.includes(a)?t.amber:t.muted,fontWeight:sPrAdditives.includes(a)?700:400}}>{a}</button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Concentrate specific */}
                {sCat==="Concentrate"&&(
                  <>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Type</label>
                      <div style={{display:"flex",gap:8}}>
                        {CONCENTRATE_TYPES.map(ct=><button key={ct} onClick={()=>setSConType(ct)} style={{flex:1,padding:"8px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${sConType===ct?t.red:t.border}`,background:sConType===ct?t.redLight:t.surface,color:sConType===ct?t.red:t.muted,fontWeight:sConType===ct?700:400}}>{ct}</button>)}
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
                      <div><label style={lbl}>Strain</label><input placeholder="Optional" value={sConStrain} onChange={e=>setSConStrain(e.target.value)} style={inp}/></div>
                      <div><label style={lbl}>Potency %</label><input type="number" min="0" max="100" step="0.1" placeholder="70" value={sConPot} onChange={e=>setSConPot(e.target.value)} style={inp}/></div>
                      <div><label style={lbl}>Weight (g)</label><input type="number" min="0" step="0.01" placeholder="1.0" value={sConWeight} onChange={e=>setSConWeight(e.target.value)} style={inp}/></div>
                    </div>
                  </>
                )}

                {/* Vape Cart specific */}
                {sCat==="Vape Cart"&&(
                  <>
                    {/* Hardware type */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Hardware</label>
                      <div style={{display:"flex",gap:8}}>
                        {["Cartridge","Disposable"].map(h=>(
                          <button key={h} onClick={()=>setSVapeHardware(h)} style={{flex:1,padding:"8px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${sVapeHardware===h?t.purple:t.border}`,background:sVapeHardware===h?t.purpleLight:t.surface,color:sVapeHardware===h?t.purple:t.muted,fontWeight:sVapeHardware===h?700:400}}>{h}</button>
                        ))}
                      </div>
                    </div>
                    {/* Size + Strain (single-barrel) / Multi-Barrel toggle */}
                    <div style={{display:"grid",gridTemplateColumns:sVapeMultiBarrel?"1fr":"1fr 1fr",gap:10,marginBottom:12}}>
                      <div>
                        <label style={lbl}>Size</label>
                        <select value={sVapeSize} onChange={e=>setSVapeSize(e.target.value)} style={{...inp,cursor:"pointer"}}>
                          <option>0.5g</option><option>1g</option><option>2g</option>
                        </select>
                      </div>
                      {!sVapeMultiBarrel&&<div><label style={lbl}>Strain</label><input placeholder="Optional" value={sVapeStrain} onChange={e=>setSVapeStrain(e.target.value)} style={inp}/></div>}
                    </div>

                    <div style={{marginBottom:12}}>
                      <button
                        onClick={()=>{
                          const next=!sVapeMultiBarrel;
                          setSVapeMultiBarrel(next);
                          if(next&&sVapeBarrels.length===0) setSVapeBarrels([{strain:sVapeStrain||"",potency:sPot||""},{strain:"",potency:""}]);
                        }}
                        style={{padding:"8px 16px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${sVapeMultiBarrel?t.purple:t.border}`,background:sVapeMultiBarrel?t.purpleLight:t.surface,color:sVapeMultiBarrel?t.purple:t.muted,fontWeight:sVapeMultiBarrel?700:400,display:"flex",alignItems:"center",gap:6}}
                      >{sVapeMultiBarrel?"✓":""} Multi-Barrel (Switch)</button>
                    </div>

                    {sVapeMultiBarrel&&(
                      <div style={{marginBottom:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <label style={{...lbl,marginBottom:0}}>Barrels</label>
                          <button onClick={()=>setSVapeBarrels(prev=>[...prev,{strain:"",potency:""}])} style={{background:"none",border:"none",color:t.purple,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>+ Add Barrel</button>
                        </div>
                        {sVapeBarrels.map((b,i)=>(
                          <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                            <span style={{fontSize:11,color:t.muted,width:14,flexShrink:0}}>{i+1}</span>
                            <input placeholder="Strain / flavor" value={b.strain} onChange={e=>setSVapeBarrels(prev=>prev.map((x,j)=>j===i?{...x,strain:e.target.value}:x))} style={{...inp,flex:2}}/>
                            <input type="number" placeholder="THC %" min="0" max="100" step="0.1" value={b.potency} onChange={e=>setSVapeBarrels(prev=>prev.map((x,j)=>j===i?{...x,potency:e.target.value}:x))} style={{...inp,flex:1}}/>
                            <button onClick={()=>setSVapeBarrels(prev=>prev.filter((_,j)=>j!==i))} disabled={sVapeBarrels.length<=1} style={{background:"none",border:"none",color:t.muted,cursor:sVapeBarrels.length<=1?"default":"pointer",fontSize:16,flexShrink:0,opacity:sVapeBarrels.length<=1?0.3:1}}>×</button>
                          </div>
                        ))}
                        <p style={{fontSize:10,color:t.muted,margin:"4px 0 0"}}>One device, multiple independently-filled chambers — e.g. a Boutiq Switch. Leave THC % blank if only printed as a device total below.</p>
                      </div>
                    )}

                    {/* Solvent type */}
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Solvent Type</label>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {["CO2","Distillate","Hydrocarbon","Solventless"].map(s=>(
                          <button key={s} onClick={()=>setSVapeSolvent(sVapeSolvent===s?"":s)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sVapeSolvent===s?t.purple:t.border}`,background:sVapeSolvent===s?t.purpleLight:t.surface,color:sVapeSolvent===s?t.purple:t.muted,fontWeight:sVapeSolvent===s?700:400}}>{s}</button>
                        ))}
                      </div>
                    </div>

                    {/* Live Resin / Live Rosin */}
                    <div style={{display:"flex",gap:12,marginBottom:12}}>
                      {[["Live Resin",sVapeLiveResin,setSVapeLiveResin],["Live Rosin",sVapeLiveRosin,setSVapeLiveRosin]].map(([label,val,setter])=>(
                        <button key={label} onClick={()=>setter(!val)} style={{padding:"8px 16px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${val?t.green:t.border}`,background:val?t.greenLight:t.surface,color:val?t.green:t.muted,fontWeight:val?700:400,display:"flex",alignItems:"center",gap:6}}>
                          {val?"✓":""} {label}
                        </button>
                      ))}
                    </div>

                    {/* Cannabinoids — easy mode first */}
                    <div style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <label style={{...lbl,marginBottom:0}}>Total THC %</label>
                        <button onClick={()=>setSGeekMode(g=>!g)} style={{background:"none",border:"none",color:t.sage,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>
                          {sGeekMode?"− Simple":"+ Geek Mode (COA)"}
                        </button>
                      </div>
                      {!sGeekMode?(
                        <input type="number" min="0" max="100" step="0.1" placeholder="e.g. 22.5" value={sPot} onChange={e=>setSPot(e.target.value)} style={inp}/>
                      ):(
                        <div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                            <div>
                              <label style={{...lbl,fontSize:10,color:t.muted}}>THCA %</label>
                              <input type="number" min="0" max="100" step="0.1" placeholder="26.5" value={sThca} onChange={e=>{setSThca(e.target.value);setSPot(((parseFloat(e.target.value||0)*0.877)+(parseFloat(sD9||0))).toFixed(1));}} style={inp}/>
                            </div>
                            <div>
                              <label style={{...lbl,fontSize:10,color:t.muted}}>Delta-9 THC %</label>
                              <input type="number" min="0" max="100" step="0.1" placeholder="0.3" value={sD9} onChange={e=>{setSD9(e.target.value);setSPot(((parseFloat(sThca||0)*0.877)+(parseFloat(e.target.value)||0)).toFixed(1));}} style={inp}/>
                            </div>
                            <div>
                              <label style={{...lbl,fontSize:10,color:t.muted}}>CBD %</label>
                              <input type="number" min="0" max="100" step="0.1" placeholder="0.1" value={sCbd} onChange={e=>setSCbd(e.target.value)} style={inp}/>
                            </div>
                            <div>
                              <label style={{...lbl,fontSize:10,color:t.muted}}>Total Cannabinoids %</label>
                              <input type="number" min="0" max="100" step="0.1" placeholder="31.0" value={sTotalCannabinoid} onChange={e=>setSTotalCannabinoid(e.target.value)} style={inp}/>
                            </div>
                          </div>
                          {sPot&&<div style={{padding:"6px 12px",background:t.sageLight,borderRadius:8,fontSize:12,color:t.sage,fontWeight:600,marginBottom:4}}>Total THC: {sPot}% (auto-calculated)</div>}
                          <p style={{fontSize:10,color:t.muted,margin:0}}>Enter THCA + D9 from your COA. Total THC = (THCA × 0.877) + D9.</p>
                        </div>
                      )}

                    {/* Minor cannabinoids % — CBG, CBN, CBC, THCv from COA */}
                    <CannabFields values={sStrength} onChange={setSStrength} label="Minor Cannabinoids (%)"/>

                    {/* Terpenes */}
                    <div style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                        <label style={{...lbl,marginBottom:0}}>Terpenes</label>
                        <button onClick={()=>setSVapeTerps(prev=>[...prev,{name:"",pct:""}])} style={{background:"none",border:"none",color:t.purple,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>+ Add</button>
                      </div>
                      {sVapeTerps.map((terp,i)=>(
                        <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                          <input placeholder="Myrcene" value={terp.name} onChange={e=>setSVapeTerps(prev=>prev.map((x,j)=>j===i?{...x,name:e.target.value}:x))} style={{...inp,flex:2}}/>
                          <input type="number" placeholder="%" min="0" step="0.01" value={terp.pct} onChange={e=>setSVapeTerps(prev=>prev.map((x,j)=>j===i?{...x,pct:e.target.value}:x))} style={{...inp,flex:1}}/>
                          <button onClick={()=>setSVapeTerps(prev=>prev.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:16,flexShrink:0}}>×</button>
                        </div>
                      ))}
                    </div>
                   </div>
                 </>
               )}
                {sCat==="Recipe"&&(
                  <>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Type</label>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                        {["Baked","Candy","Sauce","Oil","Butter","Main","Appetizer"].map(type=>(
                          <button key={type} onClick={()=>setSRecipeType(sRecipeType===type?"":type)} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${sRecipeType===type?t.amber:t.border}`,background:sRecipeType===type?"#fef3c7":t.surface,color:sRecipeType===type?t.amber:t.muted,fontWeight:sRecipeType===type?700:400}}>{type}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Recipe Link (optional)</label>
                      <input placeholder="https://... link to the recipe" value={sRecipeUrl} onChange={e=>setSRecipeUrl(e.target.value)} style={inp}/>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                      <div>
                        <label style={lbl}>Dose per serving</label>
                        <input placeholder="e.g. 10mg THC" value={sRecipeDose} onChange={e=>setSRecipeDose(e.target.value)} style={inp}/>
                      </div>
                      <div>
                        <label style={lbl}>Servings</label>
                        <input type="number" min="1" step="1" placeholder="e.g. 12" value={sRecipeServings} onChange={e=>setSRecipeServings(e.target.value)} style={inp}/>
                      </div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Infused ingredient (optional)</label>
                      <p style={{fontSize:11,color:t.muted,margin:"0 0 6px"}}>Link to the tincture, butter, or oil from your stash used in this recipe.</p>
                      {sRecipeStashId?(
                        <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:8}}>
                          <span style={{fontSize:12,color:t.purple,fontWeight:600,flex:1}}>{stash.find(x=>x.id===sRecipeStashId)?.name||"Unknown"}</span>
                          <button onClick={()=>setSRecipeStashId(null)} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:16,padding:0}}>×</button>
                        </div>
                      ):(
                        <select onChange={e=>setSRecipeStashId(e.target.value||null)} value="" style={{...inp,cursor:"pointer"}}>
                          <option value="">Select stash item…</option>
                          {stash.filter(s=>["Tincture","Concentrate","Flower","Edible"].includes(s.cat)).map(s=>(
                            <option key={s.id} value={s.id}>{s.name} ({s.cat})</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={lbl}>Recipe (optional)</label>
                      <textarea placeholder="Paste or type your recipe here…" value={sRecipeText} onChange={e=>setSRecipeText(e.target.value)} rows={6} style={{...inp,resize:"vertical",fontFamily:"inherit",fontSize:13}}/>
                    </div>
                  </>
                )}

                <div style={{marginBottom:16}}>
                  <label style={lbl}>Rating</label>
                  <Stars value={sRating} onChange={setSRating}/>
                </div>

                <div style={{marginBottom:16}}>
                  <label style={lbl}>Notes</label>
                  <textarea placeholder="Any notes..." value={sComment} onChange={e=>setSComment(e.target.value)} rows={3} style={{...inp,resize:"vertical",fontFamily:"inherit"}}/>
                </div>

                {editSId ? (
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={saveStash} style={{flex:1,padding:"10px 0",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>Save</button>
                    <button onClick={()=>{resetStashForm();setShowStashForm(false);}} style={{flex:1,padding:"10px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:8,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:14}}>Cancel</button>
                  </div>
                ) : (
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{resetStashForm();setShowStashForm(false);}} style={{flex:1,padding:"10px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:8,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>Cancel</button>
                    <button onClick={()=>{setPartakeAfterSave(false);saveStash();}} style={{flex:1,padding:"10px 0",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>Save</button>
                    <button onClick={()=>{setPartakeAfterSave(true);saveStash();}} style={{flex:1,padding:"10px 0",background:t.sage,border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>🌿 Partake</button>
                  </div>
                )}
              </Card>
    );
  }

  function catColor(cat) { return {Flower:t.green,"Edible":t.amber,Tincture:t.purple,Concentrate:t.red,"Vape Cart":t.purple,"Pre-Roll":t.clay,Blunt:t.clay}[cat]||t.muted; }
  function catBg(cat) { return {Flower:t.greenLight,"Edible":"#fef3c7",Tincture:t.purpleLight,Concentrate:t.redLight,"Vape Cart":t.purpleLight,"Pre-Roll":t.clayLight,Blunt:t.clayLight}[cat]||t.bg; }

  // ── Home tab pre-computed vars ─────────────────────────────────
  const homeTZ = prefs.timezone;
  const homeNow = new Date();
  const homeTodayIntake = intake.filter(e=>{ const d=new Date(e.ts); return d.getFullYear()===homeNow.getFullYear()&&d.getMonth()===homeNow.getMonth()&&d.getDate()===homeNow.getDate(); });

  // Daily stats
  const homeTodaySessions = homeTodayIntake.length;
  const homeTodayAbsorption = homeTodayIntake.reduce((s,e)=>s+(Number(e.thcLo)||0),0);
  const homeLastIntake = intake.length>0 ? intake.slice().sort((a,b)=>new Date(b.ts)-new Date(a.ts))[0] : null;
  const homeLastAgo = homeLastIntake ? (()=>{
    const mins = Math.floor((Date.now()-new Date(homeLastIntake.ts).getTime())/60000);
    if(mins<60) return `${mins}m ago`;
    if(mins<1440) return `${Math.floor(mins/60)}h ago`;
    return `${Math.floor(mins/1440)}d ago`;
  })() : null;
  // Streak — consecutive days with at least one intake
  const homeStreak = (()=>{
    if(intake.length===0) return 0;
    const days = new Set(intake.map(e=>new Date(e.ts).toDateString()));
    let streak=0, d=new Date();
    while(true){ if(!days.has(d.toDateString())) break; streak++; d.setDate(d.getDate()-1); }
    return streak;
  })();

  // ARCHIVED: Blood curve / THC meter — restore when real data available
  // const homeBloodCurve = buildBloodCurve(homeTodayIntake);
  // const homeNowMin = homeNow.getHours()*60+homeNow.getMinutes();
  // const homeCurTHC = homeBloodCurve[homeNowMin]||0;

  const cutoff7=Date.now()-7*86400000;
  const homeRecentItems=[...checkins,...intake].filter(e=>new Date(e.ts).getTime()>cutoff7);
  const homeTagCounts={};
  homeRecentItems.forEach(e=>{
    (e.feelings||[]).forEach(f=>{if(!homeTagCounts[f])homeTagCounts[f]={count:0,type:"feeling"};homeTagCounts[f].count++;});
    (e.activities||[]).forEach(a=>{if(!homeTagCounts[a])homeTagCounts[a]={count:0,type:"activity"};homeTagCounts[a].count++;});
  });
  const homeTags=Object.entries(homeTagCounts).sort((a,b)=>b[1].count-a[1].count).slice(0,20);
  const homeMaxTag=homeTags.length>0?homeTags[0][1].count:1;
  const homeFeed=[...checkins.map(c=>({...c,_type:"checkin"})),...intake.map(e=>({...e,_type:"intake"}))].sort((a,b)=>new Date(b.ts)-new Date(a.ts)).slice(0,8);
  // ── end home vars ──────────────────────────────────────────────

  // ── Stash tab pre-computed vars ──────────────────────────────────
  const stashEffFilters = stashEffectiveFilters();
  const stashCurrentList = sortStashItems(stash.filter(s=>s.isCurrent).filter(s=>stashMatchesFilters(s,stashEffFilters)), stashSortKey);
  const stashLibraryList = sortStashItems(stash.filter(s=>!s.isCurrent).filter(s=>stashMatchesFilters(s,stashEffFilters)), stashSortKey);
  const editingStashItem = editSId ? stash.find(s=>s.id===editSId) : null;
  const editItemInlineVisible = !!(editingStashItem && (
    (editingStashItem.isCurrent && stashView==="stash" && stashCurrentList.some(s=>s.id===editSId)) ||
    (!editingStashItem.isCurrent && stashView==="library" && stashLibraryList.some(s=>s.id===editSId))
  ));
  const stashFormCard = showStashForm ? renderStashFormCard() : null;
  // ── end stash vars ─────────────────────────────────────────────

  return (
    <div style={{minHeight:"100dvh",width:"100%",maxWidth:"min(100%, 520px)",background:t.bg,color:t.text,fontFamily:"-apple-system,'Segoe UI',sans-serif",display:"flex",flexDirection:"column",margin:"0 auto",overflowX:"hidden",position:"relative"}}>

      <header style={{background:t.surface,borderBottom:`1px solid ${t.border}`,boxShadow:t.shadow,position:"sticky",top:0,zIndex:40}}>
        <div style={{padding:"14px 20px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"baseline",gap:8}}>
            <button onClick={()=>switchTab("home")} style={{fontSize:19,fontWeight:700,color:t.clay,letterSpacing:"-0.5px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0}}>CannaTrack</button>
            <span style={{fontSize:12,color:t.muted}}>day {days}</span>
          </div>
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowSignOut(v=>!v)} style={{fontSize:12,color:t.muted,textAlign:"right",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0}}>
              {prefs.displayName
                ? prefs.displayName
                : user?.userDetails
                  ? user.userDetails.split("@")[0]
                  : null}
            </button>
            {showSignOut && (
              <>
                <div onClick={()=>setShowSignOut(false)} style={{position:"fixed",inset:0,zIndex:49}} />
                <button onClick={()=>{setShowSignOut(false);performLogout();}} style={{position:"absolute",top:"calc(100% + 6px)",right:0,zIndex:50,background:t.surface,border:`1px solid ${t.border}`,boxShadow:t.shadow,borderRadius:8,padding:"8px 14px",fontSize:12,color:t.text,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Sign Out</button>
              </>
            )}
          </div>
        </div>
        <nav style={{display:"flex",overflowX:"auto",padding:"0 8px"}}>
          {TABS.map(([id,label])=>(
            <button key={id} onClick={()=>switchTab(id)} style={{padding:"10px 14px",background:"none",border:"none",whiteSpace:"nowrap",color:tab===id?t.clay:t.muted,fontFamily:"inherit",fontSize:13,cursor:"pointer",borderBottom:tab===id?`2px solid ${t.clay}`:"2px solid transparent",fontWeight:tab===id?600:400}}>{label}</button>
          ))}
        </nav>
        {tab==="prefs"&&<div style={{display:"flex",justifyContent:"center",gap:16,padding:"4px 0",borderTop:`1px solid ${t.border}`,background:t.surface}}>
          {[["FAQ","/faq.html"],["Contact","/faq.html#contact"],["Privacy","/privacy.html"],["Terms","/terms.html"]].map(([label,href])=>(
            <a key={label} href={href} style={{fontSize:10,color:t.muted,textDecoration:"none",fontFamily:"inherit"}}>{label}</a>
          ))}
        </div>}
      </header>

      {/* Toast notification */}
      {toast&&(
        <div style={{position:"fixed",top:80,left:"50%",transform:"translateX(-50%)",background:t.ink,color:t.surface,padding:"10px 24px",borderRadius:20,fontSize:14,fontWeight:600,zIndex:300,boxShadow:t.shadowMd,whiteSpace:"nowrap",animation:"fadeIn 0.2s ease"}}>
          {toast}
        </div>
      )}

      {showCheckin && (
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>Check In</p>
            <button onClick={()=>setShowCheckin(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          <p style={{margin:"0 0 16px",color:t.muted,fontSize:13}}>What is your vibe right now?</p>
          <p style={{margin:"0 0 10px",fontWeight:600,fontSize:13,color:t.textSoft}}>How are you feeling?</p>
          <ChipPicker items={FEELINGS} selected={ciFeeling} onToggle={key=>toggleArr(ciFeeling,setCiFeeling,key)} color={t.clay} bgColor={t.clayLight}/>
          <p style={{margin:"16px 0 10px",fontWeight:600,fontSize:13,color:t.textSoft}}>What are you doing?</p>
          <ChipPicker items={ACTIVITIES} selected={ciActivity} onToggle={key=>toggleArr(ciActivity,setCiActivity,key)} color={t.dusk} bgColor={t.duskLight}/>
          <input placeholder="Notes (optional)" value={ciNote} onChange={e=>setCiNote(e.target.value)} style={{...inp,marginTop:16,marginBottom:14}}/>
          <div style={{display:"flex",gap:10}}>
            <button onClick={submitCheckin} style={{flex:1,padding:"12px 0",background:t.purple,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:15,fontWeight:600}}>Save</button>
            <button onClick={()=>setShowCheckin(false)} style={{flex:1,padding:"12px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:10,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:15}}>Cancel</button>
          </div>
        </Sheet>
      )}

      {restockItem&&(
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>Restock</p>
            <button onClick={()=>setRestockItem(null)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          <p style={{fontSize:13,color:t.purple,fontWeight:600,margin:"0 0 16px"}}>{restockItem.name} <span style={{color:t.muted,fontWeight:400}}>{STASH_ICONS[restockItem.cat]} {restockItem.cat}{restockItem.weight?` · ${restockItem.weight}g`:""}</span></p>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Dispensary</label>
            <input
              list="restock-disp-list"
              placeholder="Where'd you get it?"
              value={restockDisp}
              onChange={e=>setRestockDisp(e.target.value)}
              style={inp}
            />
            <datalist id="restock-disp-list">
              {[...new Set(stash.map(s=>s.dispensary).filter(Boolean))].map(d=><option key={d} value={d}/>)}
            </datalist>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Price paid (optional)</label>
            <input type="number" min="0" step="0.01" placeholder="$0.00" value={restockPrice} onChange={e=>setRestockPrice(e.target.value)} style={inp}/>
          </div>
          <div style={{marginBottom:20}}>
            <label style={lbl}>New COA link (optional — if batch changed)</label>
            <input placeholder="https://..." value={restockCoa} onChange={e=>setRestockCoa(e.target.value)} style={inp}/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button
              onClick={()=>{
                const updates = {
                  isCurrent:  true,
                  dispensary: restockDisp.trim() || restockItem.dispensary,
                  totalPrice: restockPrice ? restockPrice : restockItem.totalPrice,
                  ...(restockCoa.trim() && { coaUrl: restockCoa.trim() }),
                };
                setStash(prev=>prev.map(x=>x.id===restockItem.id?{...x,...updates}:x));
                if(shouldSync) syncStash({...restockItem,...updates});
                setRestockItem(null); setRestockDisp(""); setRestockPrice(""); setRestockCoa("");
                showToast("✅ Restocked!");
              }}
              style={{flex:1,padding:"13px 0",background:t.sage,border:"none",borderRadius:10,color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}
            >Restock</button>
            <button onClick={()=>setRestockItem(null)} style={{flex:1,padding:"13px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:10,color:t.muted,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </Sheet>
      )}

      {showFeedback&&(
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>💬 Send Feedback</p>
            <button onClick={()=>{setShowFeedback(false);setFbSent(false);setFbName("");setFbEmail("");setFbComment("");setFbCaptcha("");}} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          {fbSent?(
            <div style={{textAlign:"center",padding:"24px 0"}}>
              <p style={{fontSize:32,margin:"0 0 8px"}}>🙏</p>
              <p style={{fontWeight:600,fontSize:15,margin:"0 0 6px"}}>Thanks for your feedback!</p>
              <p style={{color:t.muted,fontSize:13}}>We read every message.</p>
            </div>
          ):(
            <>
              <p style={{margin:"0 0 16px",color:t.muted,fontSize:13}}>We'd love to hear from you. All fields optional except the math.</p>
              <div style={{marginBottom:12}}>
                <label style={lbl}>Name (optional)</label>
                <input placeholder="Your name" value={fbName} onChange={e=>setFbName(e.target.value)} style={inp}/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={lbl}>Email (optional)</label>
                <input type="email" placeholder="your@email.com" value={fbEmail} onChange={e=>setFbEmail(e.target.value)} style={inp}/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={lbl}>Comment *</label>
                <textarea placeholder="What's on your mind?" value={fbComment} onChange={e=>setFbComment(e.target.value)} rows={4} style={{...inp,resize:"vertical"}}/>
              </div>
              <div style={{marginBottom:18}}>
                <label style={lbl}>What is {fbCaptchaQ.a} + {fbCaptchaQ.b}? *</label>
                <input type="number" placeholder="Your answer" value={fbCaptcha} onChange={e=>setFbCaptcha(e.target.value)} style={inp}/>
              </div>
              <button
                onClick={async()=>{
                  if(!fbComment.trim()){showToast("Please add a comment"); return;}
                  if(parseInt(fbCaptcha)!==fbCaptchaQ.a+fbCaptchaQ.b){showToast("Math check failed — try again"); return;}
                  setFbSending(true);
                  try {
                    const res = await fetch("/api/feedback",{
                      method:"POST",
                      headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({
                        name:fbName.trim(),
                        email:fbEmail.trim(),
                        comment:fbComment.trim(),
                        device:navigator.userAgent,
                        timezone:prefs.timezone||Intl.DateTimeFormat().resolvedOptions().timeZone,
                        ts:new Date().toISOString(),
                      })
                    });
                    if(res.ok) setFbSent(true);
                    else showToast("Something went wrong — try again");
                  } catch(err){
                    showToast("Something went wrong — try again");
                  }
                  setFbSending(false);
                }}
                disabled={fbSending}
                style={{width:"100%",padding:"12px 0",background:fbSending?t.muted:t.sage,border:"none",borderRadius:10,color:"#fff",cursor:fbSending?"not-allowed":"pointer",fontFamily:"inherit",fontSize:15,fontWeight:600}}
              >
                {fbSending?"Sending...":"Send Feedback"}
              </button>
            </>
          )}
        </Sheet>
      )}

      {timelineEditEntry&&(
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>Edit Entry</p>
            <button onClick={()=>setTimelineEditEntry(null)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Time</label>
            <input type="datetime-local" value={editTime} onChange={e=>setEditTime(e.target.value)} style={inp}/>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Feelings</label>
            <ChipPicker items={FEELINGS} selected={editFeelings} onToggle={key=>toggleArr(editFeelings,setEditFeelings,key)} color={t.clay} bgColor={t.clayLight}/>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Activities</label>
            <ChipPicker items={ACTIVITIES} selected={editActivities} onToggle={key=>toggleArr(editActivities,setEditActivities,key)} color={t.dusk} bgColor={t.duskLight}/>
          </div>
          <div style={{marginBottom:18}}>
            <label style={lbl}>Note</label>
            <input placeholder="Any notes..." value={editNote} onChange={e=>setEditNote(e.target.value)} style={inp}/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>{
              const isIntake = timelineEditEntry._type==="intake";
              const updated = {ts:new Date(editTime).toISOString(),note:editNote,feelings:editFeelings,activities:editActivities};
              if(isIntake) setIntake(prev=>prev.map(x=>x.id===timelineEditEntry.id?{...x,...updated}:x));
              else setCheckins(prev=>prev.map(x=>x.id===timelineEditEntry.id?{...x,...updated}:x));
              setTimelineEditEntry(null); showToast("✅ Updated!");
            }} style={{flex:1,padding:"12px 0",background:t.sage,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:15,fontWeight:600}}>Save</button>
            <button onClick={()=>setTimelineEditEntry(null)} style={{flex:1,padding:"12px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:10,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:15}}>Cancel</button>
          </div>
        </Sheet>
      )}

      {reupConfirmId && (
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>Confirm Re-up 🌿</p>
            <button onClick={()=>setReupConfirmId(null)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          <p style={{margin:"0 0 4px",color:t.muted,fontSize:13}}>
            {stash.find(s=>s.id===reupConfirmId)?.name}
          </p>
          <p style={{margin:"0 0 18px",color:t.textSoft,fontSize:13}}>When did you pick this up?</p>
          <div style={{marginBottom:20}}>
            <label style={lbl}>Re-up Date</label>
            <input type="date" value={reupDate} onChange={e=>setReupDate(e.target.value)} style={inp}/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={confirmReup} style={{flex:1,padding:"12px 0",background:t.green,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:15,fontWeight:600}}>✓ Confirm Re-up</button>
            <button onClick={()=>setReupConfirmId(null)} style={{flex:1,padding:"12px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:10,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:15}}>Cancel</button>
          </div>
        </Sheet>
      )}

      {showQuickIntake && (
        <Sheet>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <p style={{margin:0,fontWeight:700,fontSize:18}}>Quick Log</p>
            <button onClick={()=>setShowQuickIntake(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:t.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          {qiItem&&<p style={{margin:"0 0 14px",color:t.purple,fontSize:14,fontWeight:600}}>{qiItem.name} <span style={{fontSize:12,color:t.muted,fontWeight:400}}>{STASH_ICONS[qiItem.cat]} {qiItem.cat}</span></p>}

          {/* Methods filtered by stash category — hidden when only one option */}
          {(()=>{
            const cat = qiItem?.cat || "Flower";
            const methodsByCat = {
              "Flower":      ["Bowl","Bong","Joint","One Hitter","Dry Herb Vape"],
              "Pre-Roll":    ["Joint"],
              "Blunt":       ["Joint"],
              "Concentrate": ["Dab","Bowl"],
              "Vape Cart":   ["Oil Vape"],
              "Edible":      ["Edible"],
              "Tincture":    ["Tincture"],
            };
            const methods = methodsByCat[cat] || ["Bowl"];
            if(methods.length === 1) return null;
            return (
              <div style={{marginBottom:14}}>
                <label style={lbl}>Method</label>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {methods.map(m=>(
                    <button key={m} onClick={()=>setQiMethod(m)} style={{padding:"8px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${qiMethod===m?t.purple:t.border}`,background:qiMethod===m?t.purpleLight:t.surface,color:qiMethod===m?t.purple:t.muted,fontWeight:qiMethod===m?600:400}}>
                      {METHOD_ICONS[m]} {m}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Weight for flower/concentrate */}
          {["Bowl","Bong","Joint","One Hitter","Dab"].includes(qiMethod)&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div><label style={lbl}>Weight (g)</label><input type="number" min="0" step="0.01" placeholder="0.35" value={qiWeight} onChange={e=>setQiWeight(e.target.value)} style={inp}/></div>
              <div><label style={lbl}>Qty</label><Stepper value={qiQty} onChange={setQiQty} min={qiMethod==="Joint"?0.5:1} step={qiMethod==="Joint"?0.5:1}/></div>
            </div>
          )}

          {/* Edible qty */}
          {qiMethod==="Edible"&&(
            <div style={{marginBottom:14}}>
              <label style={lbl}>Pieces</label>
              <Stepper value={qiQty} onChange={setQiQty} min={0.5} step={0.5}/>
            </div>
          )}
          {/* Edible THC preview */}
          {qiMethod==="Edible"&&parseFloat(qiItem?.strength?.THC||0)>0&&(()=>{
            const mgEach=parseFloat(qiItem.strength.THC);
            const raw=parseFloat((mgEach*qiQty).toFixed(1));
            return <div style={{background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:10,padding:"10px 14px",marginBottom:14}}>
              <div style={{fontSize:13}}><span style={{fontWeight:700,color:t.purple}}>{raw}mg THC</span> <span style={{color:t.muted}}>({mgEach}mg × {qiQty})</span></div>
            </div>;
          })()}

          {/* Oil Vape hits */}
          {qiMethod==="Oil Vape"&&(
            <div style={{marginBottom:14}}>
              <label style={lbl}>Hits</label>
              <div style={{display:"flex",alignItems:"center",gap:16}}>
                <Stepper value={qiOilHits} onChange={setQiOilHits}/>
                {qiItem&&(()=>{
                  const cartTHCpct = qiItem.potency ? parseFloat(qiItem.potency)/100 : 0.8;
                  const cartSizeG = qiItem.vapeSize==="0.5g" ? 0.5 : 1.0;
                  const mgPerHit = (cartSizeG*1000*cartTHCpct)/(cartSizeG===0.5?100:200);
                  return <span style={{fontSize:13,color:t.purple,fontWeight:600}}>~{(mgPerHit*qiOilHits).toFixed(1)}mg THC</span>;
                })()}
              </div>
            </div>
          )}

          {/* Tincture ml */}
          {qiMethod==="Tincture"&&(
            <div style={{marginBottom:14}}>
              <label style={lbl}>Volume (ml)</label>
              <input type="number" min="0" step="0.5" placeholder="1.0" value={qiWeight} onChange={e=>setQiWeight(e.target.value)} style={inp}/>
            </div>
          )}

          {/* THC preview for weighed methods */}
          {["Bowl","Bong","Joint","One Hitter","Dab"].includes(qiMethod)&&qiWeight&&parseFloat(qiWeight)>0&&stashTotalTHCpct(qiItem)&&(()=>{
            const thc=calcTHC(parseFloat(qiWeight)*qiQty,stashTotalTHCpct(qiItem),qiMethod);
            return <div style={{background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:10,padding:"10px 14px",marginBottom:14}}>
              <div style={{fontSize:13,marginBottom:2}}><span style={{fontWeight:700,color:t.purple}}>{thc.raw}mg</span> <span style={{color:t.muted}}>total THC</span></div>
              <div style={{fontSize:13}}><span style={{fontWeight:600,color:t.green}}>~{thc.lo}-{thc.hi}mg</span> <span style={{color:t.muted}}>estimated absorbed</span></div>
            </div>;
          })()}

          <div style={{marginBottom:14}}>
            <label style={lbl}>Feelings (optional)</label>
            <ChipPicker items={FEELINGS} selected={qiFeeling} onToggle={key=>toggleArr(qiFeeling,setQiFeeling,key)} color={t.clay} bgColor={t.clayLight}/>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Activities (optional)</label>
            <ChipPicker items={ACTIVITIES} selected={qiActivity} onToggle={key=>toggleArr(qiActivity,setQiActivity,key)} color={t.dusk} bgColor={t.duskLight}/>
          </div>

          <div style={{display:"flex",gap:10,marginTop:18}}>
            <button onClick={submitQuickIntake} style={{flex:1,padding:"14px 0",background:t.green,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:16,fontWeight:700}}>Log It</button>
            <button onClick={()=>setShowQuickIntake(false)} style={{flex:1,padding:"14px 0",background:"none",border:`1px solid ${t.border}`,borderRadius:10,color:t.muted,cursor:"pointer",fontFamily:"inherit",fontSize:15}}>Cancel</button></div>
        </Sheet>
      )}

        <main style={{flex:1,padding:20,minWidth:0,overflowY:"auto",overflowX:"hidden",WebkitOverflowScrolling:"touch"}}>

        {tab==="home" && (
          <div>
            <Card style={{marginBottom:12,padding:"14px 16px"}}>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>switchTab("intake")} style={{flex:1,padding:"12px 8px",borderRadius:12,background:pulse==="yes"?t.sage:t.sageLight,border:`1.5px solid ${t.sage}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,transform:pulse==="yes"?"scale(0.96)":"scale(1)",transition:"all 0.15s",fontFamily:"inherit"}}>
                  <span style={{fontSize:26}}>🌿</span>
                  <span style={{fontWeight:700,fontSize:13,color:pulse==="yes"?"#fff":t.sage}}>Log</span>
                  <span style={{fontSize:10,color:pulse==="yes"?"rgba(255,255,255,0.8)":t.muted}}>intake</span>
                </button>
                <button onClick={()=>{setPulse("checkin");setTimeout(()=>setPulse(null),600);setShowCheckin(true);}} style={{flex:1,padding:"12px 8px",borderRadius:12,background:pulse==="checkin"?t.clay:t.clayLight,border:`1.5px solid ${t.clay}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,transform:pulse==="checkin"?"scale(0.96)":"scale(1)",transition:"all 0.15s",fontFamily:"inherit"}}>
                  <span style={{fontSize:26}}>✨</span>
                  <span style={{fontWeight:700,fontSize:13,color:pulse==="checkin"?"#fff":t.clay}}>CHECK IN</span>
                  <span style={{fontSize:10,color:pulse==="checkin"?"rgba(255,255,255,0.8)":t.muted}}>how do you feel?</span>
                </button>
              </div>
            </Card>

            {/* Daily Stats */}
            {homeTodaySessions>0&&(
              <Card style={{marginBottom:12}}>
                <p style={{margin:"0 0 12px",fontWeight:700,fontSize:14}}>Today</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div style={{background:t.sageLight,borderRadius:10,padding:"12px 14px",border:`1px solid ${t.sageBorder}`}}>
                    <div style={{fontSize:26,fontWeight:700,color:t.sage,lineHeight:1}}>{homeTodaySessions}</div>
                    <div style={{fontSize:11,color:t.sageMid,marginTop:3,fontWeight:500}}>sessions</div>
                  </div>
                  <div style={{background:t.inkLight,borderRadius:10,padding:"12px 14px",border:`1px solid ${t.border}`}}>
                    <div style={{fontSize:26,fontWeight:700,color:t.ink,lineHeight:1}}>{homeTodayAbsorption.toFixed(1)}<span style={{fontSize:13,fontWeight:400}}> mg</span></div>
                    <div style={{fontSize:11,color:t.inkMid,marginTop:3,fontWeight:500}}>absorbed</div>
                  </div>
                  {homeLastAgo&&<div style={{background:t.surfaceAlt,borderRadius:10,padding:"12px 14px",border:`1px solid ${t.border}`}}>
                    <div style={{fontSize:18,fontWeight:700,color:t.textSoft,lineHeight:1}}>{homeLastAgo}</div>
                    <div style={{fontSize:11,color:t.muted,marginTop:3,fontWeight:500}}>last intake</div>
                  </div>}
                  {homeStreak>0&&<div style={{background:t.amberLight,borderRadius:10,padding:"12px 14px",border:`1px solid ${t.amberBorder}`}}>
                    <div style={{fontSize:26,fontWeight:700,color:t.amber,lineHeight:1}}>{homeStreak}<span style={{fontSize:13,fontWeight:400}}> days</span></div>
                    <div style={{fontSize:11,color:t.amberMid,marginTop:3,fontWeight:500}}>streak</div>
                  </div>}
                </div>
              </Card>
            )}

            {currentItems.length>0&&(
              <Card style={{marginBottom:12}}>
                <p style={{margin:"0 0 10px",fontWeight:700,fontSize:14}}>Quick Log</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[...currentItems].sort((a,b)=>(b.favorite?1:0)-(a.favorite?1:0)).map(s=>(
                    <button
                      key={s.id}
                      onClick={() => {
                        setQiItem(s);
                        const m = defaultMethodForCat(s.cat);
                        setQiMethod(m);
                        setQiWeight(
                          (s.cat === "Pre-Roll" || s.cat === "Blunt")
                            ? s.jointWt || "0.5"
                            : METHOD_DEFAULT_WEIGHT[m] || ""
                        );
                        setShowQuickIntake(true);
                      }}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: catBg(s.cat),
                        border: '1px solid ' + catColor(s.cat) + '44',
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: catColor(s.cat),
                          fontWeight: 600,
                          marginBottom: 1,
                        }}
                      >
                        {STASH_ICONS[s.cat]} {s.cat}
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: t.text,
                          marginBottom: 2,
                        }}
                      >
                        {s.name}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: t.muted,
                        }}
                      >
                        {stashSubtitle(s)}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {homeTags.length>0&&(
              <Card style={{marginBottom:12}}>
                <p style={{margin:"0 0 10px",fontWeight:700,fontSize:14}}>Your Vibe — Last 7 Days</p>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,alignItems:"center"}}>
                  {homeTags.map(([tag,{count,type}])=>{
                    const scale=0.8+(count/homeMaxTag)*0.7;
                    const isFeeling=type==="feeling";
                    return <span key={tag} style={{fontSize:`${Math.round(11*scale)}px`,fontWeight:count===homeMaxTag?700:500,padding:"4px 10px",borderRadius:20,background:isFeeling?t.clayLight:t.duskLight,color:isFeeling?t.clay:t.dusk,opacity:0.55+(count/homeMaxTag)*0.45,lineHeight:1.4}}>{isFeeling?(FEELING_MAP[tag]||""):(ACTIVITY_MAP[tag]||"")} {tag}</span>;
                  })}
                </div>
                <div style={{display:"flex",gap:16,marginTop:12,paddingTop:10,borderTop:`1px solid ${t.border}`}}>
                  <span style={{fontSize:10,color:t.clay,fontWeight:700}}>● Feelings</span>
                  <span style={{fontSize:10,color:t.dusk,fontWeight:700}}>● Activities</span>
                </div>
              </Card>
            )}

            {homeFeed.length>0?(
              <Card>
                <p style={{margin:"0 0 12px",fontWeight:700,fontSize:14}}>Recent Activity</p>
                {homeFeed.map((item,i)=>(
                  <SwipeRow
                    key={item.id}
                    onDelete={()=>{ if(item._type==="intake") setIntake(prev=>prev.filter(x=>x.id!==item.id)); else setCheckins(prev=>prev.filter(x=>x.id!==item.id)); }}
                    onEdit={()=>{
                      setEditTime(toLocalInput(item.ts));
                      setEditNote(item.note||"");
                      setEditFeelings(item.feelings||[]);
                      setEditActivities(item.activities||[]);
                      setTimelineEditEntry(item);
                    }}
                  >
                    <div style={{paddingBottom:9,marginBottom:0,borderBottom:i<homeFeed.length-1?`1px solid ${t.border}`:"none"}}>
                    {item._type==="intake"?(
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                            <span style={{fontSize:13}}>{METHOD_ICONS[item.method]}</span>
                            <span style={{fontWeight:600,fontSize:13}}>{item.method}</span>
                            {item.stashName&&<span style={{fontSize:11,color:t.clay}}>{item.stashName}</span>}
                          </div>
                          {(item.activities||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:3}}>{item.activities.map(a=><span key={a} style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:t.duskLight,color:t.dusk}}>{ACTIVITY_MAP[a]} {a}</span>)}</div>}
                          {(item.feelings||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3}}>{item.feelings.map(f=><span key={f} style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:t.clayLight,color:t.clay}}>{FEELING_MAP[f]} {f}</span>)}</div>}
                          <p style={{fontSize:10,color:t.muted,margin:"3px 0 0"}}>{fmtDate(item.ts,homeTZ)}</p>
                        </div>
                        {item.thcLo&&<div style={{textAlign:"right",flexShrink:0,marginLeft:10}}><div style={{fontSize:12,fontWeight:700,color:t.ink}}>{item.thcLo===item.thcHi?`${item.thcLo}mg`:`${item.thcLo}–${item.thcHi}mg`}</div><div style={{fontSize:9,color:t.muted}}>{item.method==="Edible"?"consumed":"absorbed"}</div></div>}
                      </div>
                    ):(
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                          <span style={{fontSize:13}}>✨</span><span style={{fontWeight:600,fontSize:13}}>Check-in</span>
                        </div>
                        {(item.activities||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:3}}>{item.activities.map(a=><span key={a} style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:t.duskLight,color:t.dusk}}>{ACTIVITY_MAP[a]} {a}</span>)}</div>}
                        {(item.feelings||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:3}}>{item.feelings.map(f=><span key={f} style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:t.clayLight,color:t.clay}}>{FEELING_MAP[f]} {f}</span>)}</div>}
                        {item.note&&<p style={{fontSize:11,color:t.muted,margin:"2px 0",fontStyle:"italic"}}>{item.note}</p>}
                        <p style={{fontSize:10,color:t.muted,margin:"2px 0 0"}}>{fmtDate(item.ts,homeTZ)}</p>
                      </div>
                    )}
                    </div>
                  </SwipeRow>
                ))}
              </Card>
            ):(
              <Card style={{textAlign:"center",padding:"32px 20px"}}>
                {stash.length===0?(
                  <>
                    <p style={{fontSize:32,margin:"0 0 8px"}}>🌱</p>
                    <p style={{fontWeight:600,fontSize:15,margin:"0 0 6px"}}>Welcome to CannaTrack</p>
                    <p style={{color:t.muted,fontSize:13,margin:"0 0 16px"}}>Start by adding what you have to your stash</p>
                    <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                      <button onClick={startScanStash} style={{padding:"10px 18px",background:t.sage,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>📷 Scan a label</button>
                      <button onClick={startGenericStash} style={{padding:"10px 18px",background:t.surface,border:`1px solid ${t.sage}`,borderRadius:10,color:t.sage,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>🌿 No label? Add generic</button>
                    </div>
                  </>
                ):(
                  <>
                    <p style={{fontSize:32,margin:"0 0 8px"}}>✨</p>
                    <p style={{fontWeight:600,fontSize:15,margin:"0 0 6px"}}>How are you feeling?</p>
                    <p style={{color:t.muted,fontSize:13,margin:"0 0 16px"}}>Log your first check-in to start tracking your experience</p>
                    <button onClick={()=>setShowCheckin(true)} style={{padding:"10px 22px",background:t.clay,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>✨ First Check-in</button>
                  </>
                )}
              </Card>
            )}
          </div>
        )}

        {tab==="intake" && (
          <div>
            {pendingIntakes.length>0&&(
              <div style={{background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:12,padding:"12px 14px",marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontWeight:700,fontSize:13,color:t.purple}}>Batch — {pendingIntakes.length} queued</span>
                  <button onClick={()=>setPendingIntakes([])} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Clear all</button>
                </div>
                {pendingIntakes.map((e,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,padding:"4px 0",borderTop:i>0?`1px solid ${t.purpleMid}`:undefined}}>
                    <span style={{color:t.purple,fontWeight:600}}>{METHOD_ICONS[e.method]} {e.method}{e.stashName?` · ${e.stashName}`:""}</span>
                    <button onClick={()=>setPendingIntakes(prev=>prev.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>×</button>
                  </div>
                ))}
              </div>
            )}
            <Card style={{marginBottom:20}}>
              <p style={{margin:"0 0 16px",fontWeight:700,fontSize:15}}>Log Intake</p>
              <label style={lbl}>Method</label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:18}}>
                {METHODS.map(m=>(
                  <button key={m} onClick={()=>{ setIMethod(m); if(METHOD_DEFAULT_WEIGHT[m]) setIWeight(METHOD_DEFAULT_WEIGHT[m]); setIJointSharing(false); }} style={{padding:"10px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:11,border:`1px solid ${iMethod===m?t.purple:t.border}`,background:iMethod===m?t.purpleLight:t.surface,color:iMethod===m?t.purple:t.textSoft,display:"flex",flexDirection:"column",alignItems:"center",gap:4,fontWeight:iMethod===m?600:400}}>
                    <span style={{fontSize:18}}>{METHOD_ICONS[m]}</span>{m}
                  </button>
                ))}
              </div>

              {/* Stash picker — moved above weight */}
              <div style={{marginBottom:16,position:"relative"}}>
                <label style={lbl}>Stash item (optional)</label>
                {iStashId?(
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:8}}>
                    <span style={{fontSize:12,color:catColor(selStash?.cat),fontWeight:600}}>{STASH_ICONS[selStash?.cat]} {selStash?.cat}</span>
                    <span style={{fontSize:13,color:t.purple,fontWeight:600,flex:1}}>{selStash?.name}</span>
                    <button onClick={()=>{setIStashId(null);setIStashQuery("");}} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:16,padding:0}}>×</button>
                  </div>
                ):stash.length===0?(
                  <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 0"}}>
                    <span style={{fontSize:13,color:t.muted}}>No stash yet —</span>
                    <button onClick={()=>switchTab("stash")} style={{background:"none",border:"none",color:t.purple,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",padding:0,textDecoration:"underline"}}>add to stash</button>
                  </div>
                ):(
                  <>
                    <input placeholder="Search stash..." value={iStashQuery} onChange={e=>{setIStashQuery(e.target.value);setShowIDrop(true);}} onFocus={()=>setShowIDrop(true)} style={inp}/>
                    {showIDrop&&(
                      <div style={{position:"absolute",top:"100%",left:0,right:0,background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,boxShadow:t.shadowMd,zIndex:50,maxHeight:180,overflowY:"auto",marginTop:4}}>
                        {stashMatches.length===0?<p style={{padding:"10px 14px",color:t.muted,fontSize:13,margin:0}}>No matches</p>
                          :stashMatches.map(s=>(
                            <div key={s.id} onClick={()=>{setIStashId(s.id);setIStashQuery(s.name);setShowIDrop(false);}} style={{padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.background=t.purpleLight} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              <span style={{fontSize:13,fontWeight:500}}>{s.name}</span>
                              <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:catBg(s.cat),color:catColor(s.cat),fontWeight:600}}>{STASH_ICONS[s.cat]} {s.cat}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {["Bowl","Bong","Joint","One Hitter","Dab"].includes(iMethod)&&(
                <div style={{marginBottom:16}}>
                  {iMethod==="Joint"?(
                    <>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                        <label style={{...lbl,marginBottom:0}}>Joint</label>
                        <button onClick={()=>setIJointSharing(p=>!p)} style={{padding:"5px 12px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${iJointSharing?t.purple:t.border}`,background:iJointSharing?t.purpleLight:t.surface,color:iJointSharing?t.purple:t.muted,fontWeight:iJointSharing?700:400}}>
                          {iJointSharing?"👥 Sharing":"Solo"}
                        </button>
                      </div>
                      {!iJointSharing?(
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                          <div><label style={lbl}>Weight (g)</label><input type="number" min="0" step="0.1" value={iWeight} onChange={e=>setIWeight(e.target.value)} style={inp}/></div>
                          <div><label style={lbl}>Qty</label><Stepper value={iQty} onChange={setIQty} min={0.5} step={0.5}/></div>
                        </div>
                      ):(
                        <>
                          <div style={{marginBottom:12}}>
                            <label style={lbl}>Joint Size</label>
                            <div style={{display:"flex",gap:8}}>
                              {["0.5","1"].map(sz=>(
                                <button key={sz} onClick={()=>{setIJointSize(sz);setIWeight(sz);}} style={{flex:1,padding:"9px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${iJointSize===sz?t.purple:t.border}`,background:iJointSize===sz?t.purpleLight:t.surface,color:iJointSize===sz?t.purple:t.muted,fontWeight:iJointSize===sz?700:400}}>{sz}g</button>
                              ))}
                              <button onClick={()=>{setIJointSize("custom");}} style={{flex:1,padding:"9px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${iJointSize==="custom"?t.purple:t.border}`,background:iJointSize==="custom"?t.purpleLight:t.surface,color:iJointSize==="custom"?t.purple:t.muted,fontWeight:iJointSize==="custom"?700:400}}>Custom</button>
                            </div>
                            {iJointSize==="custom"&&<input type="number" min="0" step="0.1" placeholder="Weight (g)" value={iWeight} onChange={e=>setIWeight(e.target.value)} style={{...inp,marginTop:8}}/>}
                          </div>
                          {(()=>{
                            const totalHits = JOINT_HITS[iJointSize] || (iWeight ? Math.round(parseFloat(iWeight)*6) : 10);
                            const myPct = Math.min(iJointHits/totalHits,1);
                            const myG = iWeight ? (parseFloat(iWeight)*myPct).toFixed(2) : "—";
                            return (
                              <div>
                                <label style={lbl}>Your Hits <span style={{color:t.muted,fontWeight:400}}>({iJointHits} of ~{totalHits} total)</span></label>
                                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                                  <Stepper value={iJointHits} onChange={v=>setIJointHits(Math.max(1,Math.min(v,totalHits)))}/>
                                  <span style={{fontSize:13,color:t.purple,fontWeight:600}}>≈ {myG}g your share</span>
                                </div>
                                <p style={{fontSize:11,color:t.muted,margin:0}}>Adjust for canoe, clogs, or bad luck 🙃</p>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </>
                  ):(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={lbl}>{iMethod==="Dab"?"Weight (g concentrate)":"Weight (g)"}</label><input type="number" min="0" step="0.01" value={iWeight} onChange={e=>setIWeight(e.target.value)} style={inp}/></div>
                      <div><label style={lbl}>Qty</label><Stepper value={iQty} onChange={setIQty}/></div>
                    </div>
                  )}
                </div>
              )}

              {/* Additives — only for Bowl, Bong, Joint, One Hitter */}
              {["Bowl","Bong","Joint","One Hitter"].includes(iMethod)&&(
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Additives (optional)</label>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {["Wax","Shatter","Kief","Hash","Diamonds","Live Resin","Live Rosin"].map(a=>(
                      <button key={a} onClick={()=>setIAdditives(prev=>prev.includes(a)?prev.filter(x=>x!==a):[...prev,a])} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${iAdditives.includes(a)?t.amber:t.border}`,background:iAdditives.includes(a)?"#fef3c7":t.surface,color:iAdditives.includes(a)?t.amber:t.muted,fontWeight:iAdditives.includes(a)?700:400}}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {iMethod==="Dry Herb Vape"&&(
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Device Type</label>
                  <div style={{display:"flex",gap:8,marginBottom:8}}>
                    {["Personal","Volcano"].map(v=>(
                      <button key={v} onClick={()=>setIDryVapeType(v)} style={{flex:1,padding:"9px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${iDryVapeType===v?t.purple:t.border}`,background:iDryVapeType===v?t.purpleLight:t.surface,color:iDryVapeType===v?t.purple:t.muted,fontWeight:iDryVapeType===v?700:400}}>
                        {v==="Personal"?"📱 Personal (PAX etc.)":"🌋 Desktop (Volcano)"}
                      </button>
                    ))}
                  </div>
                  <p style={{fontSize:11,color:t.muted,margin:0}}>Assumes {iDryVapeType==="Personal"?"0.1g":"0.3g"} per session</p>
                </div>
              )}
              {iMethod==="Edible"&&(
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Size (mg THC each)</label>
                  <div style={{display:"flex",gap:8,marginBottom:12}}>{["1","2.5","5","10"].map(sz=><button key={sz} onClick={()=>setIEdibleSize(sz)} style={{flex:1,padding:"9px 4px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,border:`1px solid ${iEdibleSize===sz?t.purple:t.border}`,background:iEdibleSize===sz?t.purpleLight:t.surface,color:iEdibleSize===sz?t.purple:t.muted,fontWeight:iEdibleSize===sz?700:400}}>{sz}mg</button>)}</div>
                  <div style={{display:"flex",alignItems:"center",gap:16}}><Stepper value={iEdibleQty} onChange={setIEdibleQty} min={0.5} step={0.5}/><span style={{fontSize:13,color:t.purple,fontWeight:600}}>= {(parseFloat(iEdibleSize)*iEdibleQty).toFixed(1)}mg</span></div>
                </div>
              )}
              {iMethod==="Tincture"&&(
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Volume (ml)</label>
                  <input type="number" min="0" step="0.5" placeholder="1.0" value={iTinctureML} onChange={e=>setITinctureML(e.target.value)} style={inp}/>
                </div>
              )}
              {iMethod==="Oil Vape"&&(
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Hits</label>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <Stepper value={iOilHits} onChange={setIOilHits}/>
                    {selStash&&<span style={{fontSize:13,color:t.purple,fontWeight:600}}>
                      ~{(()=>{
                        const cartTHCpct = selStash.potency ? parseFloat(selStash.potency)/100 : 0.8;
                        const cartSizeG = selStash.vapeSize==="0.5g" ? 0.5 : 1.0;
                        const mgPerHit = (cartSizeG*1000*cartTHCpct)/(cartSizeG===0.5?100:200);
                        return (mgPerHit*iOilHits).toFixed(1);
                      })()}mg THC</span>}
                    {!selStash&&<span style={{fontSize:12,color:t.muted}}>~{(OIL_VAPE_MG_PER_HIT*iOilHits).toFixed(0)}mg est.</span>}
                  </div>
                  <p style={{fontSize:11,color:t.muted,margin:"6px 0 0"}}>Link a stash cart above for accurate calc</p>
                </div>
              )}

              {formTHC&&(
                <div style={{background:t.purpleLight,border:`1px solid ${t.purpleMid}`,borderRadius:10,padding:"10px 14px",marginBottom:16}}>
                  <div style={{fontSize:13,marginBottom:2}}><span style={{fontWeight:700,color:t.purple}}>{formTHC.raw}mg</span> <span style={{color:t.muted}}>total THC in material</span></div>
                  <div style={{fontSize:13}}><span style={{fontWeight:600,color:t.green}}>~{formTHC.lo}-{formTHC.hi}mg</span> <span style={{color:t.muted}}>estimated absorbed</span></div>
                </div>
              )}

              <div style={{marginBottom:16}}>
                <label style={lbl}>Feelings (optional)</label>
                <ChipPicker items={FEELINGS} selected={iFeeling} onToggle={key=>toggleArr(iFeeling,setIFeeling,key)} color={t.clay} bgColor={t.clayLight}/>
              </div>
              <div style={{marginBottom:16}}>
                <label style={lbl}>Activities (optional)</label>
                <ChipPicker items={ACTIVITIES} selected={iActivity} onToggle={key=>toggleArr(iActivity,setIActivity,key)} color={t.dusk} bgColor={t.duskLight}/>
              </div>
              <div style={{marginBottom:16}}><label style={lbl}>Note (optional)</label><input placeholder="Any notes..." value={iNote} onChange={e=>setINote(e.target.value)} style={inp}/></div>
              <div style={{marginBottom:16}}>
                <label style={lbl}>When? (defaults to now)</label>
                <input type="datetime-local" value={iTime} onChange={e=>setITime(e.target.value)} style={inp}/>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:0}}>
                <button onClick={logIntake} style={{flex:2,padding:"12px 0",background:t.purple,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:15,fontWeight:700}}>
                  {pendingIntakes.length>0?`Log All (${pendingIntakes.length+1})`:"Log Intake"}
                </button>
                <button onClick={addToBatch} title="Add to batch and log another" style={{flex:1,padding:"12px 0",background:"none",border:`1.5px solid ${t.purple}`,borderRadius:10,color:t.purple,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                  + Add Another
                </button>
              </div>
              {["Bowl","Bong","Joint","One Hitter","Dab"].includes(iMethod)&&(!iWeight||parseFloat(iWeight)<=0)&&(
                <p style={{textAlign:"center",fontSize:12,color:t.muted,marginTop:8}}>Enter a weight above to save</p>
              )}
            </Card>

            <SectionLabel>Session Log - {intake.length} entries</SectionLabel>
            {intake.length===0&&<p style={{color:t.muted,fontStyle:"italic",fontSize:13}}>Nothing logged yet.</p>}
            {intake.length>0&&(
              <Card style={{padding:0,overflow:"hidden"}}>
                <div style={{maxHeight:400,overflowY:"auto",padding:"12px 16px"}}>
                  {[...intake].sort((a,b)=>new Date(b.ts)-new Date(a.ts)).map((e,i,arr)=>(
                    <SwipeRow
                      key={e.id}
                      onDelete={()=>delEntry(setIntake,e.id)}
                      onEdit={()=>{
                        setEditTime(toLocalInput(e.ts));
                        setEditNote(e.note||"");
                        setEditFeelings(e.feelings||[]);
                        setEditActivities(e.activities||[]);
                        setTimelineEditEntry({...e,_type:"intake"});
                      }}
                    >
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 0",borderBottom:i<arr.length-1?`1px solid ${t.border}`:"none"}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontSize:14,fontWeight:600}}>{METHOD_ICONS[e.method]} {e.method}</span>
                            <span style={{fontSize:12,color:t.sage,fontWeight:500}}>{intakeSummary(e)}</span>
                          </div>
                          <THCBadge entry={e}/>
                          {e.stashName&&<div style={{marginTop:4}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:10,background:catBg(e.stashCat),color:catColor(e.stashCat),fontWeight:500}}>{STASH_ICONS[e.stashCat]} {e.stashName}</span></div>}
                          {(e.additives||[]).length>0&&<div style={{marginTop:4,display:"flex",flexWrap:"wrap",gap:4}}>{e.additives.map(a=><span key={a} style={{fontSize:11,padding:"2px 7px",borderRadius:10,background:t.amberLight,color:t.amber,fontWeight:500}}>+{a}</span>)}</div>}
                          {(e.feelings||[]).length>0&&<div style={{marginTop:4,display:"flex",flexWrap:"wrap",gap:4}}>{e.feelings.map(f=><span key={f} style={{fontSize:11,padding:"2px 7px",borderRadius:10,background:t.clayLight,color:t.clay}}>{FEELING_MAP[f]} {f}</span>)}</div>}
                          {(e.activities||[]).length>0&&<div style={{marginTop:4,display:"flex",flexWrap:"wrap",gap:4}}>{e.activities.map(a=><span key={a} style={{fontSize:11,padding:"2px 7px",borderRadius:10,background:t.duskLight,color:t.dusk}}>{ACTIVITY_MAP[a]} {a}</span>)}</div>}
                          {e.note&&<div style={{fontSize:12,color:t.muted,marginTop:4,fontStyle:"italic"}}>{e.note}</div>}
                          <div style={{fontSize:10,color:t.muted,marginTop:3}}>{fmtDate(e.ts,prefs.timezone)}</div>
                        </div>
                      </div>
                    </SwipeRow>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {tab==="stash" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>{resetStashForm();setShowStashForm(true);}} style={{padding:"10px 16px",background:t.clay,border:"none",borderRadius:10,color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:700,boxShadow:"0 2px 8px rgba(154, 52, 18, 0.28)",letterSpacing:"0.01em"}}>+ Packaged Stash</button>
                <button onClick={startGenericStash} style={{padding:"10px 14px",background:sGenericMode&&showStashForm?t.clayLight:t.surface,border:`1px solid ${t.clay}`,borderRadius:10,color:t.clay,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>+ Add Generic</button>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>setStashView("stash")} style={{padding:"7px 16px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:stashView==="stash"?700:500,border:`1px solid ${stashView==="stash"?t.sage:t.border}`,background:stashView==="stash"?t.sageLight:t.surface,color:stashView==="stash"?t.sage:t.muted}}>Stash</button>
                {stash.filter(s=>!s.isCurrent).length>0&&(
                  <button onClick={()=>setStashView("library")} style={{padding:"7px 16px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:stashView==="library"?700:500,border:`1px solid ${stashView==="library"?t.ink:t.border}`,background:stashView==="library"?t.inkLight:t.surface,color:stashView==="library"?t.ink:t.muted}}>Library</button>
                )}
              </div>
            </div>

            {/* Search / filter / sort — applies to both Stash and Library views */}
            {stash.length>0&&(()=>{
              const stashFilters = stashEffectiveFilters();
              const stashFiltersActive = !!(stashQuery.trim()||stashFilterCat||stashFilterStrain||stashFilterDays||stashSortKey!=="recommended");
              return (
                <div style={{marginBottom:16}}>
                  <input
                    type="text"
                    value={stashQuery}
                    onChange={e=>setStashQuery(e.target.value)}
                    placeholder='Try "favorite sativas from the last 30 days"'
                    style={{...inp,marginBottom:8}}
                  />
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <select value={stashFilterCat} onChange={e=>setStashFilterCat(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",padding:"7px 10px",fontSize:12}}>
                      <option value="">All Categories</option>
                      {STASH_CATS.map(c=><option key={c} value={c}>{STASH_ICONS[c]} {c}</option>)}
                    </select>
                    <select value={stashFilterStrain} onChange={e=>setStashFilterStrain(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",padding:"7px 10px",fontSize:12}}>
                      <option value="">All Strain Types</option>
                      <option value="Sativa">Sativa</option>
                      <option value="Indica">Indica</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                    <select value={stashFilterDays} onChange={e=>setStashFilterDays(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",padding:"7px 10px",fontSize:12}}>
                      <option value="">Any Time</option>
                      <option value="7">Last 7 Days</option>
                      <option value="30">Last 30 Days</option>
                      <option value="90">Last 90 Days</option>
                    </select>
                    <select value={stashSortKey} onChange={e=>setStashSortKey(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",padding:"7px 10px",fontSize:12}}>
                      <option value="recommended">Sort: Recommended</option>
                      <option value="name">Sort: Name A–Z</option>
                      <option value="rating">Sort: Rating</option>
                      <option value="recent">Sort: Recently Added</option>
                      <option value="price">Sort: Price</option>
                    </select>
                    {stashFiltersActive&&(
                      <button onClick={()=>{setStashQuery("");setStashFilterCat("");setStashFilterStrain("");setStashFilterDays("");setStashSortKey("recommended");}} style={{background:"none",border:"none",padding:"7px 4px",fontSize:12,color:t.clay,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",fontWeight:600}}>Clear</button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Library inline view */}
            {stashView==="library"&&(
              <div style={{marginBottom:16}}>
                <p style={{fontSize:12,color:t.muted,margin:"0 0 12px"}}>Items you've had before — tap Restock to add a new batch with the same strain profile.</p>
                {stash.filter(s=>!s.isCurrent).length===0?(
                  <Card style={{textAlign:"center",padding:"24px 16px"}}>
                    <p style={{color:t.muted,fontSize:13,margin:0}}>No library items yet — items you mark Out will appear here</p>
                  </Card>
                ):stashLibraryList.length===0?(
                  <Card style={{textAlign:"center",padding:"24px 16px"}}>
                    <p style={{color:t.muted,fontSize:13,margin:0}}>No library items match your search/filters</p>
                  </Card>
                ):(
                  stashLibraryList.map(s=>(
                    <div key={s.id} style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${t.border}`,background:t.surface,marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
                          <span style={{fontWeight:700,fontSize:14}}>{s.name}</span>
                          {s.rating>0&&<span style={{fontSize:12,color:t.amber}}>{"★".repeat(s.rating)}</span>}
                          {s.favorite&&<span style={{fontSize:12,color:t.amber,fontWeight:700}}>★ Favorite</span>}
                          {s.isHemp&&<span style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:"#dcfce7",color:"#15803d",fontWeight:700}}>🌾 Hemp</span>}
                        </div>
                        <div style={{fontSize:12,color:t.muted}}>{STASH_ICONS[s.cat]} {s.cat}{s.manufacturer?` · ${s.manufacturer}`:""}</div>
                        {s.dispensary&&<div style={{fontSize:11,color:t.muted,marginTop:1}}>{s.dispensary}</div>}
                        <div style={{fontSize:11,color:t.textSoft,marginTop:1,display:"flex",gap:8}}>
                          {s.potency&&<span>THC {s.potency}%</span>}
                          {s.cbd&&parseFloat(s.cbd)>0&&<span>CBD {s.cbd}%</span>}
                        </div>
                        {s.reupAt&&<div style={{fontSize:11,color:t.sage,marginTop:2}}>Last: {new Date(s.reupAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>}
                        <div style={{display:"flex",gap:12,marginTop:6}}>
                          <button onClick={()=>toggleFavorite(s.id)} style={{background:"none",border:"none",padding:0,fontSize:11,color:s.favorite?t.amber:t.muted,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",fontWeight:s.favorite?700:400}}>{s.favorite?"★ unfave":"☆ fave"}</button>
                          <button onClick={()=>{openEditStash(s);setShowLibrary(false);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.muted,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>edit</button>
                          <button onClick={()=>{if(window.confirm(`Delete "${s.name}"?`))delStashEntry(s.id);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.red,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>delete</button>
                          {s.retailUrl&&<a href={s.retailUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:t.sage,textDecoration:"underline",fontFamily:"inherit"}}>{(()=>{try{return new URL(s.retailUrl).hostname.replace(/^www\./,'');}catch{return'find it';}})()+" ↗"}</a>}
                        </div>
                      </div>
                      <button
                        onClick={()=>{setRestockItem(s);setRestockDisp(s.dispensary||"");setRestockPrice("");setRestockCoa("");}}
                        style={{flexShrink:0,marginLeft:12,padding:"7px 14px",borderRadius:8,border:`1px solid ${t.sage}`,background:t.sageLight,color:t.sage,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}
                      >Restock</button>
                    </div>
                      {editSId===s.id && stashFormCard}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Main stash list — filtered by view */}
            {showStashForm && (!editSId || !editItemInlineVisible) && stashFormCard}

            {stash.length===0&&!showStashForm&&<p style={{color:t.muted,fontStyle:"italic",fontSize:13}}>Your stash is empty. Add something!</p>}
            {stashView==="stash"&&stash.length>0&&stashCurrentList.length===0&&(
              <p style={{color:t.muted,fontStyle:"italic",fontSize:13}}>No stash items match your search/filters.</p>
            )}
            {stashView==="stash"&&stashCurrentList.map(s=>{
              const expanded=expandedStash===s.id;
              const tPct = stashTotalTHCpct(s);
              const tFromPotency = parseFloat(s.potency) > 0;
              const expDateObj = s.expirationDate ? new Date(`${s.expirationDate}T00:00:00`) : null;
              const hasExpDate = !!(expDateObj && !Number.isNaN(expDateObj.getTime()));
              const expDaysLeft = hasExpDate ? Math.ceil((expDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
              const expLabel = hasExpDate
                ? expDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : null;
              const expColor = expDaysLeft != null && expDaysLeft < 0 ? t.red : (expDaysLeft != null && expDaysLeft <= 30 ? t.amber : t.muted);
              const expBg = expDaysLeft != null && expDaysLeft < 0 ? t.redLight : (expDaysLeft != null && expDaysLeft <= 30 ? t.amberLight : t.cardBg);
              return (
                <div key={s.id} style={{background:t.surface,border:`1px solid ${catColor(s.cat)+"44"}`,borderRadius:12,padding:"14px 16px",marginBottom:10,boxShadow:t.shadow}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1,cursor:"pointer"}} onClick={()=>setExpandedStash(expanded?null:s.id)}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:catBg(s.cat),color:catColor(s.cat),fontWeight:600}}>{STASH_ICONS[s.cat]} {s.cat}</span>
                        {s.isCurrent&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:t.duskLight,color:t.dusk,fontWeight:600}}>Current</span>}
                        {s.isHemp&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#dcfce7",color:"#15803d",fontWeight:600}}>🌾 Hemp</span>}
                        {hasExpDate&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:expBg,color:expColor,fontWeight:700}}>Exp {expLabel}</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                        <span style={{fontWeight:700,fontSize:14}}>{s.name}</span>
                        {s.coaUrl&&<span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                          <a href={s.coaUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:t.sageLight,color:t.sage,fontWeight:600,textDecoration:"none",border:`1px solid ${t.sage}44`,whiteSpace:"nowrap"}}>COA</a>
                          <button onClick={e=>{e.stopPropagation();setStash(prev=>prev.map(x=>x.id===s.id?{...x,coaUrl:undefined}:x));if(shouldSync)syncStash({...s,coaUrl:undefined});}} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:11,padding:0,lineHeight:1}} title="Remove COA">×</button>
                        </span>}
                        {s.retailUrl&&<span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                          <a href={s.retailUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:t.purpleLight,color:t.purple,fontWeight:600,textDecoration:"none",border:`1px solid ${t.purple}44`,whiteSpace:"nowrap"}}>{(()=>{try{return new URL(s.retailUrl).hostname.replace(/^www\./,'');}catch{return'Link';}})()}</a>
                          <button onClick={e=>{e.stopPropagation();setStash(prev=>prev.map(x=>x.id===s.id?{...x,retailUrl:undefined}:x));if(shouldSync)syncStash({...s,retailUrl:undefined});}} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:11,padding:0,lineHeight:1}} title="Remove link">×</button>
                        </span>}
                      </div>
                      {s.manufacturer&&<div style={{fontSize:12,color:t.muted,marginBottom:3}}>{s.manufacturer}</div>}
                      <div style={{fontSize:12,color:t.textSoft}}>{stashSubtitle(s)}</div>
                      {(s.pricePerGram||s.pricePerMl)&&<div style={{fontSize:12,color:t.amber,fontWeight:600,marginTop:2}}>${s.pricePerGram||s.pricePerMl}/{s.cat==="Tincture"?"ml":"g"} {s.weight&&<span style={{color:t.muted,fontWeight:400}}>· {s.weight}{s.cat==="Tincture"?"ml":"g"}</span>} {s.totalPrice&&<span style={{color:t.muted,fontWeight:400}}>· ${s.totalPrice} total</span>}</div>}
                      {s.reupAt&&<div style={{fontSize:11,color:t.green,fontWeight:600,marginTop:2}}>Re-up: {new Date(s.reupAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>}
                      <div onClick={e=>e.stopPropagation()} style={{marginTop:4}}><Stars value={s.rating||0} onChange={r=>{setStash(prev=>prev.map(x=>x.id===s.id?{...x,rating:r}:x));if(shouldSync)syncStash({...s,rating:r});}}/></div>
                      <div style={{fontSize:11,color:t.muted,marginTop:6,display:"flex",gap:12}}>
                        <span>{expanded?"▲ less":"▼ details"}</span>
                        <button onClick={e=>{e.stopPropagation();openEditStash(s);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.muted,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>edit</button>
                        <button onClick={e=>{e.stopPropagation();dupeStash(s);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.sage,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>dupe</button>
                        <button onClick={e=>{e.stopPropagation();setQiItem(s);setQiMethod(defaultMethodForCat(s.cat));setQiWeight(METHOD_DEFAULT_WEIGHT[defaultMethodForCat(s.cat)]||"");setShowQuickIntake(true);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.clay,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",fontWeight:600}}>log session</button>
                        <button onClick={e=>{e.stopPropagation();if(window.confirm(`Delete "${s.name}"? This cannot be undone.`))delStashEntry(s.id);}} style={{background:"none",border:"none",padding:0,fontSize:11,color:t.red,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>delete</button>
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,marginLeft:12,flexShrink:0}}>
                      <button onClick={()=>toggleCurrentFlavor(s.id)} style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:11,border:`1px solid ${s.isCurrent?t.green:t.border}`,background:s.isCurrent?t.greenLight:t.surface,color:s.isCurrent?t.green:t.muted,fontWeight:600,whiteSpace:"nowrap"}}>
                        {s.isCurrent?"✓ Current":"Out"}
                      </button>
                    </div>
                  </div>

                  {expanded&&(
                    <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${t.border}`}}>
                      {s.cat==="Flower"&&(
                        <>
                          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
                            {tPct&&<div title={tFromPotency?"Labelled potency":"Auto-calculated from COA"} style={{background:t.purpleLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:16,fontWeight:700,color:t.purple}}>{tPct}%</div><div style={{fontSize:10,color:t.muted}}>{tFromPotency?"THC":"THC (COA)"}</div></div>}
                            {s.thca&&<div style={{background:t.purpleLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:16,fontWeight:700,color:t.purple}}>{s.thca}%</div><div style={{fontSize:10,color:t.muted}}>THCA</div></div>}
                            {s.cbd&&<div style={{background:t.greenLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:16,fontWeight:700,color:t.green}}>{s.cbd}%</div><div style={{fontSize:10,color:t.muted}}>CBD</div></div>}
                            {s.growType&&s.growType!=="Unknown"&&<div style={{background:s.growType==="Indoor"?t.purpleLight:t.greenLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:14}}>{s.growType==="Indoor"?"🏠":"🌳"}</div><div style={{fontSize:10,color:t.muted}}>{s.growType}</div></div>}
                          </div>
                          {(s.terpenes||[]).length>0&&(
                            <div style={{marginBottom:10}}>
                              <p style={{...lbl,marginBottom:6}}>Terpenes</p>
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {s.terpenes.map((terp,i)=>(
                                  <div key={i} style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"4px 10px",display:"flex",alignItems:"center",gap:5}}>
                                    <span style={{fontSize:12,fontWeight:600,color:t.green}}>{terp.name}</span>
                                    {terp.pct&&<span style={{fontSize:11,color:t.muted}}>{terp.pct}%</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(s.effects||[]).length>0&&<div style={{marginBottom:8}}><p style={{...lbl,marginBottom:6}}>Effects</p><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{s.effects.map((ef,i)=><span key={i} style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.clayLight,color:t.clay,fontWeight:500}}>{ef}</span>)}</div></div>}
                          {(s.flavors||[]).length>0&&<div style={{marginBottom:8}}><p style={{...lbl,marginBottom:6}}>Flavors</p><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{s.flavors.map((fl,i)=><span key={i} style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:"#fef3c7",color:t.amber,fontWeight:500}}>{fl}</span>)}</div></div>}
                        </>
                      )}
                      {(s.cat==="Edible"||s.cat==="Tincture")&&s.strength&&(
                        <div style={{marginBottom:10}}>
                          {(s.liveResin||s.liveRosin)&&(
                            <div style={{display:"flex",gap:8,marginBottom:8}}>
                              {s.liveResin&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.duskLight,color:t.dusk,fontWeight:600}}>Live Resin</span>}
                              {s.liveRosin&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.duskLight,color:t.dusk,fontWeight:600}}>Live Rosin</span>}
                            </div>
                          )}
                          <p style={{...lbl,marginBottom:6}}>{s.cat==="Edible"?"Per piece":"Per ml"}</p>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            {CANNAB_FIELDS.filter(f=>s.strength[f]).map(f=>(
                              <div key={f} style={{background:t.purpleLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}>
                                <div style={{fontSize:15,fontWeight:700,color:t.purple}}>{s.strength[f]}mg</div>
                                <div style={{fontSize:10,color:t.muted}}>{f}</div>
                              </div>
                            ))}
                          </div>
                          {s.cat==="Edible"&&s.edibleType&&<div style={{marginTop:6}}><span style={{fontSize:12,padding:"2px 10px",borderRadius:10,background:"#fef3c7",color:t.amber,fontWeight:500}}>{s.edibleType}</span></div>}
                        </div>
                      )}
                      {s.cat==="Edible"&&s.nutrition&&(()=>{
                        const n = s.nutrition || {};
                        const hasServing = n.servingSizeText || n.servingSizeUnits || n.servingsPerPackage;
                        const hasServingVals = n.caloriesPerServing != null || n.carbsGPerServing != null || n.sugarsGPerServing != null;
                        const hasPieceVals = n.caloriesPerPiece != null || n.carbsGPerPiece != null || n.sugarsGPerPiece != null;
                        if (!hasServing && !hasServingVals && !hasPieceVals) return null;
                        return (
                          <div style={{marginBottom:10,padding:"10px 12px",borderRadius:10,background:t.greenLight,border:`1px solid ${t.green}33`}}>
                            <p style={{...lbl,marginBottom:6,color:t.green}}>Nutrition</p>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                              {n.servingSizeText&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:t.surface,color:t.text}}>Serving: {n.servingSizeText}</span>}
                              {n.servingsPerPackage!=null&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:t.surface,color:t.text}}>Servings: {n.servingsPerPackage}</span>}
                            </div>
                            {hasServingVals&&(
                              <div style={{fontSize:12,color:t.textSoft,marginBottom:4}}>
                                Per serving: {[
                                  n.caloriesPerServing != null ? `${n.caloriesPerServing} cal` : null,
                                  n.carbsGPerServing != null ? `${n.carbsGPerServing}g carbs` : null,
                                  n.sugarsGPerServing != null ? `${n.sugarsGPerServing}g sugars` : null,
                                ].filter(Boolean).join(' · ')}
                              </div>
                            )}
                            {hasPieceVals&&(
                              <div style={{fontSize:12,color:t.text,fontWeight:600}}>
                                Per gummy: {[
                                  n.caloriesPerPiece != null ? `${n.caloriesPerPiece} cal` : null,
                                  n.carbsGPerPiece != null ? `${n.carbsGPerPiece}g carbs` : null,
                                  n.sugarsGPerPiece != null ? `${n.sugarsGPerPiece}g sugars` : null,
                                ].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {s.cat==="Edible"&&(s.vegan||s.kosher||s.glutenFree||s.nonGmo)&&(
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                          {s.vegan&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:t.greenLight,color:t.green,fontWeight:600}}>Vegan</span>}
                          {s.glutenFree&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:t.greenLight,color:t.green,fontWeight:600}}>Gluten-Free</span>}
                          {s.nonGmo&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:t.greenLight,color:t.green,fontWeight:600}}>Non-GMO</span>}
                          {s.kosher&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:t.greenLight,color:t.green,fontWeight:600}}>Kosher</span>}
                        </div>
                      )}
                      {s.cat==="Edible"&&(s.ingredients||[]).length>0&&(
                        <details style={{marginBottom:10}}>
                          <summary style={{fontSize:12,color:t.muted,cursor:"pointer",userSelect:"none",fontWeight:600}}>Ingredients ({s.ingredients.length})</summary>
                          <p style={{fontSize:12,color:t.textSoft,lineHeight:1.6,margin:"8px 0 0"}}>{s.ingredients.join(', ')}</p>
                        </details>
                      )}
                      {s.cat==="Concentrate"&&(
                        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:10}}>
                          {s.conType&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.redLight,color:t.red,fontWeight:500}}>{s.conType}</span>}
                          {s.strain&&<span style={{fontSize:12,color:t.muted}}>Strain: {s.strain}</span>}
                          {tPct&&<span title={tFromPotency?"Labelled potency":"Auto-calculated from COA"} style={{fontSize:12,color:t.muted}}>{tPct}% potency{!tFromPotency?" (COA)":""}</span>}
                          {s.weight&&<span style={{fontSize:12,color:t.muted}}>{s.weight}g</span>}
                        </div>
                      )}
                      {s.cat==="Vape Cart"&&(
                        <div style={{marginBottom:10}}>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:6}}>
                            {s.vapeHardware&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.purpleLight,color:t.purple,fontWeight:500}}>{s.vapeHardware}</span>}
                            {s.vapeSize&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.clayLight,color:t.clay,fontWeight:500}}>{s.vapeSize}</span>}
                            {s.solvent&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.clayLight,color:t.clay,fontWeight:500}}>{s.solvent}</span>}
                            {s.liveResin&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.duskLight,color:t.dusk,fontWeight:600}}>Live Resin</span>}
                            {s.liveRosin&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:t.duskLight,color:t.dusk,fontWeight:600}}>Live Rosin</span>}
                            {s.strain&&<span style={{fontSize:12,color:t.muted}}>Strain: {s.strain}</span>}
                          </div>
                          {s.multiBarrel&&(s.barrels||[]).length>0&&(
                            <div style={{marginBottom:8}}>
                              <p style={{...lbl,marginBottom:6}}>Barrels</p>
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {s.barrels.map((b,i)=>(
                                  <div key={i} style={{background:t.purpleLight,borderRadius:8,padding:"4px 10px",display:"flex",alignItems:"center",gap:5}}>
                                    <span style={{fontSize:12,fontWeight:600,color:t.purple}}>{b.strain||`Barrel ${i+1}`}</span>
                                    {b.potency&&<span style={{fontSize:11,color:t.muted}}>{b.potency}%</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:6}}>
                            {tPct&&<div title={tFromPotency?"Labelled potency":"Auto-calculated from COA"} style={{background:t.purpleLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:15,fontWeight:700,color:t.purple}}>{tPct}%</div><div style={{fontSize:10,color:t.muted}}>{tFromPotency?"THC":"THC (COA)"}{s.multiBarrel?" (avg)":""}</div></div>}
                            {s.thca&&<div style={{background:t.purpleLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:15,fontWeight:700,color:t.purple}}>{s.thca}%</div><div style={{fontSize:10,color:t.muted}}>THCA</div></div>}
                            {s.cbd&&<div style={{background:t.greenLight,borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:15,fontWeight:700,color:t.green}}>{s.cbd}%</div><div style={{fontSize:10,color:t.muted}}>CBD</div></div>}
                          </div>
                          {(s.terpenes||[]).length>0&&(
                            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                              {s.terpenes.map((terp,i)=>(
                                <div key={i} style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"4px 10px",display:"flex",alignItems:"center",gap:5}}>
                                  <span style={{fontSize:12,fontWeight:600,color:t.green}}>{terp.name}</span>
                                  {terp.pct&&<span style={{fontSize:11,color:t.muted}}>{terp.pct}%</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {s.cat==="Recipe"&&(
                        <div style={{marginBottom:8}}>
                          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
                            {s.recipeType&&<span style={{fontSize:12,padding:"3px 10px",borderRadius:10,background:"#fef3c7",color:t.amber,fontWeight:600}}>{s.recipeType}</span>}
                            {s.dosePerServing&&<div style={{background:t.amberLight,borderRadius:8,padding:"6px 12px"}}><div style={{fontSize:14,fontWeight:700,color:t.amber}}>{s.dosePerServing}</div><div style={{fontSize:10,color:t.muted}}>per serving</div></div>}
                            {s.servings&&<div style={{background:t.amberLight,borderRadius:8,padding:"6px 12px"}}><div style={{fontSize:14,fontWeight:700,color:t.amber}}>{s.servings}</div><div style={{fontSize:10,color:t.muted}}>servings</div></div>}
                          </div>
                          {s.recipeStashId&&(()=>{const ing=stash.find(x=>x.id===s.recipeStashId);return ing?<p style={{fontSize:12,color:t.purple,marginBottom:6}}>🧪 Infused with: <strong>{ing.name}</strong></p>:null;})()}
                          {s.recipeUrl&&<a href={s.recipeUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:t.sage,textDecoration:"underline",display:"block",marginBottom:6}}>{(()=>{try{return new URL(s.recipeUrl).hostname.replace(/^www\./,'');}catch{return'Recipe Link';}})()+" ↗"}</a>}
                          {s.recipeText&&<details style={{marginTop:4}}><summary style={{fontSize:12,color:t.muted,cursor:"pointer",userSelect:"none"}}>Recipe</summary><pre style={{fontSize:12,color:t.textSoft,whiteSpace:"pre-wrap",margin:"8px 0 0",fontFamily:"inherit",lineHeight:1.6}}>{s.recipeText}</pre></details>}
                        </div>
                      )}
                      {s.comment&&<p style={{fontSize:12,color:t.textSoft,fontStyle:"italic",lineHeight:1.5,margin:0}}>{s.comment}</p>}
                      {(s.coaUrl||s.retailUrl)&&(
                        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:10,paddingTop:10,borderTop:`1px solid ${t.border}`}}>
                          {s.coaUrl&&<span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                            <a href={s.coaUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:t.sage,textDecoration:"underline"}}>Lab Report (COA)</a>
                            <button onClick={e=>{e.stopPropagation();setStash(prev=>prev.map(x=>x.id===s.id?{...x,coaUrl:undefined}:x));if(shouldSync)syncStash({...s,coaUrl:undefined});}} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:12,padding:0,lineHeight:1}}>×</button>
                          </span>}
                          {s.retailUrl&&<span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                            <a href={s.retailUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:t.purple,textDecoration:"underline"}}>{(()=>{try{return new URL(s.retailUrl).hostname.replace(/^www\./,'');}catch{return'Product Page';}})()}</a>
                            <button onClick={e=>{e.stopPropagation();setStash(prev=>prev.map(x=>x.id===s.id?{...x,retailUrl:undefined}:x));if(shouldSync)syncStash({...s,retailUrl:undefined});}} style={{background:"none",border:"none",color:t.muted,cursor:"pointer",fontSize:12,padding:0,lineHeight:1}}>×</button>
                          </span>}
                        </div>
                      )}
{stashUsage[s.id]&&<p style={{fontSize:11,color:t.muted,marginTop:6}}>{stashUsage[s.id]} sessions logged</p>}
                    </div>
                  )}
                  {editSId===s.id && stashFormCard}
                </div>
              );
            })}
          </div>
        )}

        {tab==="stats" && (
          <div>
            {/* Timeframe selector */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <SectionLabel>Usage Profile</SectionLabel>
              <div style={{display:"flex",gap:6}}>
                {[1,7,30,90].map(d=>(
                  <button key={d} onClick={()=>setStatDays(d)} style={{padding:"5px 10px",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontSize:12,border:`1px solid ${statDays===d?t.purple:t.border}`,background:statDays===d?t.purpleLight:t.surface,color:statDays===d?t.purple:t.muted,fontWeight:statDays===d?700:400}}>{d}d</button>
                ))}
              </div>
            </div>

            {/* ── Blood level curve (today / 1d only) ── */}
            {showBloodCard&&(
              <Card style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <p style={{...lbl,marginBottom:0}}>Estimated Blood THC — Today</p>
                  <span style={{fontSize:11,color:t.muted}}>~{currentTHC.toFixed(1)}mg now</span>
                </div>
                {todayIntake.length===0&&<p style={{fontSize:13,fontWeight:600,color:t.sage,margin:"0 0 4px"}}>No usage today — stored THC only</p>}
                <p style={{fontSize:11,color:t.muted,margin:"0 0 12px",fontStyle:"italic"}}>Best estimate only. Individual metabolism varies. THC is stored in body fat and can take 30+ days to fully clear.</p>

                {/* SVG line graph - 12 hour window with axes */}
                {(()=>{
                  const W=340, H=120;
                  const padL=36, padR=8, padT=8, padB=22; // space for axes
                  const plotW = W-padL-padR;
                  const plotH = H-padT-padB;
                  const WINDOW = 12*60;
                  const windowStart = Math.max(0, nowMinute - WINDOW);

                  // Build points
                  const pts = [];
                  for(let m=windowStart;m<=Math.min(24*60-1,windowStart+WINDOW);m+=5){
                    const x = padL + ((m-windowStart)/WINDOW)*plotW;
                    const y = padT + plotH - (bloodCurve[m]/Math.max(peakTHC*1.3,1))*plotH;
                    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
                  }
                  const path = `M ${pts.join(' L ')}`;
                  const fill = `M ${padL},${padT+plotH} L ${pts.join(' L ')} L ${(padL+plotW).toFixed(1)},${padT+plotH} Z`;
                  const nowX = padL + ((nowMinute-windowStart)/WINDOW)*plotW;

                  // X axis — hour labels every 2 hours, skip if too close to edges
                  const hourLabels = [];
                  for(let h=0;h<24;h+=2){
                    const m=h*60;
                    if(m>=windowStart && m<=windowStart+WINDOW){
                      const x = padL+((m-windowStart)/WINDOW)*plotW;
                      if(x > padL+10 && x < padL+plotW-10) {
                        const label = h===0?"12am":h<12?`${h}am`:h===12?"12pm":`${h-12}pm`;
                        hourLabels.push({x, label});
                      }
                    }
                  }

                  // Y axis — 3 labels: 0, half, peak
                  const yLabels = [
                    {y: padT+plotH, label:"0"},
                    {y: padT+plotH/2, label:`${(peakTHC/2).toFixed(peakTHC<10?1:0)}`},
                    {y: padT, label:`${peakTHC.toFixed(peakTHC<10?1:0)}mg`},
                  ];

                  // Intake dots
                  const ciDots = todayIntake.map(e=>{
                    const min=(new Date(e.ts).getHours()*60)+new Date(e.ts).getMinutes();
                    if(min<windowStart||min>windowStart+WINDOW) return null;
                    const x=padL+((min-windowStart)/WINDOW)*plotW;
                    const y=padT+plotH-(bloodCurve[min]/Math.max(peakTHC*1.3,1))*plotH;
                    return {x,y,method:e.method};
                  }).filter(Boolean);

                  return (
                    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H+20,display:"block"}}>
                      <defs>
                        <linearGradient id="bloodGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={t.purple} stopOpacity="0.25"/>
                          <stop offset="100%" stopColor={t.purple} stopOpacity="0.02"/>
                        </linearGradient>
                      </defs>

                      {/* Plot area border */}
                      <rect x={padL} y={padT} width={plotW} height={plotH} fill="#fafafa" stroke={t.border} strokeWidth="1" rx="2"/>

                      {/* Y axis grid lines and labels */}
                      {yLabels.map((l,i)=>(
                        <g key={i}>
                          <line x1={padL} y1={l.y} x2={padL+plotW} y2={l.y} stroke={t.border} strokeWidth="0.5" strokeDasharray={i>0?"3,3":""}/>
                          <text x={padL-4} y={l.y+3} fontSize="9" fill={t.muted} textAnchor="end">{l.label}</text>
                        </g>
                      ))}

                      {/* X axis grid lines and labels */}
                      {hourLabels.map((h,i)=>(
                        <g key={i}>
                          <line x1={h.x} y1={padT} x2={h.x} y2={padT+plotH} stroke={t.border} strokeWidth="0.5" strokeDasharray="3,3"/>
                          <text x={h.x} y={padT+plotH+14} fontSize="9" fill={t.muted} textAnchor="middle">{h.label}</text>
                        </g>
                      ))}

                      {/* Fill + line */}
                      <path d={fill} fill="url(#bloodGrad)"/>
                      <path d={path} fill="none" stroke={t.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>

                      {/* Now line */}
                      {nowX>=padL&&nowX<=padL+plotW&&(
                        <line x1={nowX} y1={padT} x2={nowX} y2={padT+plotH} stroke={t.amber} strokeWidth="1.5" strokeDasharray="4,2"/>
                      )}

                      {/* Intake dots */}
                      {ciDots.map((d,i)=>(
                        <circle key={i} cx={d.x} cy={d.y} r="4" fill={t.green} stroke="#fff" strokeWidth="1.5"/>
                      ))}
                    </svg>
                  );
                })()}

                <div style={{display:"flex",gap:12,marginTop:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:t.muted}}><span style={{color:t.green}}>● </span>intake event</span>
                  <span style={{fontSize:11,color:t.muted}}><span style={{color:t.amber}}>| </span>now</span>
                  {peakTHC>0&&<span style={{fontSize:11,color:t.muted}}>peak ~{peakTHC.toFixed(1)}mg</span>}
                  {currentResidual>=0.05&&<span style={{fontSize:11,color:t.muted}}>stored baseline ~{currentResidual.toFixed(1)}mg</span>}
                  <span style={{fontSize:11,color:t.muted}}>clearance half-life ~{blood.halfLife.toFixed(1)} days ({blood.useDays} use day{blood.useDays===1?"":"s"} in last 30)</span>
                </div>
              </Card>
            )}

            {/* Empty state */}
            {fIntake.length===0&&(
              <Card style={{textAlign:"center",padding:"32px 20px"}}>
                <p style={{fontSize:28,margin:"0 0 8px"}}>📊</p>
                <p style={{fontWeight:600,margin:"0 0 4px"}}>{intake.length>0&&statDays===1?"No usage today":"No data yet"}</p>
                <p style={{color:t.muted,fontSize:13,margin:0}}>{intake.length>0&&statDays===1?"Pick 7d or 30d to see your recent usage":"Log some intake to see your usage profile"}</p>
              </Card>
            )}

            {fIntake.length>0&&(()=>{

              // Build daily session counts + THC totals for combo chart
              function buildDailyBars() {
                const bars = {};
                fIntake.forEach(e=>{
                  const d = new Date(e.ts).toLocaleDateString('en-US',{month:'short',day:'numeric'});
                  if(!bars[d]) bars[d] = {date:d, count:0, thc:0};
                  bars[d].count += 1;
                  bars[d].thc += Number(e.thcLo)||0;
                });
                return Object.values(bars).slice(-statDays);
              }
              const dailyBars = buildDailyBars();
              const maxBar = Math.max(...dailyBars.map(b=>b.count),1);
              const maxBarThc = Math.max(...dailyBars.map(b=>b.thc),1);

              // ── Usage summary stats ────────────────────────────────
              const methodsUsed = [...new Set(fIntake.map(e=>e.method))];
              const flowerWeight = fIntake.filter(e=>["Bowl","Bong","Joint","One Hitter","Dry Herb Vape","Dab"].includes(e.method)).reduce((s,e)=>s+(e.weight||0)*(e.qty||1),0);
              const edibleMg = fIntake.filter(e=>e.method==="Edible").reduce((s,e)=>s+(e.edibleSize||0)*(e.edibleQty||1),0);
              // Calories/sugar — joined from each edible session back to its stash
              // item's nutrition (not captured at log time: it's a fixed per-piece
              // property of the item, not something that varies per session like THC).
              let totalCalories = 0, totalSugarG = 0;
              fIntake.filter(e=>e.method==="Edible").forEach(e=>{
                const pn = perPieceNutrition(stash.find(x=>x.id===e.stashId)?.nutrition);
                if(!pn) return;
                const qty = e.edibleQty||1;
                if(pn.cal!=null)   totalCalories += pn.cal*qty;
                if(pn.sugar!=null) totalSugarG   += pn.sugar*qty;
              });
              const vapeSessions = fIntake.filter(e=>e.method==="Oil Vape").length;
              const tinctureML = fIntake.filter(e=>e.method==="Tincture").reduce((s,e)=>s+(e.tinctureML||0),0);
              const totalAbsorbed = fIntake.reduce((s,e)=>s+(Number(e.thcLo)||0),0);
              const avgThcPerDay = statDays>1 ? (totalAbsorbed/statDays).toFixed(1) : null;
              const avgPerDay = statDays>1 ? (fIntake.length/statDays).toFixed(1) : fIntake.length;
              const todaySessions = todayIntake.length;

              // Top feeling and activity
              const allFeelings = fCheckins.flatMap(c=>c.feelings||[]);
              const topFeeling = allFeelings.length>0 ? Object.entries(allFeelings.reduce((a,f)=>{a[f]=(a[f]||0)+1;return a;},{})).sort((a,b)=>b[1]-a[1])[0] : null;
              const allActivities = fIntake.flatMap(e=>e.activities||[]);
              const topActivity = allActivities.length>0 ? Object.entries(allActivities.reduce((a,f)=>{a[f]=(a[f]||0)+1;return a;},{})).sort((a,b)=>b[1]-a[1])[0] : null;

              return <>

                {/* ── Daily sessions + THC combo chart (multi-day) ── */}
                {/* Two genuinely distinct colors: t.purple is an alias for t.clay (same
                    hex), so it must NOT be used alongside t.clay in the same chart —
                    use t.dusk (blue) for the THC series instead. */}
                {statDays>1&&dailyBars.length>0&&(()=>{
                  const barW = statDays<=14?34:statDays<=30?18:9;
                  const padL=34, padR=44, padT=12, padB=statDays<=14?36:14;
                  const plotW = dailyBars.length*barW;
                  const plotH = 84;
                  const W = padL+plotW+padR, H = padT+plotH+padB;

                  const linePts = dailyBars.map((b,i)=>({
                    x: padL + i*barW + barW/2,
                    y: padT + plotH - (b.thc/maxBarThc)*plotH,
                    b,
                  }));
                  const linePath = `M ${linePts.map(p=>`${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ')}`;

                  return (
                    <Card style={{marginBottom:16}}>
                      <p style={{...lbl,marginBottom:12,fontSize:13}}>Usage &amp; THC per Day — {statDays}d</p>
                      <div style={{overflowX:"auto"}}>
                        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{display:"block"}}>
                          {/* Left axis — sessions */}
                          <line x1={padL} y1={padT} x2={padL} y2={padT+plotH} stroke={t.clay} strokeWidth="1" opacity="0.4"/>
                          <text x={padL-6} y={padT+5} fontSize="11" fontWeight="600" fill={t.clay} textAnchor="end">{maxBar}</text>
                          <text x={padL-6} y={padT+plotH} fontSize="11" fontWeight="600" fill={t.clay} textAnchor="end">0</text>

                          {/* Right axis — THC mg */}
                          <text x={padL+plotW+8} y={padT+5} fontSize="11" fontWeight="600" fill={t.dusk} textAnchor="start">{maxBarThc.toFixed(0)}mg</text>
                          <text x={padL+plotW+8} y={padT+plotH} fontSize="11" fontWeight="600" fill={t.dusk} textAnchor="start">0</text>

                          {/* Bars — session count */}
                          {dailyBars.map((b,i)=>{
                            const barH = (b.count/maxBar)*plotH;
                            const x = padL+i*barW+barW*0.15;
                            return (
                              <g key={i}>
                                <title>{`${b.date}: ${b.count} session${b.count===1?'':'s'}, ${b.thc.toFixed(0)}mg THC`}</title>
                                <rect x={x} y={padT+plotH-barH} width={barW*0.7} height={Math.max(barH,b.count>0?2:0)} fill={t.clay} opacity="0.8" rx="2"/>
                              </g>
                            );
                          })}

                          {/* Line — THC mg */}
                          {linePts.length>1&&<path d={linePath} fill="none" stroke={t.dusk} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
                          {linePts.map((p,i)=>(
                            <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={t.dusk} stroke="#fff" strokeWidth="1.5"/>
                          ))}

                          {/* Date labels */}
                          {statDays<=14&&dailyBars.map((b,i)=>{
                            const x = padL+i*barW+barW/2;
                            return (
                              <text key={i} x={x} y={padT+plotH+16} fontSize="10" fontWeight="500" fill={t.textSoft} textAnchor="middle" transform={`rotate(-45 ${x} ${padT+plotH+16})`}>{b.date}</text>
                            );
                          })}
                        </svg>
                      </div>
                      <div style={{display:"flex",gap:16,marginTop:10,flexWrap:"wrap"}}>
                        <span style={{fontSize:13,fontWeight:600,color:t.clay}}>■ sessions</span>
                        <span style={{fontSize:13,fontWeight:600,color:t.dusk}}>● THC absorbed (mg)</span>
                      </div>
                    </Card>
                  );
                })()}

                {/* ── Highlighted stat cards (dynamic, non-zero only) ── */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                  {fIntake.length>0&&<StatCard icon="🌿" value={fIntake.length} label={statDays===1?"today":"this period"} sub="sessions"/>}
                  {fIntake.length>0&&statDays>1&&<StatCard icon="📅" value={avgPerDay} label={`avg per day`} sub={`over ${statDays}d`}/>}
                  {totalAbsorbed>0&&<StatCard icon="⚗️" value={`~${totalAbsorbed.toFixed(0)}mg`} label="est. absorbed" sub="THC total"/>}
                  {avgThcPerDay>0&&<StatCard icon="📊" value={`~${avgThcPerDay}mg`} label="avg per day" sub="THC absorbed"/>}
                  {flowerWeight>0&&<StatCard icon="⚖️" value={`${flowerWeight.toFixed(2)}g`} label="flower used" sub="this period"/>}
                  {edibleMg>0&&<StatCard icon="🍬" value={`${edibleMg.toFixed(0)}mg`} label="edibles" sub="total THC"/>}
                  {totalCalories>0&&<StatCard icon="🔥" value={Math.round(totalCalories)} label={statDays===1?"today":"this period"} sub="calories"/>}
                  {totalSugarG>0&&<StatCard icon="🍭" value={`${totalSugarG.toFixed(0)}g`} label={statDays===1?"today":"this period"} sub="sugar"/>}
                  {vapeSessions>0&&<StatCard icon="💨" value={vapeSessions} label="vape" sub="sessions"/>}
                  {tinctureML>0&&<StatCard icon="💧" value={`${tinctureML.toFixed(1)}ml`} label="tincture" sub="total"/>}
                  {usedCount>0&&<StatCard icon="📆" value={`${usedCount}/${Math.min(statDays,fSessions.length||statDays)}`} label="days medicated" sub="this period"/>}
                </div>

                {/* ── Timeline — scales to selected period ── */}
                {fIntake.length>0&&(
                  <div style={{marginBottom:16}}>
                    <SectionLabel>{statDays===1?"Today's Timeline":`${statDays}-Day Timeline`}</SectionLabel>
                    <Card style={{padding:0,overflow:"hidden"}}>
                      <div style={{maxHeight:320,overflowY:"auto",padding:"12px 16px"}}>
                      {[...fIntake.map(e=>({...e,_type:"intake"})),...fCheckins.map(c=>({...c,_type:"checkin"}))].sort((a,b)=>new Date(b.ts)-new Date(a.ts)).map((e,i,arr)=>{
                          const isIntake = e._type==="intake";
                          const time = new Date(e.ts).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',timeZone:prefs.timezone||undefined});
                          const dateLabel = statDays>1 ? new Date(e.ts).toLocaleDateString('en-US',{month:'short',day:'numeric',timeZone:prefs.timezone||undefined}) : null;
                          return (
                            <SwipeRow
                              key={e.id||i}
                              onDelete={()=>{ if(isIntake) setIntake(prev=>prev.filter(x=>x.id!==e.id)); else setCheckins(prev=>prev.filter(x=>x.id!==e.id)); }}
                              onEdit={()=>{
                                setEditTime(toLocalInput(e.ts));
                                setEditNote(e.note||"");
                                setEditFeelings(e.feelings||[]);
                                setEditActivities(e.activities||[]);
                                setTimelineEditEntry(e);
                              }}
                            >
                              <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 0",borderBottom:i<arr.length-1?`1px solid ${t.border}`:"none"}}>
                                <div style={{fontSize:18,flexShrink:0,marginTop:2}}>{isIntake?METHOD_ICONS[e.method]||"🌿":"✨"}</div>
                                <div style={{flex:1}}>
                                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                    <span style={{fontSize:13,fontWeight:600}}>{isIntake?e.method:"Check-in"}</span>
                                    <span style={{fontSize:11,color:t.muted}}>{dateLabel&&<span style={{marginRight:4}}>{dateLabel}</span>}{time}</span>
                                  </div>
                                  {isIntake&&intakeSummary(e)&&<div style={{fontSize:12,color:t.muted}}>{intakeSummary(e)}</div>}
                                  {isIntake&&e.thcLo>0&&<div style={{fontSize:12,color:t.clay}}>{e.thcLo===e.thcHi?`${e.thcLo}mg consumed`:`~${e.thcLo}–${e.thcHi}mg absorbed`}</div>}
                                  {isIntake&&e.stashName&&<div style={{fontSize:11,color:t.muted}}>{e.stashName}</div>}
                                  {(e.feelings||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>{e.feelings.map(f=><span key={f} style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.clayLight,color:t.clay}}>{FEELING_MAP[f]} {f}</span>)}</div>}
                                  {(e.activities||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>{(e.activities||[]).map(a=><span key={a} style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:t.duskLight,color:t.dusk}}>{ACTIVITY_MAP[a]} {a}</span>)}</div>}
                                </div>
                              </div>
                            </SwipeRow>
                          );
                        })}
                      </div>
                    </Card>
                  </div>
                )}

                {/* ── Methods used (only methods with data) ── */}
                {methodsUsed.length>0&&(
                  <div style={{marginBottom:16}}>
                    <SectionLabel>How You Medicate</SectionLabel>
                    <Card>
                      {methodsUsed.map(m=>{
                        const count = fIntake.filter(e=>e.method===m).length;
                        const pct = Math.round((count/fIntake.length)*100);
                        return (
                          <div key={m} style={{marginBottom:10}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                              <span style={{fontSize:13}}>{METHOD_ICONS[m]} {m}</span>
                              <span style={{fontSize:13,color:t.purple,fontWeight:600}}>{count}x · {pct}%</span>
                            </div>
                            <div style={{height:5,borderRadius:3,background:t.border,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct}%`,background:t.purple,borderRadius:3}}/>
                            </div>
                          </div>
                        );
                      })}
                    </Card>
                  </div>
                )}

                {/* ── Top feeling + activity ── */}
                {(topFeeling||topActivity)&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                    {topFeeling&&<Card style={{textAlign:"center"}}>
                      <div style={{fontSize:24}}>{FEELING_MAP[topFeeling[0]]}</div>
                      <div style={{fontWeight:700,fontSize:13,marginTop:4}}>{topFeeling[0]}</div>
                      <div style={{fontSize:11,color:t.muted}}>top feeling · {topFeeling[1]}x</div>
                    </Card>}
                    {topActivity&&<Card style={{textAlign:"center"}}>
                      <div style={{fontSize:24}}>{ACTIVITY_MAP[topActivity[0]]}</div>
                      <div style={{fontWeight:700,fontSize:13,marginTop:4}}>{topActivity[0]}</div>
                      <div style={{fontSize:11,color:t.muted}}>top activity · {topActivity[1]}x</div>
                    </Card>}
                  </div>
                )}

                {/* ── Stash usage (only items actually used) ── */}
                {Object.keys(stashUsage).length>0&&(
                  <div style={{marginBottom:16}}>
                    <SectionLabel>Stash Usage</SectionLabel>
                    {sortStash(stash).filter(s=>stashUsage[s.id]>0).map(s=>(
                      <div key={s.id} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:10,padding:"10px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <span style={{fontSize:11,padding:"2px 7px",borderRadius:8,background:catBg(s.cat),color:catColor(s.cat),fontWeight:600,marginRight:8}}>{STASH_ICONS[s.cat]}</span>
                          <span style={{fontWeight:600,fontSize:13}}>{s.name}</span>
                          {s.rating>0&&<span style={{fontSize:12,color:t.amber,marginLeft:8}}>{"★".repeat(s.rating)}</span>}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:16,fontWeight:700,color:t.purple}}>{stashUsage[s.id]}x</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Dispensary breakdown ── */}
                {(()=>{
                  const byDisp = {};
                  stash.forEach(s => {
                    const d = s.dispensary?.trim();
                    if (!d) return;
                    if (!byDisp[d]) byDisp[d] = [];
                    byDisp[d].push(s);
                  });
                  const entries = Object.entries(byDisp).sort((a,b) => b[1].length - a[1].length);
                  if (entries.length === 0) return null;
                  return (
                    <div style={{marginBottom:16}}>
                      <SectionLabel>By Dispensary</SectionLabel>
                      {entries.map(([disp, items]) => {
                        const open = expandedDispensary === disp;
                        return (
                          <div key={disp} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:10,marginBottom:8,overflow:"hidden"}}>
                            <button onClick={()=>setExpandedDispensary(open ? null : disp)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                              <span style={{fontWeight:600,fontSize:14}}>{disp}</span>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                <span style={{fontSize:12,color:t.purple,fontWeight:700}}>{items.length} product{items.length!==1?"s":""}</span>
                                <span style={{fontSize:12,color:t.muted}}>{open?"▲":"▼"}</span>
                              </div>
                            </button>
                            {open&&(
                              <div style={{borderTop:`1px solid ${t.border}`,padding:"8px 0"}}>
                                {items.slice(0,20).map(s=>(
                                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 14px"}}>
                                    <span style={{fontSize:11,padding:"2px 7px",borderRadius:8,background:catBg(s.cat),color:catColor(s.cat),fontWeight:600,flexShrink:0}}>{STASH_ICONS[s.cat]||"•"} {s.cat}</span>
                                    <span style={{fontSize:13,fontWeight:500,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                                    {s.id&&<span style={{fontSize:11,color:t.muted,flexShrink:0}}>{new Date(s.id).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>}
                                  </div>
                                ))}
                                {items.length>20&&<p style={{fontSize:11,color:t.muted,margin:"4px 14px 8px",fontStyle:"italic"}}>+{items.length-20} more</p>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* ── Terpene profile ── */}
                {(()=>{
                  const terpCounts={};
                  fIntake.forEach(e=>{
                    if(!e.stashId) return;
                    const s=stash.find(x=>x.id===e.stashId);
                    (s?.terpenes||[]).forEach(terp=>{
                      if(!terp.name) return;
                      if(parseFloat(terp.pct||0) < 0.01) return;
                      const name = normTerpName(terp.name);
                      if(!terpCounts[name]) terpCounts[name]={count:0,pcts:[],stashItems:new Map()};
                      terpCounts[name].count++;
                      if(terp.pct) terpCounts[name].pcts.push(parseFloat(terp.pct));
                      if(s) terpCounts[name].stashItems.set(s.id, s);
                    });
                  });
                  const entries=Object.entries(terpCounts).sort((a,b)=>b[1].count-a[1].count);
                  if(entries.length===0) return null;
                  const maxCount=entries[0][1].count;
                  return (
                    <div style={{marginBottom:16}}>
                      <SectionLabel>Terpene Profile</SectionLabel>
                      <Card>
                        {entries.map(([name,{count,pcts,stashItems}])=>{
                          const pct=Math.round((count/maxCount)*100);
                          const avgPct=pcts.length>0?(pcts.reduce((a,b)=>a+b,0)/pcts.length).toFixed(2):null;
                          const isOpen = terpExpanded===name;
                          const items=[...stashItems.values()];
                          return <div key={name} style={{marginBottom:10}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                              <span style={{fontSize:13,fontWeight:500}}>{name}</span>
                              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                                {avgPct&&<span style={{fontSize:11,color:t.muted}}>{avgPct}%</span>}
                                <button
                                  onClick={()=>setTerpExpanded(isOpen?null:name)}
                                  style={{fontSize:12,color:isOpen?t.sage:t.green,fontWeight:700,background:"none",border:"none",cursor:"pointer",padding:"2px 6px",borderRadius:6,fontFamily:"inherit",textDecoration:"underline dotted"}}
                                >{count}x</button>
                              </div>
                            </div>
                            <div style={{height:5,borderRadius:3,background:t.border,overflow:"hidden",marginBottom:isOpen?8:0}}>
                              <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#16a34a,#4ade80)",borderRadius:3}}/>
                            </div>
                            {isOpen&&(
                              <div style={{background:t.sageLight,border:`1px solid ${t.sageBorder}`,borderRadius:8,padding:"8px 10px",display:"flex",flexDirection:"column",gap:5}}>
                                {items.map(s=>(
                                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:8}}>
                                    <span style={{fontSize:10,color:catColor(s.cat),fontWeight:700}}>{STASH_ICONS[s.cat]}</span>
                                    <span style={{fontSize:12,fontWeight:600,color:t.text}}>{s.name}</span>
                                    {s.rating>0&&<span style={{fontSize:11,color:t.amber}}>{"★".repeat(s.rating)}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>;
                        })}
                      </Card>
                    </div>
                  );
                })()}

              </>;
            })()}

          </div>
        )}
        {tab==="prefs" && (
          <div>
            {/* Privacy banner */}
            <div style={{background:"#0f1f2e",borderRadius:14,padding:20,marginBottom:24,border:`1px solid ${t.purple}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <span style={{fontSize:24}}>🔒</span>
                <span style={{fontWeight:700,fontSize:16,color:"#fff"}}>Your Privacy is Protected</span>
              </div>
              <p style={{fontSize:13,color:"#93c5fd",lineHeight:1.6,margin:0}}>
                CannaTrack will <strong style={{color:"#fff"}}>never</strong> store identifiable information about you.
              </p>
            </div>

            {/* Age range */}
            <Card style={{marginBottom:16}}>
              <p style={{margin:"0 0 4px",fontWeight:700,fontSize:15}}>About You</p>
              <p style={{margin:"0 0 16px",color:t.muted,fontSize:12}}>Optional — helps improve aggregate insights. Never shared individually.</p>

              <div style={{marginBottom:16}}>
                <label style={lbl}>Age Range</label>
                <select value={prefs.ageRange} onChange={e=>setPrefs(p=>({...p,ageRange:e.target.value}))} style={{...inp,cursor:"pointer",appearance:"auto"}}>
                  {AGE_BANDS.map(a=><option key={a}>{a}</option>)}
                </select>
              </div>

              <div style={{marginBottom:4}}>
                <label style={lbl}>Sex</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {SEX_OPTIONS.map(s=>(
                    <button key={s} onClick={()=>setPrefs(p=>({...p,sex:s}))} style={{
                      padding:"8px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,
                      border:`1px solid ${prefs.sex===s?t.purple:t.border}`,
                      background:prefs.sex===s?t.purpleLight:t.surface,
                      color:prefs.sex===s?t.purple:t.muted,
                      fontWeight:prefs.sex===s?600:400,
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Timezone */}
            <Card style={{marginBottom:16}}>
              <p style={{margin:"0 0 4px",fontWeight:700,fontSize:15}}>Timezone</p>
              <p style={{margin:"0 0 12px",color:t.muted,fontSize:12}}>Used for accurate time display in logs and graphs.</p>
              <select value={prefs.timezone||Intl.DateTimeFormat().resolvedOptions().timeZone} onChange={e=>setPrefs(p=>({...p,timezone:e.target.value}))} style={{...inp,cursor:"pointer",appearance:"auto"}}>
                {[
                  "America/New_York","America/Chicago","America/Denver","America/Phoenix",
                  "America/Los_Angeles","America/Anchorage","America/Adak","Pacific/Honolulu",
                  "America/Toronto","America/Vancouver","America/Edmonton","America/Winnipeg",
                  "Europe/London","Europe/Paris","Europe/Berlin","Europe/Amsterdam",
                  "Europe/Madrid","Europe/Rome","Europe/Stockholm","Europe/Oslo",
                  "Asia/Tokyo","Asia/Shanghai","Asia/Singapore","Asia/Dubai",
                  "Asia/Kolkata","Asia/Bangkok","Asia/Seoul","Asia/Hong_Kong",
                  "Australia/Sydney","Australia/Melbourne","Australia/Perth","Pacific/Auckland",
                  "UTC"
                ].map(tz=><option key={tz} value={tz}>{tz.replace(/_/g," ")}</option>)}
              </select>
            </Card>

            {/* Data storage */}
            <Card style={{marginBottom:16}}>
              <p style={{margin:"0 0 4px",fontWeight:700,fontSize:15}}>Data Storage</p>
              <p style={{margin:"0 0 16px",color:t.muted,fontSize:12}}>
                {prefs.dataStorage==="Local Only"
                  ? "Data stays on this device only. Nothing is sent to the cloud."
                  : "Sessions and stash are synced to the cloud. Sign in on any device to access your data."}
              </p>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:shouldSync?14:0}}>
                {DATA_STORAGE_OPTIONS.map(d=>(
                  <button key={d} onClick={()=>setPrefs(p=>({...p,dataStorage:d}))} style={{
                    padding:"8px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,
                    border:`1px solid ${prefs.dataStorage===d?t.purple:t.border}`,
                    background:prefs.dataStorage===d?t.purpleLight:t.surface,
                    color:prefs.dataStorage===d?t.purple:t.muted,
                    fontWeight:prefs.dataStorage===d?600:400,
                  }}>{d}</button>
                ))}
              </div>
              {shouldSync&&(
                <div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button onClick={syncNow} disabled={syncStatus==="syncing"} style={{padding:"9px 18px",background:t.dusk,border:"none",borderRadius:8,color:"#fff",cursor:syncStatus==="syncing"?"not-allowed":"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,opacity:syncStatus==="syncing"?0.6:1}}>
                    {syncStatus==="syncing"?"Syncing…":"↓ Sync Now"}
                  </button>
                  <button onClick={uploadLocalCacheNow} disabled={syncStatus==="syncing"} style={{padding:"9px 18px",background:t.purple,border:"none",borderRadius:8,color:"#fff",cursor:syncStatus==="syncing"?"not-allowed":"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,opacity:syncStatus==="syncing"?0.6:1}}>
                    ↑ Upload Local Cache
                  </button>
                  </div>
                  {syncMsg&&<p style={{margin:"8px 0 0",fontSize:12,color:syncStatus==="ok"?t.sage:t.red}}>{syncMsg}</p>}
                </div>
              )}
              {SHOW_BACKUP_TOOLS && (
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
                  <button onClick={exportLocalBackupNow} style={{padding:"9px 18px",background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,color:t.textSoft,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                    Export Backup
                  </button>
                  <button onClick={()=>importBackupInputRef.current?.click()} style={{padding:"9px 18px",background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,color:t.textSoft,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                    Import Backup
                  </button>
                  <input
                    ref={importBackupInputRef}
                    type="file"
                    accept="application/json"
                    style={{display:"none"}}
                    onChange={(e)=>importLocalBackupNow(e.target.files?.[0])}
                  />
                </div>
              )}
            </Card>

            {/* Account */}
            <Card style={{marginBottom:16}}>
              <p style={{margin:"0 0 4px",fontWeight:700,fontSize:15}}>Account</p>
              {user?.userDetails && (
                <p style={{margin:"0 0 14px",color:t.muted,fontSize:12}}>
                  Signed in as <strong style={{color:t.text}}>{user.userDetails}</strong>
                  {user.identityProvider && <span style={{color:t.muted}}> via {user.identityProvider}</span>}
                </p>
              )}
              <label style={{...lbl,marginBottom:6,display:"block"}}>Display name</label>
              <input
                placeholder="How should we call you?"
                value={prefs.displayName||""}
                onChange={e=>setPrefs(p=>({...p,displayName:e.target.value}))}
                style={{...inp,marginBottom:14}}
              />
              <button onClick={performLogout} style={{width:"100%",padding:"11px 0",background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,color:t.textSoft,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600}}>
                Sign Out
              </button>
            </Card>

            {/* Quick wipe */}
            <Card style={{border:`1px solid ${t.red}22`}}>
              <p style={{margin:"0 0 4px",fontWeight:700,fontSize:15,color:t.red}}>Danger Zone</p>
              <p style={{margin:"0 0 14px",color:t.muted,fontSize:12}}>Instantly and permanently clear all local data from this device. Cannot be undone.</p>
              <button onClick={()=>{
                if(window.confirm("Clear ALL CannaTrack data? This cannot be undone.")) {
                  Object.values(SK).forEach(k=>localStorage.removeItem(k));
                  window.location.reload();
                }
              }} style={{width:"100%",padding:"11px 0",background:t.redLight,border:`1px solid ${t.red}`,borderRadius:8,color:t.red,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:700}}>
                Clear All Data
              </button>
            </Card>

            <p style={{textAlign:"center",fontSize:11,color:t.muted,marginTop:20,fontStyle:"italic"}}>
              CannaTrack · Privacy First · Always
            </p>
            <div style={{display:"flex",justifyContent:"center",gap:20,marginTop:10}}>
              <button onClick={()=>setShowFeedback(true)} style={{background:"none",border:"none",fontSize:12,color:t.sage,cursor:"pointer",fontFamily:"inherit",fontWeight:600,textDecoration:"underline"}}>💬 Feedback</button>
              <a href="/privacy.html" style={{fontSize:12,color:t.muted,textDecoration:"underline"}}>Privacy Policy</a>
              <a href="/terms.html" style={{fontSize:12,color:t.muted,textDecoration:"underline"}}>Terms of Service</a>
            </div>
            <p style={{textAlign:"center",fontSize:10,color:t.muted,marginTop:10}}>
              © 2026 CannaTrack. All rights reserved. · Version {appVersion}
            </p>
          </div>
        )}

      </main>
    </div>
  );
}